import React, { useRef, useState, useEffect, useCallback, useMemo } from "react";
import dmcPalette from "./data/dmc.json";
import anchorPalette from "./data/anchor.json";
import {
  clamp,
  resizeCanvasToDisplaySize,
  deepCopyLayers,
  createEmptyLayer,
  HistoryStack,
  clientToIndex,
  suggestedCellSize,
} from "./utils";
import {
  extractDominantColors,
  convertImageToGrid,
} from "./lib/imageProcessing";
import CanvasSettingsModal from "./components/modals/CanvasSettingsModal.jsx";
import ConvertOptionsModal from "./components/modals/ConvertOptionsModal.jsx";
import BrandingSettingsModal from "./components/modals/BrandingSettingsModal.jsx";
import OverlaySettingsModal from "./components/modals/OverlaySettingsModal.jsx";
import FindReplaceModal from "./components/modals/FindReplaceModal.jsx";
import PaletteOptimizationModal from "./components/modals/PaletteOptimizationModal.jsx";
import ProUpgradeModal from "./components/modals/ProUpgradeModal.jsx";
import TutorialModal from "./components/modals/TutorialModal.jsx";
import EmojiIcon, { EMOJI } from "./components/EmojiIcon.jsx";
import CommunityControlCenter from "./components/CommunityControlCenter.jsx";
import CommunityForum from "./components/CommunityForum.jsx";
import {
  ACTIVE_FLAVOR,
  activeFlavorId,
  getFlavorBadgeCopy,
  getFlavorDescription,
  getFlavorTagline,
  getFlavorUserAreaConfig,
  isPremiumBuild,
  shouldHideUpgradeUi,
} from "./config/flavors";

// ------------------------------------
// NEW FIREBASE IMPORTS
// ------------------------------------
import { httpsCallable } from "firebase/functions";
import { auth, db, cloudFunctions } from "./firebase";
import {
  GoogleAuthProvider,
  signInWithPopup,
  signInWithRedirect,
  signInWithCredential,
  signOut,
  onAuthStateChanged,
  // ADDED: Imports for fixing persistence issue
  getRedirectResult,
  browserLocalPersistence,
  setPersistence,
} from "firebase/auth";
import { doc, getDoc, setDoc } from "firebase/firestore"; // ADDED: Firestore functions
import { GoogleAuth } from "@codetrix-studio/capacitor-google-auth";
// ------------------------------------

import { Capacitor } from "@capacitor/core";
import { Filesystem, Directory, Encoding } from "@capacitor/filesystem";

const isNativeRuntime = () =>
  typeof Capacitor !== "undefined" &&
  typeof Capacitor.isNativePlatform === "function" &&
  Capacitor.isNativePlatform();

// Utility function to get symbol color (black or white) based on background hex
const getSymbolColor = (hex) => {
  // Convert hex to R, G, B
  const r = parseInt(hex.slice(1, 3), 16) / 255;
  const g = parseInt(hex.slice(3, 5), 16) / 255;
  const b = parseInt(hex.slice(5, 7), 16) / 255;

  // Calculate Luminance (L = 0.2126*R + 0.7152*G + 0.0722*B)
  const luminance = 0.2126 * r + 0.7152 * g + 0.0722 * b;

  // Return white for dark backgrounds (luminance < 0.5) and black for light backgrounds
  return luminance < 0.5 ? "white" : "black";
};


// Constants
const SYMBOL_LIST = [
  "×","•","☆","✳","✕","□","■","●","◦","△","▲","◇","◆","✚","✖","％","#","@","."
, "+", "=",
  "◻","◼","◯","○","◉","◎","▢","▣","▦","▧","▨","▩","▥","▤","♢","♣","♠","♤","♧",
  "✦","✧","※","✢","✣","✤","✥","❖","⬣","⬢","⬡","⎔","⌂","∎","∗","∴","∵","╳","╱","╲"
];

const FRACTIONAL_SLOTS = 4;
const FRACTIONAL_INTENSITY_LEVELS = [
  { value: 0, label: "Very Low" },
  { value: 1, label: "Low" },
  { value: 2, label: "Medium" },
  { value: 3, label: "Medium High" },
  { value: 4, label: "High" },
  { value: 5, label: "Very High" },
];
const FRACTIONAL_TRIANGLES = [
  [
    [0, 0],
    [0.5, 0],
    [0, 0.5],
  ], // NW quarter
  [
    [1, 0],
    [1, 0.5],
    [0.5, 0],
  ], // NE quarter
  [
    [0, 0.5],
    [0.5, 1],
    [0, 1],
  ], // SW quarter
  [
    [0.5, 1],
    [1, 0.5],
    [1, 1],
  ], // SE quarter
];
const FRACTIONAL_HALF_TRIANGLES = [
  { mask: 0b0011, points: [[0, 0], [1, 0], [0, 1]] }, // top row -> / diagonal
  { mask: 0b1100, points: [[1, 0], [1, 1], [0, 1]] }, // bottom row -> / diagonal
  { mask: 0b0101, points: [[0, 0], [0, 1], [1, 1]] }, // left col -> \ diagonal
  { mask: 0b1010, points: [[0, 0], [1, 0], [1, 1]] }, // right col -> \ diagonal
];

const INITIAL_COLS = 85;
const INITIAL_ROWS = 85;
const MAJOR_GRID_EVERY = 10;
const MAX_ZOOM = 8;
const MIN_ZOOM = 0.25;
const ZOOM_FACTOR = 1.25;
const DEFAULT_ZOOM = 0.75;
const DEFAULT_DESIGN_NAME = "Untitled Design";
const GOOGLE_WEB_CLIENT_ID = "262250690428-uuakid5uhin5g8l5hp0dq4n4t9qr94fm.apps.googleusercontent.com";
const readEnvValue = (key) => {
  if (typeof import.meta !== "undefined" && import.meta.env?.[key]) {
    return import.meta.env[key];
  }
  if (typeof process !== "undefined" && process.env?.[key]) {
    return process.env[key];
  }
  return "";
};
const PAYMENT_PROVIDER = (
  readEnvValue("VITE_PAYMENT_PROVIDER") || "auto"
).toLowerCase();
const GOOGLE_PAY_MERCHANT_ID = readEnvValue("VITE_GOOGLE_PAY_MERCHANT_ID");
const PLAY_BILLING_PRODUCT_ID = readEnvValue("VITE_PLAY_BILLING_PRODUCT_ID");

// NEW: Increased Max Logo Size for Import
const MAX_LOGO_SIZE_W = 512;
const MAX_LOGO_SIZE_H = 1024;

// NEW EXPORT PADDING CONSTANTS (Adjusted to be relative to HD_MULTIPLIER later)
const EXPORT_HORIZONTAL_PADDING = 100; // Base value for left/right padding
const EXPORT_TOP_PADDING = 100; // Base value for padding below the header/above the design
const BASE_HEADER_HEIGHT = 150; // Base height in logical pixels for header area
const DEFAULT_LOGO_SCALE = 80; // Default percentage scale (relative to header height)
const DEFAULT_TEXT_SCALE = 100; // Default percentage scale (relative to base font size)
const NATIVE_SAVE_FOLDER = "CrossStitchDesigner";
const SAVE_LOG = "[Save]";
const PRO_TRIAL_DURATION_MS = 24 * 60 * 60 * 1000;
const TRIAL_STORAGE_PREFIX = "csd_trial_start_";

const formatDuration = (hours) => {
  if (!Number.isFinite(hours) || hours <= 0) return "0h";
  const totalMinutes = Math.round(hours * 60);
  const h = Math.floor(totalMinutes / 60);
  const m = totalMinutes % 60;
  if (h === 0) return `${m}m`;
  if (m === 0) return `${h}h`;
  return `${h}h ${m}m`;
};

const formatStitchCount = (value) => {
  if (!Number.isFinite(value)) return "0";
  if (Math.abs(value - Math.round(value)) < 1e-3) {
    return Math.round(value).toLocaleString();
  }
  return value.toLocaleString(undefined, {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
};

const seedPaletteWithDefaultBlack = (palette = []) => {
  if (palette.length) return palette;
  const defaultBlack =
    (dmcPalette.find((c) => c.code === "310") ||
      anchorPalette.find((c) => c.hex === "#000000") || {
        code: "BLK",
        name: "Black",
        hex: "#000000",
      });
  const firstSymbol = SYMBOL_LIST[0] || "■";
  return [{ ...defaultBlack, symbol: firstSymbol }];
};

const COMMUNITY_SETTINGS_STORAGE_KEY = "csd_user_settings";
const SAVED_DESIGNS_STORAGE_KEY = "csd_saved_designs";
const COMMUNITY_PROFILE_COLLECTION = "community_profiles";
const COMMUNITY_FORUM_PATH = "/community";
const BOOTSTRAP_ADMIN_UIDS = new Set(["7Wf9wvGPkNTWJ3mymz9gS2aKFQO2"]);
const COMMUNITY_SETTINGS_TEMPLATE = {
  displayName: "",
  handle: "",
  bio: "",
  website: "",
  shopLinks: [],
  shareGallery: true,
  allowMessages: true,
  allowDownloads: false,
  showWorkInProgress: true,
  autoPublish: false,
  allowCollabInvites: true,
  allowBetaAccess: false,
  shareAnalytics: false,
  profilePhoto: "",
  memberSince: "",
  deleteRequestedAt: null,
  deleteScheduledFor: null,
  role: "member",
};

const sanitizeShopLinks = (links) => {
  if (!Array.isArray(links)) return [];
  return links
    .map((link) => ({
      label: typeof link?.label === "string" ? link.label.trim() : "",
      url: typeof link?.url === "string" ? link.url.trim() : "",
    }))
    .filter((link) => link.label || link.url);
};

const buildCommunityProfilePayload = (settings, user) => {
  const safe = createCommunitySettings(settings);
  const rawPhoto =
    typeof safe.profilePhoto === "string" ? safe.profilePhoto.trim() : "";
  const photoUrl = rawPhoto && !rawPhoto.startsWith("data:")
    ? rawPhoto
    : user?.photoURL || "";
  return {
    displayName: (safe.displayName || user?.displayName || "").trim(),
    handle: (safe.handle || (user?.email ? user.email.split("@")[0] : "")).trim(),
    bio: safe.bio || "",
    website: (safe.website || "").trim(),
    shopLinks: sanitizeShopLinks(safe.shopLinks),
    profilePhoto: photoUrl,
    memberSince: safe.memberSince || new Date().toISOString(),
    shareGallery: !!safe.shareGallery,
    allowMessages: !!safe.allowMessages,
    allowDownloads: !!safe.allowDownloads,
    showWorkInProgress: !!safe.showWorkInProgress,
    autoPublish: !!safe.autoPublish,
    allowCollabInvites: !!safe.allowCollabInvites,
    allowBetaAccess: !!safe.allowBetaAccess,
    shareAnalytics: !!safe.shareAnalytics,
    deleteRequestedAt: safe.deleteRequestedAt || null,
    deleteScheduledFor: safe.deleteScheduledFor || null,
  };
};

const buildCommunityProfileSeedPayload = (settings, user) => ({
  ...buildCommunityProfilePayload(settings, user),
  role: BOOTSTRAP_ADMIN_UIDS.has(user?.uid) ? "admin" : "member",
});

const createCommunitySettings = (overrides = {}) => {
  const merged = {
    ...COMMUNITY_SETTINGS_TEMPLATE,
    ...overrides,
  };
  merged.shopLinks = sanitizeShopLinks(merged.shopLinks);
  if (!merged.memberSince) {
    merged.memberSince = new Date().toISOString();
  }
  return merged;
};

const loadSavedDesignNames = () => {
  if (typeof window === "undefined") return [];
  try {
    const raw = window.localStorage.getItem(SAVED_DESIGNS_STORAGE_KEY);
    if (!raw) return [];
    const parsed = JSON.parse(raw);
    return Array.isArray(parsed)
      ? parsed.map((name) => (typeof name === "string" ? name.trim() : "")).filter(Boolean)
      : [];
  } catch (error) {
    console.warn("Failed to parse saved design list:", error);
    return [];
  }
};

const loadStoredCommunitySettings = () => {
  if (typeof window === "undefined") {
    return createCommunitySettings();
  }
  try {
    const raw = window.localStorage.getItem(
      COMMUNITY_SETTINGS_STORAGE_KEY
    );
    if (raw) {
      const parsed = JSON.parse(raw);
      return createCommunitySettings(parsed);
    }
  } catch (error) {
    console.warn("Failed to parse stored community settings:", error);
  }
  return createCommunitySettings();
};

const getTrialStorageKey = (uid) => `${TRIAL_STORAGE_PREFIX}${uid}`;

const readTrialStart = (uid) => {
  if (!uid || typeof window === "undefined") return null;
  try {
    const raw = window.localStorage.getItem(getTrialStorageKey(uid));
    if (!raw) return null;
    const parsed = Number(raw);
    return Number.isFinite(parsed) ? parsed : null;
  } catch (error) {
    console.warn("Failed to read trial start:", error);
    return null;
  }
};

const writeTrialStart = (uid, timestamp) => {
  if (!uid || typeof window === "undefined") return;
  try {
    window.localStorage.setItem(getTrialStorageKey(uid), String(timestamp));
  } catch (error) {
    console.warn("Failed to persist trial start:", error);
  }
};

const getTrialInfo = (uid) => {
  const startedAt = readTrialStart(uid);
  if (!startedAt) {
    return { active: false, startedAt: null, expiresAt: null };
  }
  const expiresAt = startedAt + PRO_TRIAL_DURATION_MS;
  return {
    active: Date.now() < expiresAt,
    startedAt,
    expiresAt,
  };
};
// Initial state definition
const getInitialDesignState = (cols, rows) => ({
  layers: [
    createEmptyLayer(generateId(), "Stitches", cols, rows),
    // NEW: Placeholder for a Fractional Stitch Layer
    // fractionalCells: new Int32Array(cols * rows * 4).fill(-1), 
    // fractionalStitches: [], // For more complex data
  ],
  backstitchLines: [],
  // NEW: Placeholder for Specialty Stitches (Knots/Beads)
  specialtyStitches: [],
  customPalette: seedPaletteWithDefaultBlack([]),
  cols,
  rows,
  designMode: "aida",
  aidaCount: 14,
  gridThin: 1,
  margin: 3,
  imageOverlay: {
    opacity: 0.5,
    url: null,
    // --- NEW IMAGE OVERLAY LOCATION/SIZE STATE ---
    x: 0, 
    y: 0,
    w: cols,
    h: rows,
    // ---------------------------------------------
  },
  // NEW: Branding state
  companyName: "",
  logoUrl: null,
  // NEW: Branding Position & Alignment
  logoAlignment: "left", // 'left', 'center', 'right'
  textAlignment: "right", // 'left', 'center', 'right'
  logoMargin: 20, // Margin in logical pixels (from side/top/bottom)
  textMargin: 20, // Margin in logical pixels (from side/top/bottom)
  // NEW: Branding Size
  logoScale: DEFAULT_LOGO_SCALE, // Logo scale in percentage
  textScale: DEFAULT_TEXT_SCALE, // Text scale in percentage
  // NEW: Grid Color
  gridColor: "#d0d0d0", 
  // NEW: Fabric Color
  fabricColor: "#ffffff",
  // NEW: Symbol Color - 'auto' means contrast-based, a hex value overrides it
  symbolColor: "auto",
  allowFractionalStitches: false,
  fractionalCells: new Int32Array(cols * rows * FRACTIONAL_SLOTS).fill(-1),
});

// Utility functions
const generateId = () => Math.random().toString(36).slice(2) + Date.now().toString(36);

// Normalize any design snapshot so JSON comparisons don't get tripped up by typed arrays
const normalizeSnapshot = (snapshot) => {
  if (!snapshot) return snapshot;
  return {
    ...snapshot,
    layers: (snapshot.layers || []).map((L) => ({
      ...L,
      cells: Array.from(L.cells || []),
    })),
    fractionalCells: Array.from(snapshot.fractionalCells || []),
  };
};

export default function CrossStitchApp() {
  const canvasRef = useRef(null);
  const imageOverlayRef = useRef(null);
  const history = useRef(new HistoryStack(100));
  const currentInitialState = useRef(getInitialDesignState(INITIAL_COLS, INITIAL_ROWS)); 
  const editionName = ACTIVE_FLAVOR.name;
  const flavorBadges = getFlavorBadgeCopy();
  const flavorTagline = getFlavorTagline();
  const flavorDescription = getFlavorDescription();
  const flavorUserConfig = getFlavorUserAreaConfig();
  const isPremiumEdition = isPremiumBuild;
  const hideUpgradeUi = shouldHideUpgradeUi;
  
  // State for File System Access API
  const [savedFileHandle, setSavedFileHandle] = useState(null); 
  const [hasFsPermission, setHasFsPermission] = useState(false);
  
  // State declarations
  const [currentDesignName, setCurrentDesignName] = useState(DEFAULT_DESIGN_NAME);
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
  
  const [user, setUser] = useState(null); // State to hold the logged-in user
  // ADDED: State to track Pro status
  const [isPro, setIsPro] = useState(isPremiumEdition); 
  // NEW: State for Pro Upgrade Modal
  const [showProModal, setShowProModal] = useState(false);
  const [isProcessingCheckout, setIsProcessingCheckout] = useState(false);
  const [pendingProFeature, setPendingProFeature] = useState(null);
  const [communitySettings, setCommunitySettings] = useState(loadStoredCommunitySettings);
  const [savedDesignNames, setSavedDesignNames] = useState(loadSavedDesignNames);
  const [showCommunityCenter, setShowCommunityCenter] = useState(false);
  const [showCommunityForum, setShowCommunityForum] = useState(false);
  const [showExportModal, setShowExportModal] = useState(false);
  const [exportFormat, setExportFormat] = useState("png");
  const [isExporting, setIsExporting] = useState(false);
  const communitySettingsRef = useRef(communitySettings);
  const communitySyncTimeoutRef = useRef(null);
  const lastCommunitySyncRef = useRef("");
  const openCommunityForum = useCallback((replace = false) => {
    setShowCommunityForum(true);
    if (typeof window === "undefined") return;
    const method = replace ? "replaceState" : "pushState";
    window.history?.[method]?.({}, "", COMMUNITY_FORUM_PATH);
  }, []);
  const closeCommunityForum = useCallback((replace = false) => {
    setShowCommunityForum(false);
    if (typeof window === "undefined") return;
    const method = replace ? "replaceState" : "pushState";
    window.history?.[method]?.({}, "", "/");
  }, []);
  
  const [showDesignMenu, setShowDesignMenu] = useState(false);
  const [paletteBrand, setPaletteBrand] = useState("DMC");
  const [showSidebar, setShowSidebar] = useState(false); // New state for sidebar toggle
  const [showLayers, setShowLayers] = useState(true); // Layers toggle state
  // NEW: My Palette Toggle State
  const [showMyPalette, setShowMyPalette] = useState(false); 
  const hasCenteredOnMount = useRef(false);
  const [isCompactToolbar, setIsCompactToolbar] = useState(() => {
    if (typeof window === "undefined") return false;
    return window.innerWidth < 1024;
  });
  const [showToolsMenu, setShowToolsMenu] = useState(false);

  // NEW: Theme State
  const getInitialTheme = () => {
    const savedTheme = localStorage.getItem('theme');
    if (savedTheme) return savedTheme;
    return window.matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : 'light';
  };
  const [theme, setTheme] = useState(getInitialTheme);
  const toggleTheme = () => {
    setTheme(t => t === 'light' ? 'dark' : 'light');
  };
  // Onboarding prompt to pick a theme on first launch
  const [showThemeOnboarding, setShowThemeOnboarding] = useState(() => {
    if (typeof window === "undefined") return false;
    return localStorage.getItem("csd_theme_onboarded") === "1" ? false : true;
  });
  const handleThemeChoice = (mode) => {
    setTheme(mode);
    if (typeof window !== "undefined") {
      localStorage.setItem("csd_theme_onboarded", "1");
    }
    setShowThemeOnboarding(false);
  };
  
  const [imageOverlay, setImageOverlay] = useState(currentInitialState.current.imageOverlay);
  const [cols, setCols] = useState(INITIAL_COLS);
  const [rows, setRows] = useState(INITIAL_ROWS);
  const [cellSize, setCellSize] = useState(suggestedCellSize(currentInitialState.current.aidaCount));
  const [zoom, setZoom] = useState(DEFAULT_ZOOM);
  const [offset, setOffset] = useState({ x: 20, y: 20 });
  const [gridThin, setGridThin] = useState(currentInitialState.current.gridThin);
  const [gridColor, setGridColor] = useState(currentInitialState.current.gridColor); // NEW State
  const [showCenterLines, setShowCenterLines] = useState(true);

  const [aidaCount, setAidaCount] = useState(currentInitialState.current.aidaCount);
  const [margin, setMargin] = useState(currentInitialState.current.margin);
  const [designMode, setDesignMode] = useState(
    currentInitialState.current.designMode || "aida"
  );
  const [pendingCols, setPendingCols] = useState(INITIAL_COLS);
  const [pendingRows, setPendingRows] = useState(INITIAL_ROWS);
  const [pendingAida, setPendingAida] = useState(currentInitialState.current.aidaCount);
  const [pendingGridThin, setPendingGridThin] = useState(currentInitialState.current.gridThin);
  const [pendingMargin, setPendingMargin] = useState(currentInitialState.current.margin);
  const [pendingDesignMode, setPendingDesignMode] = useState(
    currentInitialState.current.designMode || "aida"
  );
  const [pendingGridColor, setPendingGridColor] = useState(currentInitialState.current.gridColor); // NEW Pending State
  const [showSettings, setShowSettings] = useState(false);
  const [showOverlaySettings, setShowOverlaySettings] = useState(false); // <--- NEW MODAL STATE

  // Pending states for Image Overlay Settings
  const [pendingOverlayX, setPendingOverlayX] = useState(imageOverlay.x);
  const [pendingOverlayY, setPendingOverlayY] = useState(imageOverlay.y);
  const [pendingOverlayW, setPendingOverlayW] = useState(imageOverlay.w);
  const [pendingOverlayH, setPendingOverlayH] = useState(imageOverlay.h);
  const [pendingOverlayOpacity, setPendingOverlayOpacity] = useState(imageOverlay.opacity);

  // NEW State for Fabric Color
  const [fabricColor, setFabricColor] = useState(currentInitialState.current.fabricColor || "#ffffff"); 
  const [pendingFabricColor, setPendingFabricColor] = useState(currentInitialState.current.fabricColor || "#ffffff");

  // NEW: Symbol Color State
  const [symbolColor, setSymbolColor] = useState(currentInitialState.current.symbolColor || "auto");
  const [pendingSymbolColor, setPendingSymbolColor] = useState(currentInitialState.current.symbolColor || "auto");


  // State for file naming prompt
  const [showNamePrompt, setShowNamePrompt] = useState(false);
  const [pendingDesignName, setPendingDesignName] = useState(DEFAULT_DESIGN_NAME);

  // NEW: Branding State
  const [companyName, setCompanyName] = useState(currentInitialState.current.companyName);
  const [logoUrl, setLogoUrl] = useState(currentInitialState.current.logoUrl);
  const [logoImage, setLogoImage] = useState(null); // The actual Image object for drawing
  const [showBrandingSettings, setShowBrandingSettings] = useState(false); // New modal state
  // NEW Branding Position/Alignment States
  const [logoAlignment, setLogoAlignment] = useState(currentInitialState.current.logoAlignment);
  const [textAlignment, setTextAlignment] = useState(currentInitialState.current.textAlignment);
  const [logoMargin, setLogoMargin] = useState(currentInitialState.current.logoMargin);
  const [textMargin, setTextMargin] = useState(currentInitialState.current.textMargin);
  // NEW Branding Size States
  const [logoScale, setLogoScale] = useState(currentInitialState.current.logoScale);
  const [textScale, setTextScale] = useState(currentInitialState.current.textScale);


  const [pendingCompanyName, setPendingCompanyName] = useState(currentInitialState.current.companyName);
  const [pendingLogoAlignment, setPendingLogoAlignment] = useState(currentInitialState.current.logoAlignment);
  const [pendingTextAlignment, setPendingTextAlignment] = useState(currentInitialState.current.textAlignment);
  const [pendingLogoMargin, setPendingLogoMargin] = useState(currentInitialState.current.logoMargin);
  const [pendingTextMargin, setPendingTextMargin] = useState(currentInitialState.current.textMargin);
  // NEW Pending Size States
  const [pendingLogoScale, setPendingLogoScale] = useState(currentInitialState.current.logoScale);
  const [pendingTextScale, setPendingTextScale] = useState(currentInitialState.current.textScale);

  // NEW Branding Preview State
  const [showBrandingPreview, setShowBrandingPreview] = useState(false); 

  // NEW: Dominant Color Extraction State (Configurable Count)
  const [dominantColorCount, setDominantColorCount] = useState(100);
  const [pendingDominantColorCount, setPendingDominantColorCount] = useState(100);
  const [cartoonifyConversion, setCartoonifyConversion] = useState(false);
  const [convertUseFractionalDetail, setConvertUseFractionalDetail] = useState(false);
  const [convertFractionalIntensity, setConvertFractionalIntensity] = useState(3);
  const [hasSeenTutorial, setHasSeenTutorial] = useState(() => {
    if (typeof window === "undefined") return false;
    return localStorage.getItem("csd_seen_tutorial") === "1";
  });
  const [showTutorial, setShowTutorial] = useState(false);
  const [allowFractionalStitches, setAllowFractionalStitches] = useState(
    currentInitialState.current.allowFractionalStitches
  );
  const [pendingAllowFractionalStitches, setPendingAllowFractionalStitches] =
    useState(currentInitialState.current.allowFractionalStitches);
  const [showEstimates, setShowEstimates] = useState(false);
  const [estimateStrands, setEstimateStrands] = useState(2);
  const [estimateWasteFactor, setEstimateWasteFactor] = useState(1);
  const [estimateSkeinLength, setEstimateSkeinLength] = useState(315); // inches (~8m)
  const [estimateStitchesPerHour, setEstimateStitchesPerHour] = useState(150);
  const [exportBorderEnabled, setExportBorderEnabled] = useState(false);
  const [exportBorderColor, setExportBorderColor] = useState("#000000");
  const [exportBorderThickness, setExportBorderThickness] = useState(6);
  const [fractionalCells, setFractionalCells] = useState(
    currentInitialState.current.fractionalCells
  );
  const [fractionalMode, setFractionalMode] = useState(false);

  useEffect(() => {
    if (isPro) return;
    setAllowFractionalStitches(false);
    setPendingAllowFractionalStitches(false);
    setFractionalMode(false);
    setConvertUseFractionalDetail(false);
  }, [isPro]);

  const [customPalette, setCustomPalette] = useState(currentInitialState.current.customPalette);
  const [currentColorIndex, setCurrentColorIndex] = useState(0);
  const [search, setSearch] = useState("");
  const [showSymbols, setShowSymbols] = useState(true);
  const [showSymbolEditor, setShowSymbolEditor] = useState(false);
  const [symbolEditIndex, setSymbolEditIndex] = useState(null);

  const [tool, setTool] = useState("draw");
  const [isDrawingLine, setIsDrawingLine] = useState(false);
  const [lineStart, setLineStart] = useState(null);
  const [lineColor, setLineColor] = useState("#FF0000");
  const [currentClientCell, setCurrentClientCell] = useState(null);

  const [backstitchLines, setBackstitchLines] = useState(currentInitialState.current.backstitchLines);
  const [layers, setLayers] = useState(currentInitialState.current.layers);
  const [activeLayer, setActiveLayer] = useState(0);

  const [lastPan, setLastPan] = useState(null);
  const [lastTouchDistance, setLastTouchDistance] = useState(null);
  
  const lineDrawMode = useRef('tap'); // 'tap' or 'drag' for line tool
  const trialExpiryTimeoutRef = useRef(null);

  // --- NEW SELECTION STATE ---
  const [selectionStart, setSelectionStart] = useState(null); // { c, r } of the starting cell
  const [selectionEnd, setSelectionEnd] = useState(null);   // { c, r } of the current/ending cell
  const [isSelecting, setIsSelecting] = useState(false);  // True while the user is dragging the selection box
  const [copiedCells, setCopiedCells] = useState(null); // Stores the selected cell data for move/paste: { cells: Int32Array, width, height }
  const [selectionOffset, setSelectionOffset] = useState(null); // { c: dx, r: dy } for moving selected area (Top-Left of block)
  // ---------------------------
  
  // --- NEW IMAGE IMPORT STATE FOR MODAL ---
  const [showConvertOptions, setShowConvertOptions] = useState(false);
  const [pendingImageToConvert, setPendingImageToConvert] = useState(null); // Stores the loaded <img> object

  // --- NEW FIND/REPLACE STATE ---
  const [showReplaceModal, setShowReplaceModal] = useState(false);
  const [findColorIndex, setFindColorIndex] = useState(null); // The index of the color to be replaced
  // -----------------------------
  
  // --- NEW PALETTE OPTIMIZATION/CONVERSION STATE ---
  const [showOptimizeModal, setShowOptimizeModal] = useState(false);
  const [optimizationThreshold, setOptimizationThreshold] = useState(50); // Min usage count to keep
  const [pendingConversionBrand, setPendingConversionBrand] = useState("DMC");
  // -------------------------------------------------


  // ------------------------------------
  // NEW FIREBASE AUTHENTICATION LOGIC
  // ------------------------------------
  const checkProStatus = useCallback(async (uid) => {
    if (isPremiumEdition) {
      setIsPro(true);
      return;
    }
    if (!uid) {
      setIsPro(false);
      return;
    }
    const trial = getTrialInfo(uid);
    if (trial.active) {
      setIsPro(true);
      return;
    }
    try {
      const userDocRef = doc(db, "pro_users", uid);
      const userDoc = await getDoc(userDocRef);
      
      // Check if the document exists AND if the 'is_pro' field is true
      if (userDoc.exists() && userDoc.data().is_pro === true) {
        setIsPro(true);
      } else {
        setIsPro(false);
      }
    } catch (error) {
      console.error("Error fetching pro status:", error);
      setIsPro(false);
    }
  }, [isPremiumEdition]);

  // Request filesystem permission on native platforms so saves can succeed
  useEffect(() => {
    if (!isNativeRuntime()) return;

    let cancelled = false;

    const ensureFsPermission = async () => {
      try {
        const status = await Filesystem.checkPermissions();
        if (status.publicStorage === "granted") {
          if (!cancelled) setHasFsPermission(true);
          return;
        }

        const requestStatus = await Filesystem.requestPermissions();
        if (!cancelled) {
          setHasFsPermission(requestStatus.publicStorage === "granted");
        }
      } catch (permissionError) {
        console.warn("Filesystem permission request failed:", permissionError);
      }
    };

    ensureFsPermission();

    return () => {
      cancelled = true;
    };
  }, []);

  // Proactively ask for storage permission on native at startup (best-effort)
  useEffect(() => {
    if (!isNativeRuntime()) return;
    ensureNativeFsPermission();
  }, []);

  useEffect(() => {
    // Listen for auth changes
    const unsubscribe = onAuthStateChanged(auth, (currentUser) => {
      console.log("[Auth] onAuthStateChanged user:", currentUser ? currentUser.uid : "null");
      setUser(currentUser);
      if (isPremiumEdition) {
        setIsPro(true);
        return;
      }
      // Check Pro Status when user state changes
      if (currentUser) {
        checkProStatus(currentUser.uid);
      } else {
        setIsPro(false); // Clear Pro status on logout
      }
    });
    return () => unsubscribe(); // Cleanup on unmount
  }, [checkProStatus, isPremiumEdition]);

  useEffect(() => {
    if (trialExpiryTimeoutRef.current) {
      clearTimeout(trialExpiryTimeoutRef.current);
      trialExpiryTimeoutRef.current = null;
    }
    if (isPremiumEdition) return undefined;
    const uid = user?.uid;
    if (!uid) return undefined;
    const trial = getTrialInfo(uid);
    if (!trial.active || !trial.expiresAt) return undefined;
    const remainingMs = Math.max(0, trial.expiresAt - Date.now());
    trialExpiryTimeoutRef.current = setTimeout(() => {
      checkProStatus(uid);
    }, remainingMs);
    return () => {
      if (trialExpiryTimeoutRef.current) {
        clearTimeout(trialExpiryTimeoutRef.current);
        trialExpiryTimeoutRef.current = null;
      }
    };
  }, [user?.uid, checkProStatus, isPremiumEdition]);

  useEffect(() => {
    if (isPremiumEdition) return undefined;
    if (typeof document === "undefined") return undefined;
    const handleVisibility = () => {
      if (document.visibilityState === "visible" && user?.uid) {
        checkProStatus(user.uid);
      }
    };
    document.addEventListener("visibilitychange", handleVisibility);
    return () => {
      document.removeEventListener("visibilitychange", handleVisibility);
    };
  }, [user?.uid, checkProStatus, isPremiumEdition]);

// ADDED: useEffect to handle sign-in redirect result and set local persistence
  useEffect(() => {
    const handleRedirect = async () => {
        try {
            console.log("[Auth] Checking redirect result...");
            // Check if there is a redirect result pending after a successful sign-in
            const result = await getRedirectResult(auth); 
            if (result?.user) {
              console.log("[Auth] Redirect result user:", result.user.uid || "(no uid)");
            } else {
              console.log("[Auth] Redirect result empty (no user).");
            }
            if (result) {
                // If the redirect was successful, ensure persistence is set to local 
                // to prevent future session storage issues on redirects/reloads.
                await setPersistence(auth, browserLocalPersistence); 
                console.log("[Auth] Persistence set to browserLocalPersistence after redirect.");
            }
        } catch (error) {
            // NOTE: If you are seeing auth/unauthorized-domain here, the Google Cloud config is still wrong.
            // If you are seeing storage partition error, this logic should fix it on the next load.
            console.error("Error handling redirect result:", error);
            alert(error?.message || "Error handling redirect result");
        }
    };
    // Run this logic once when the component mounts
    handleRedirect();
}, []);

  // Initialize native Google Auth plugin on device
  useEffect(() => {
    if (!isNativeRuntime()) return;
    GoogleAuth.initialize({
      clientId: GOOGLE_WEB_CLIENT_ID,
      scopes: ["email", "profile", "openid"],
      grantOfflineAccess: true,
    }).catch((err) => console.error("GoogleAuth initialize failed:", err));
  }, []);


  // Use native Google sign-in on native (Android/iOS) and web popup on browser
  const handleGoogleLogin = async () => {
    const provider = new GoogleAuthProvider();
    try {
      console.log("[Auth] Starting Google login. isNativeRuntime:", isNativeRuntime());
      await setPersistence(auth, browserLocalPersistence);
      if (isNativeRuntime()) {
        console.log("[Auth] Using native GoogleAuth plugin...");
        const nativeUser = await GoogleAuth.signIn();
        const idToken = nativeUser?.authentication?.idToken;
        if (!idToken) throw new Error("No idToken returned from native GoogleAuth");
        const credential = GoogleAuthProvider.credential(idToken);
        await signInWithCredential(auth, credential);
      } else {
        console.log("[Auth] Using signInWithPopup...");
        await signInWithPopup(auth, provider);
      }
    } catch (error) {
      console.error("Login attempt failed:", error);
      // Fallback for popup blockers / closures on webview by switching to redirect
      if (
        error?.code === "auth/popup-closed-by-user" ||
        error?.code === "auth/popup-blocked"
      ) {
        try {
          console.log("[Auth] Popup flow failed, retrying with redirect...");
          await signInWithRedirect(auth, provider);
          return;
        } catch (redirectError) {
          console.error("Redirect fallback also failed:", redirectError);
        }
      }
      alert(
        error?.message ||
          "Login attempt failed. Check Firebase config and browser settings."
      );
    }
  };

  // Dedicated register flow (same auth, but always lets you pick the account)
  const handleGoogleRegister = async () => {
    const provider = new GoogleAuthProvider();
    // Force account chooser so users can register with a different Google account
    provider.setCustomParameters?.({ prompt: "select_account" });
    try {
      console.log("[Auth] Starting Google register. isNativeRuntime:", isNativeRuntime());
      await setPersistence(auth, browserLocalPersistence);
      if (isNativeRuntime()) {
        const nativeUser = await GoogleAuth.signIn();
        const idToken = nativeUser?.authentication?.idToken;
        if (!idToken) throw new Error("No idToken returned from native GoogleAuth");
        const credential = GoogleAuthProvider.credential(idToken);
        const userCredential = await signInWithCredential(auth, credential);
        const uid = userCredential?.user?.uid || auth.currentUser?.uid;
        startTrialIfNeeded(uid);
        if (uid) {
          const trial = getTrialInfo(uid);
          if (trial.active) setIsPro(true);
        }
      } else {
        const userCredential = await signInWithPopup(auth, provider);
        const uid = userCredential?.user?.uid || auth.currentUser?.uid;
        startTrialIfNeeded(uid);
        if (uid) {
          const trial = getTrialInfo(uid);
          if (trial.active) setIsPro(true);
        }
      }
    } catch (error) {
      console.error("Register attempt failed:", error);
      if (
        error?.code === "auth/popup-closed-by-user" ||
        error?.code === "auth/popup-blocked"
      ) {
        try {
          console.log("[Auth] Register popup failed, retrying with redirect...");
          await signInWithRedirect(auth, provider);
          return;
        } catch (redirectError) {
          console.error("Register redirect also failed:", redirectError);
        }
      }
      alert(
        error?.message ||
          "Register attempt failed. Check Firebase config and browser settings."
      );
    }
  };

  const handleLogout = async () => {
    await signOut(auth);
  };
  // ------------------------------------

  // Enhanced upgrade prompts in CrossStitchApp.jsx
  // Add this function for consistent upgrade prompts
  const showUpgradePrompt = (featureName) => {
    setPendingProFeature(featureName);
    setShowProModal(true);
  };

  // Update your requirePro function
  const requirePro = (featureName, actionCallback) => {
    return (...args) => {
      if (!isPro) {
        showUpgradePrompt(featureName);
        return;
      }
      actionCallback(...args);
    };
  };

  const closeProModal = () => {
    setShowProModal(false);
    setPendingProFeature(null);
    setIsProcessingCheckout(false);
  };

  const updateCommunitySetting = (key, value) => {
    setCommunitySettings((prev) => ({
      ...prev,
      [key]: value,
    }));
  };

  const startTrialIfNeeded = useCallback((uid) => {
    if (!uid || isPremiumEdition) return null;
    const existingStart = readTrialStart(uid);
    if (existingStart) return existingStart;
    const now = Date.now();
    writeTrialStart(uid, now);
    return now;
  }, [isPremiumEdition]);

  // Derived values
  const activeBrand = paletteBrand === "DMC" ? dmcPalette : anchorPalette;
  const filteredPalette = activeBrand.filter(
    (c) =>
      (c.code || "").toLowerCase().includes(search.toLowerCase()) ||
      (c.name || "").toLowerCase().includes(search.toLowerCase())
  );
  const requiresForumAuth = !user;

  const isPixelMode = designMode === "pixel";
  const safeAidaCount = aidaCount > 0 ? aidaCount : 1;
  const effectiveAidaCount = isPixelMode ? 1 : safeAidaCount;
  const effectiveMargin = isPixelMode ? 0 : margin;
  const designWidthInches = cols / effectiveAidaCount;
  const designHeightInches = rows / effectiveAidaCount;
  const designWidthCM = designWidthInches * 2.54;
  const designHeightCM = designHeightInches * 2.54;
  const totalFabricWidth = designWidthInches + 2 * effectiveMargin;
  const totalFabricHeight = designHeightInches + 2 * effectiveMargin;
  const totalFabricWidthCM = totalFabricWidth * 2.54;
  const totalFabricHeightCM = totalFabricHeight * 2.54;

  const getCenteredOffset = useCallback(
    (nextZoom = DEFAULT_ZOOM, nextCols = cols, nextRows = rows) => {
      const canvas = canvasRef.current;
      if (!canvas) return { x: 20, y: 20 };
      const rect = canvas.getBoundingClientRect();
      const designWidth = nextCols * cellSize * nextZoom;
      const designHeight = nextRows * cellSize * nextZoom;
      const x = (rect.width - designWidth) / 2;
      const y = (rect.height - designHeight) / 2;
      return { x, y };
    },
    [canvasRef, cellSize, cols, rows]
  );

  const colorUsageCounts = useMemo(() => {
    if (!customPalette.length) return [];
    const counts = new Array(customPalette.length).fill(0);
    layers.forEach((layer) => {
      const { cells } = layer;
      for (let i = 0; i < cells.length; i++) {
        const colorIndex = cells[i];
        if (colorIndex >= 0 && colorIndex < counts.length) {
          counts[colorIndex] += 1;
        }
      }
    });
    if (fractionalCells?.length) {
      const totalCells = cols * rows;
      for (let idx = 0; idx < totalCells; idx++) {
        const base = idx * FRACTIONAL_SLOTS;
        for (let q = 0; q < FRACTIONAL_SLOTS; q++) {
          const colorIndex = fractionalCells[base + q];
          if (colorIndex >= 0 && colorIndex < counts.length) {
            counts[colorIndex] += 0.25;
          }
        }
      }
    }
    return counts;
  }, [layers, customPalette.length, fractionalCells, cols, rows]);

  const safeStrands = Math.max(1, estimateStrands || 1);
  const safeWasteFactor = Math.max(1, estimateWasteFactor || 1);
  const safeSkeinLength = Math.max(1, estimateSkeinLength || 1);
  const safeStitchesPerHour = Math.max(1, estimateStitchesPerHour || 1);

  const perStitchInches = useMemo(() => {
    const base = (2 * Math.SQRT2) / safeAidaCount;
    return base * safeStrands * safeWasteFactor;
  }, [safeAidaCount, safeStrands, safeWasteFactor]);
  const perStitchCm = perStitchInches * 2.54;

  const colorEstimates = useMemo(() => {
    if (!customPalette.length) return [];
    return customPalette.map((color, index) => {
      const stitches = colorUsageCounts[index] || 0;
      const inches = stitches * perStitchInches;
      const cm = inches * 2.54;
      const skeinsExact = inches / safeSkeinLength;
      const skeinsRounded = skeinsExact > 0 ? Math.ceil(skeinsExact) : 0;
      const hours = stitches / safeStitchesPerHour;
      return {
        color,
        index,
        stitches,
        inches,
        cm,
        skeinsExact,
        skeinsRounded,
        hours,
      };
    });
  }, [
    customPalette,
    colorUsageCounts,
    perStitchInches,
    safeSkeinLength,
    safeStitchesPerHour,
  ]);

  const sortedColorEstimates = useMemo(
    () =>
      [...colorEstimates].sort(
        (a, b) => b.stitches - a.stitches || a.index - b.index
      ),
    [colorEstimates]
  );

  const totalStitches = colorUsageCounts.reduce((sum, count) => sum + count, 0);
  const totalInches = totalStitches * perStitchInches;
  const totalMeters = totalInches * 0.0254;
  const totalSkeinsExact = totalInches / safeSkeinLength;
  const totalSkeinsRounded = Math.ceil(totalSkeinsExact);
  const totalTimeHours = totalStitches / safeStitchesPerHour;
  const paletteCount = customPalette.length;
  const layerCount = layers.length;

  const communityStats = useMemo(
    () => {
      const sizeLabel = isPixelMode ? "Grid size" : "Fabric size";
      const sizeValue = isPixelMode
        ? `${cols} × ${rows} px`
        : `${totalFabricWidth.toFixed(1)}″ × ${totalFabricHeight.toFixed(1)}″`;
      return {
        designCount: savedDesignNames.length,
        paletteCount,
        layerCount,
        totalStitches: formatStitchCount(totalStitches),
        estimatedHours:
          totalTimeHours > 0 ? totalTimeHours.toFixed(1) : "0.0",
        fabricSize: sizeValue,
        sizeLabel,
        sizeValue,
        fractionalEnabled: allowFractionalStitches,
      };
    },
    [
      savedDesignNames.length,
      paletteCount,
      layerCount,
      totalStitches,
      totalTimeHours,
      totalFabricWidth,
      totalFabricHeight,
      allowFractionalStitches,
      isPixelMode,
      cols,
      rows,
    ]
  );

  
  // Helper for cell index calculation
  const getIndex = useCallback((c, r) => r * cols + c, [cols]);

  const getCellInfoFromClient = (clientX, clientY) => {
    const canvas = canvasRef.current;
    if (!canvas) return null;
    const rect = canvas.getBoundingClientRect();
    const cs = cellSize * zoom;
    const gridX = (clientX - rect.left - offset.x) / cs;
    const gridY = (clientY - rect.top - offset.y) / cs;
    const col = Math.floor(gridX);
    const row = Math.floor(gridY);
    if (col < 0 || row < 0 || col >= cols || row >= rows) return null;
    return {
      cellIndex: row * cols + col,
      col,
      row,
      localX: gridX - col,
      localY: gridY - row,
    };
  };

  const quadrantFromLocalOffsets = (localX, localY) => {
    const top = localY < 0.5;
    const left = localX < 0.5;
    if (top && left) return 0; // NW
    if (top && !left) return 1; // NE
    if (!top && left) return 2; // SW
    return 3; // SE
  };

  const setFractionalQuadrantValue = (cellIndex, quadrant, value) => {
    if (
      !allowFractionalStitches ||
      cellIndex == null ||
      quadrant == null ||
      quadrant < 0 ||
      quadrant >= FRACTIONAL_SLOTS
    ) {
      return;
    }
    setFractionalCells((prev) => {
      const offsetIndex = cellIndex * FRACTIONAL_SLOTS + quadrant;
      if (prev[offsetIndex] === value) return prev;
      const next = new Int32Array(prev);
      next[offsetIndex] = value;
      return next;
    });
  };

  const clearFractionalValuesForIndices = (indices) => {
    if (!allowFractionalStitches || !indices || indices.length === 0) return;
    setFractionalCells((prev) => {
      let changed = false;
      const next = new Int32Array(prev);
      for (const idx of indices) {
        if (idx == null || idx < 0) continue;
        const base = idx * FRACTIONAL_SLOTS;
        for (let q = 0; q < FRACTIONAL_SLOTS; q++) {
          if (next[base + q] !== -1) {
            next[base + q] = -1;
            changed = true;
          }
        }
      }
      return changed ? next : prev;
    });
  };


  // --- NEW: Selection Helper ---
  const getNormalizedSelection = useCallback(() => {
    if (!selectionStart || !selectionEnd) return null;

    const minC = Math.min(selectionStart.c, selectionEnd.c);
    const maxC = Math.max(selectionStart.c, selectionEnd.c);
    const minR = Math.min(selectionStart.r, selectionEnd.r);
    const maxR = Math.max(selectionStart.r, selectionEnd.r);

    // Clamp to grid boundaries
    const finalMinC = clamp(minC, 0, cols - 1);
    const finalMaxC = clamp(maxC, 0, cols - 1);
    const finalMinR = clamp(minR, 0, rows - 1);
    const finalMaxR = clamp(maxR, 0, rows - 1);
    
    // Only return if a valid selection exists (at least 1 cell)
    if (finalMinC > finalMaxC || finalMinR > finalMaxR) return null;

    return { 
        minC: finalMinC, 
        maxC: finalMaxC, 
        minR: finalMinR, 
        maxR: finalMaxR, 
        width: finalMaxC - finalMinC + 1, 
        height: finalMaxR - finalMinR + 1 
    };
  }, [selectionStart, selectionEnd, cols, rows]);
  // -----------------------------


  // Memoized functions
  const getDesignSnapshot = useCallback(() => ({
    layers: layers.map(L => ({
      ...L,
      cells: Array.from(L.cells)
    })),
    backstitchLines: backstitchLines,
    customPalette: customPalette,
    cols,
    rows,
    designMode,
    aidaCount,
    gridThin,
    margin,
    imageOverlay: {
      opacity: imageOverlay.opacity,
      url: imageOverlay.url,
      x: imageOverlay.x,
      y: imageOverlay.y,
      w: imageOverlay.w,
      h: imageOverlay.h,
    },
    // NEW: Include branding data and size in snapshot
    companyName: companyName,
    logoUrl: logoUrl,
    logoAlignment,
    textAlignment,
    logoMargin,
    textMargin,
    logoScale, // NEW
    textScale, // NEW
    gridColor: gridColor,
    // NEW: Include fabric color
    fabricColor: fabricColor,
    // NEW: Include symbol color
    symbolColor: symbolColor,
    allowFractionalStitches,
    fractionalCells: Array.from(fractionalCells),
  }), [
    layers,
    backstitchLines,
    customPalette,
    cols,
    rows,
    designMode,
    aidaCount,
    gridThin,
    margin,
    imageOverlay,
    companyName,
    logoUrl,
    logoAlignment,
    textAlignment,
    logoMargin,
    textMargin,
    logoScale,
    textScale,
    gridColor,
    fabricColor,
    symbolColor,
    allowFractionalStitches,
    fractionalCells,
  ]);

  // Effects
  useEffect(() => {
    const currentSnapshot = normalizeSnapshot(getDesignSnapshot());
    const savedSnapshot = normalizeSnapshot(currentInitialState.current);
    // Compare current state to the last saved/loaded state
    const isDifferent = JSON.stringify(currentSnapshot) !== JSON.stringify(savedSnapshot); 
    setHasUnsavedChanges(isDifferent);
  }, [getDesignSnapshot]);

  // NEW: Theme useEffect
  useEffect(() => {
    document.documentElement.setAttribute('data-theme', theme);
    localStorage.setItem('theme', theme);
  }, [theme]);

  useEffect(() => {
    document.documentElement.setAttribute("data-app-flavor", activeFlavorId);
  }, []);

  useEffect(() => {
    if (typeof window === "undefined") return;
    try {
      window.localStorage.setItem(
        COMMUNITY_SETTINGS_STORAGE_KEY,
        JSON.stringify(communitySettings)
      );
    } catch (error) {
      console.warn("Unable to persist community settings:", error);
    }
  }, [communitySettings]);

  useEffect(() => {
    if (typeof window === "undefined") return undefined;
    const syncFromPath = () => {
      const path = window.location.pathname;
      setShowCommunityForum(path === COMMUNITY_FORUM_PATH);
    };
    syncFromPath();
    window.addEventListener("popstate", syncFromPath);
    return () => {
      window.removeEventListener("popstate", syncFromPath);
    };
  }, []);

  useEffect(() => {
    communitySettingsRef.current = communitySettings;
  }, [communitySettings]);

  useEffect(() => {
    if (!user?.uid) return undefined;
    let cancelled = false;

    const loadCommunityProfile = async () => {
      try {
        const profileRef = doc(db, COMMUNITY_PROFILE_COLLECTION, user.uid);
        const snapshot = await getDoc(profileRef);
        if (cancelled) return;

        if (snapshot.exists()) {
          const data = snapshot.data() || {};
          const isBootstrapAdmin = BOOTSTRAP_ADMIN_UIDS.has(user.uid);
          const merged = createCommunitySettings({
            ...communitySettingsRef.current,
            ...data,
            role: data.role || (isBootstrapAdmin ? "admin" : "member"),
          });
          setCommunitySettings(merged);
          if (isBootstrapAdmin && data.role !== "admin") {
            await setDoc(profileRef, { role: "admin" }, { merge: true });
          }
          return;
        }

        const seedPayload = buildCommunityProfileSeedPayload(
          communitySettingsRef.current,
          user
        );
        await setDoc(profileRef, seedPayload, { merge: true });
      } catch (error) {
        console.error("Failed to load community profile:", error);
      }
    };

    loadCommunityProfile();
    return () => {
      cancelled = true;
    };
  }, [user?.uid]);

  useEffect(() => {
    if (!user?.uid) return undefined;
    const payload = buildCommunityProfilePayload(communitySettings, user);
    const payloadKey = JSON.stringify(payload);
    if (payloadKey === lastCommunitySyncRef.current) return undefined;

    if (communitySyncTimeoutRef.current) {
      clearTimeout(communitySyncTimeoutRef.current);
    }

    communitySyncTimeoutRef.current = setTimeout(async () => {
      try {
        const profileRef = doc(db, COMMUNITY_PROFILE_COLLECTION, user.uid);
        await setDoc(profileRef, payload, { merge: true });
        lastCommunitySyncRef.current = payloadKey;
      } catch (error) {
        console.error("Failed to sync community profile:", error);
      }
    }, 700);

    return () => {
      if (communitySyncTimeoutRef.current) {
        clearTimeout(communitySyncTimeoutRef.current);
      }
    };
  }, [communitySettings, user?.uid]);

  useEffect(() => {
    if (typeof window === "undefined") return;
    try {
      window.localStorage.setItem(
        SAVED_DESIGNS_STORAGE_KEY,
        JSON.stringify(savedDesignNames)
      );
    } catch (error) {
      console.warn("Unable to persist saved designs list:", error);
    }
  }, [savedDesignNames]);

  useEffect(() => {
    if (!user) return;
    setCommunitySettings((prev) => {
      const next = { ...prev };
      let updated = false;
      if (!prev.displayName && user.displayName) {
        next.displayName = user.displayName;
        updated = true;
      }
      if (!prev.handle && user.email) {
        next.handle = user.email.split("@")[0];
        updated = true;
      }
      return updated ? next : prev;
    });
  }, [user]);

  useEffect(() => {
    if (hasCenteredOnMount.current) return;
    const centered = getCenteredOffset(DEFAULT_ZOOM, cols, rows);
    setOffset(centered);
    hasCenteredOnMount.current = true;
  }, [getCenteredOffset, cols, rows]);

  useEffect(() => {
    if (typeof window === "undefined") return;
    const handleResize = () => {
      const compact = window.innerWidth < 1024;
      setIsCompactToolbar(compact);
      if (!compact) {
        setShowToolsMenu(false);
      }
    };
    handleResize();
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  useEffect(() => {
    if (!allowFractionalStitches && fractionalMode) {
      setFractionalMode(false);
    }
  }, [allowFractionalStitches, fractionalMode]);

  useEffect(() => {
    if (!hasSeenTutorial) {
      setShowTutorial(true);
    }
  }, [hasSeenTutorial]);


  // Core functions
  const pushHistory = () =>
    history.current.push({
      layers: deepCopyLayers(layers),
      backstitchLines: [...backstitchLines],
      fractionalCells: new Int32Array(fractionalCells),
    });

  const undo = () => {
    const prev = history.current.undo({
      layers: deepCopyLayers(layers),
      backstitchLines: [...backstitchLines],
      fractionalCells: new Int32Array(fractionalCells),
    });
    if (prev) {
      setLayers(prev.layers);
      setBackstitchLines(prev.backstitchLines);
      if (prev.fractionalCells) {
        setFractionalCells(prev.fractionalCells);
      }
    }
  };

  const redo = () => {
    const next = history.current.redo({
      layers: deepCopyLayers(layers),
      backstitchLines: [...backstitchLines],
      fractionalCells: new Int32Array(fractionalCells),
    });
    if (next) {
      setLayers(next.layers);
      setBackstitchLines(next.backstitchLines);
      if (next.fractionalCells) {
        setFractionalCells(next.fractionalCells);
      }
    }
  };

  const clientToCell = (x, y) => {
    return clientToIndex(canvasRef.current, x, y, cols, rows, cellSize, zoom, offset);
  };
  
  // --- NEW: Flood Fill Logic ---
  const floodFill = (startC, startR, targetColorIndex, newColorIndex) => {
    // 1. Guard against no-op or replacement with same color
    if (targetColorIndex === newColorIndex) return;

    // 2. Queue-based flood-fill (avoids stack overflow)
    const queue = [{ c: startC, r: startR }];
    const visited = new Set();
    const cellsToChange = [];

    setLayers(prev => {
        // Push history *before* starting the change
        pushHistory();
        const next = deepCopyLayers(prev);
        const nextCells = next[activeLayer].cells;

        while (queue.length > 0) {
            const { c, r } = queue.shift();
            const key = `${c},${r}`;
            const index = getIndex(c, r);

            // Check boundaries and visited status
            if (c < 0 || c >= cols || r < 0 || r >= rows || visited.has(key)) continue;

            visited.add(key);
            
            // Check if current cell matches the target color
            if (nextCells[index] !== targetColorIndex) continue;

            // Mark for change and queue neighbors
            cellsToChange.push(index);

            queue.push({ c: c + 1, r }); // Right
            queue.push({ c: c - 1, r }); // Left
            queue.push({ c, r: r + 1 }); // Down
            queue.push({ c, r: r - 1 }); // Up
        }

        if (cellsToChange.length > 0) {
            // Apply changes outside the loop for performance
            cellsToChange.forEach(i => {
                nextCells[i] = newColorIndex;
            });
            // Force redraw since a large area changed
            setTimeout(draw, 0); 
            return next;
        }

        // No change, undo the history push and return previous state
        history.current.undo();
        return prev;
    });
    clearFractionalValuesForIndices(cellsToChange);
};
  // ------------------------------

  const finalizeLine = (endC, endR) => {
    if (!lineStart) return;
    const { c: startC, r: startR } = lineStart;
    
    // Check if line is too short (start and end are the same) - only finalize if they differ
    if (startC === endC && startR === endR) {
        // If in tap mode, just update the preview cell and wait for next tap
        if (lineDrawMode.current === 'tap') {
            setCurrentClientCell({ c: startC, r: startR });
            return;
        }
    }
    
    // Only push history if we are finalizing a line (not on the first tap in 'tap' mode)
    if (isDrawingLine) { 
        pushHistory(); 
    }

    setBackstitchLines(prev => [...prev, {
      start: { c: startC, r: startR },
      end: { c: endC, r: endR },
      color: lineColor
    }]);

    setIsDrawingLine(false);
    setLineStart(null);
    setCurrentClientCell(null);
  };

  const handleImageImport = (e) => {
    const input = e?.target;

    if (!isPro) {
      showUpgradePrompt("Image Import / Overlay");
      if (input) {
        input.value = null;
      }
      return;
    }

    const file = input?.files?.[0];
    if (!file) return;

    const reader = new FileReader();
    reader.onload = (event) => {
      const img = new Image();
      // Store the URL temporarily for later use (either overlay or conversion)
      const imageUrl = event.target.result; 

      img.onload = () => {
        // 1. Store the loaded image object and the URL
        setPendingImageToConvert({ img, url: imageUrl });
        
        // 2. Open the modal to ask the user what they want to do
        setShowConvertOptions(true);
      };
      img.src = imageUrl;
    };
    reader.readAsDataURL(file);
    if (input) {
      input.value = null;
    }
  };

  const openImageImportDialog = () => {
    if (!isPro) {
      showUpgradePrompt("Image Import / Overlay");
      return;
    }
    const input = document.getElementById("image-import-file");
    if (input) {
      input.click();
    }
  };
  
  const applyImageAsOverlay = requirePro("Image Import / Overlay", () => {
    if (!pendingImageToConvert) return;
    
    const { img, url } = pendingImageToConvert;
    
    imageOverlayRef.current = img;
    // When a new image is loaded, reset overlay settings to cover the whole grid
    setImageOverlay((prev) => ({
      ...prev,
      img,
      url,
      x: 0,
      y: 0,
      w: cols,
      h: rows,
    }));
    // Update pending states too
    setPendingOverlayX(0);
    setPendingOverlayY(0);
    setPendingOverlayW(cols);
    setPendingOverlayH(rows);
    
    // Clean up temporary state and close modal
    setPendingImageToConvert(null);
    setShowConvertOptions(false);
    draw();
  });
  
  // --- MODIFIED: Added Pro Check (using new requirePro) ---
  const applyImageAsStitches = requirePro("Image-to-Stitches Conversion", () => {
      if (!pendingImageToConvert) return;
      if (customPalette.length === 0) {
          alert("Your custom palette is empty. Please add some colors before converting an image to stitches.");
          return;
      }
      
      pushHistory();
      const { img } = pendingImageToConvert;
      const wantsFractionalConversion = convertUseFractionalDetail;
      
      // Perform the heavy lifting conversion
      const conversionResult = convertImageToGrid(img, customPalette, cols, rows, {
        cartoonify: cartoonifyConversion,
        fractionalDetail: wantsFractionalConversion,
        fractionalIntensity: convertFractionalIntensity,
      });
      const newCellData = wantsFractionalConversion && conversionResult?.cells
        ? conversionResult.cells
        : conversionResult;
      const newFractionalData = wantsFractionalConversion && conversionResult?.fractionalCells
        ? conversionResult.fractionalCells
        : null;
      
      // Apply the new cell data to the active layer
      setLayers(prev => {
          const next = deepCopyLayers(prev);
          next[activeLayer].cells = newCellData;
          return next;
      });
      if (wantsFractionalConversion && newFractionalData) {
        setFractionalCells(new Int32Array(newFractionalData));
        if (!allowFractionalStitches) {
          setAllowFractionalStitches(true);
          setPendingAllowFractionalStitches(true);
        }
      } else if (allowFractionalStitches) {
        setFractionalCells(
          new Int32Array(cols * rows * FRACTIONAL_SLOTS).fill(-1)
        );
      }

      // Clear the overlay state since we are converting it to the grid
      imageOverlayRef.current = null;
      setImageOverlay(prev => ({ ...prev, img: null, url: null }));
      
      // Clean up temporary state and close modal
      setPendingImageToConvert(null);
      setShowConvertOptions(false);
      draw();
  });


  // --- MODIFIED FUNCTION: Analyze and Add Top Colors (Uses configurable count and brand) ---
  const analyzeAndAddColors = (
    brandToUse,
    countOverride = dominantColorCount,
    analyzeOptions = {}
  ) => {
    if (!pendingImageToConvert) return;
    
    const count = parseInt(countOverride ?? dominantColorCount, 10) || 100;
    if (count <= 0) {
        alert("Color count must be a positive number.");
        return;
    }

    const { img } = pendingImageToConvert;
    const dominantColors = extractDominantColors(
      img,
      count,
      10,
      analyzeOptions
    ); // Pass the configurable count
    
    // Find the closest commercial color for each dominant color, and add to palette
    const newPaletteColors = dominantColors.map(autoColor => {
        const targetR = parseInt(autoColor.hex.slice(1, 3), 16);
        const targetG = parseInt(autoColor.hex.slice(3, 5), 16);
        const targetB = parseInt(autoColor.hex.slice(5, 7), 16);
        
        let closestThreadColor = null;
        let minDistanceSq = Infinity;
        
        // Use the brand passed as an argument
        const paletteToSearch = brandToUse === "DMC" ? dmcPalette : anchorPalette;

        paletteToSearch.forEach(paletteColor => {
            const hex = paletteColor.hex;
            const sourceR = parseInt(hex.slice(1, 3), 16);
            const sourceG = parseInt(hex.slice(3, 5), 16);
            const sourceB = parseInt(hex.slice(5, 7), 16);

            const distanceSq = (targetR - sourceR)**2 + (targetG - sourceG)**2 + (targetB - sourceB)**2;

            if (distanceSq < minDistanceSq) {
                minDistanceSq = distanceSq;
                closestThreadColor = paletteColor;
            }
        });
        
        // Return the closest thread color, or null if no thread palette is available
        return closestThreadColor; 
    }).filter(c => c && !customPalette.find(existing => existing.code === c.code)); // Filter out duplicates

    if (newPaletteColors.length > 0) {
        // Find the first available symbol that is not currently in use
        const existingSymbols = new Set(customPalette.map(c => c.symbol));
        let nextSymbolIndex = 0;
        
        const colorsToAdd = newPaletteColors.map(color => {
            let symbol = null;
            // Find the next available symbol
            while (symbol === null) {
                const potentialSymbol = SYMBOL_LIST[nextSymbolIndex % SYMBOL_LIST.length];
                if (!existingSymbols.has(potentialSymbol)) {
                    symbol = potentialSymbol;
                    existingSymbols.add(potentialSymbol);
                }
                nextSymbolIndex++;
                if (nextSymbolIndex >= SYMBOL_LIST.length * 2 && symbol === null) {
                    // Fallback if somehow all symbols are taken/checked
                    symbol = generateId().substring(0, 2); 
                }
            }
            
            return { ...color, symbol };
        });
        
        setCustomPalette(prev => [...prev, ...colorsToAdd]);
        alert(`Successfully analyzed image and added ${colorsToAdd.length} new colors (closest match in ${brandToUse} threads) to your palette!`);
        
        // Force a redraw if modal closes and conversion starts immediately after
        draw();
    } else {
        alert("Image analyzed, but no new unique, dominant colors were found to add to your existing palette.");
    }
  };


  // NEW: Logo import handler for branding
  const handleLogoImport = requirePro("Branding", (e) => {
    const file = e.target.files[0];
    if (!file) return;

    const reader = new FileReader();
    reader.onload = (event) => {
      const img = new Image();
      img.onload = () => {
        // Validation for max size
        if (img.width > MAX_LOGO_SIZE_W || img.height > MAX_LOGO_SIZE_H) {
          alert(`Logo image size must not exceed ${MAX_LOGO_SIZE_W}x${MAX_LOGO_SIZE_H} pixels. Current size: ${img.width}x${img.height}.`);
          setLogoUrl(null);
          setLogoImage(null);
        } else {
          setLogoImage(img);
          setLogoUrl(event.target.result);
        }
        // Force redraw in case the preview is open
      };
      img.src = event.target.result;
    };
    reader.readAsDataURL(file);
    e.target.value = null;
  });

  const handleCloseConvertOptions = () => {
    setPendingImageToConvert(null);
    setShowConvertOptions(false);
  };

  const handleAnalyzeSelectedColors = (brand, countValue) => {
    setDominantColorCount(countValue);
    analyzeAndAddColors(brand, countValue, {
      cartoonify: cartoonifyConversion,
    });
  };

  const handleReplaceModalClose = () => {
    setShowReplaceModal(false);
    setFindColorIndex(null);
    setTool("draw");
  };

  const handleOptimizationClose = () => setShowOptimizeModal(false);

  const normalizePaymentProvider = (value) => {
    const normalized = (value || "").toString().trim().toLowerCase();
    if (!normalized || normalized === "google" || normalized === "google-pay" || normalized === "googlepay") {
      return "google";
    }
    if (normalized === "play" || normalized === "play-billing" || normalized === "google-play") {
      return "play";
    }
    if (normalized === "stripe") return "stripe";
    if (normalized === "auto") return "auto";
    return "google";
  };

  const getPreferredPaymentProvider = () => {
    const provider = normalizePaymentProvider(PAYMENT_PROVIDER);
    if (provider === "auto") {
      return isNativeRuntime() ? "play" : "google";
    }
    return provider;
  };

  const getUpgradeCtaLabel = () => {
    const provider = getPreferredPaymentProvider();
    if (provider === "play" && PLAY_BILLING_PRODUCT_ID) {
      return "Continue with Google Play";
    }
    if (provider === "google" && GOOGLE_PAY_MERCHANT_ID) {
      return "Continue with Google Pay";
    }
    return "Go Pro Now";
  };

  const startStripeCheckout = async () => {
    const createCheckoutSession = httpsCallable(cloudFunctions, "createCheckoutSession");
    const { data } = await createCheckoutSession();
    if (data?.url) {
      closeProModal();
      window.location.href = data.url;
      return;
    }
    throw new Error("Missing checkout URL");
  };

  const startGooglePayCheckout = async () => {
    if (!GOOGLE_PAY_MERCHANT_ID) {
      throw new Error("Google Pay merchant ID not configured.");
    }
    throw new Error("Google Pay checkout is not configured yet.");
  };

  const startPlayBillingCheckout = async () => {
    if (!PLAY_BILLING_PRODUCT_ID) {
      throw new Error("Google Play Billing product ID not configured.");
    }
    throw new Error("Google Play Billing is not configured yet.");
  };

  const handleUpgradeClick = async () => {
    if (isProcessingCheckout) return;

    try {
      setIsProcessingCheckout(true);
      const provider = getPreferredPaymentProvider();
      if (provider !== "stripe") {
        try {
          if (provider === "play") {
            await startPlayBillingCheckout();
          } else {
            await startGooglePayCheckout();
          }
          setIsProcessingCheckout(false);
          return;
        } catch (googleError) {
          console.warn("Google payment unavailable, falling back to Stripe.", googleError);
        }
      }

      await startStripeCheckout();
    } catch (error) {
      console.error("Failed to start checkout", error);
      alert("We couldn't start the checkout session. Please try again in a moment.");
      setIsProcessingCheckout(false);
    }
  };

  const handleSettingsCancel = () => setShowSettings(false);

  const handleOverlayCancel = () => setShowOverlaySettings(false);

  const handleBrandingCancel = () => {
    setPendingSymbolColor(symbolColor);
    setPendingLogoAlignment(logoAlignment);
    setPendingTextAlignment(textAlignment);
    setPendingLogoMargin(logoMargin);
    setPendingTextMargin(textMargin);
    setPendingLogoScale(logoScale);
    setPendingTextScale(textScale);
    setShowBrandingSettings(false);
    setShowBrandingPreview(false);
  };

  const registerSavedDesign = useCallback((name) => {
    const trimmed = typeof name === "string" ? name.trim() : "";
    if (!trimmed) return;
    setSavedDesignNames((prev) => {
      if (prev.includes(trimmed)) return prev;
      return [...prev, trimmed];
    });
  }, []);

  const ensureNativeFsPermission = async (options = { promptUser: true }) => {
    if (!isNativeRuntime()) return true;
    if (hasFsPermission) return true;

    try {
      const status = await Filesystem.checkPermissions();
      console.log(`${SAVE_LOG} checkPermissions =>`, status);
      if (status.publicStorage === "granted" || status.publicStorage === "limited") {
        setHasFsPermission(true);
        return true;
      }

      if (!options.promptUser) {
        return false;
      }

      const requestStatus = await Filesystem.requestPermissions({
        // Ask for broad storage scope (best effort on scoped-storage devices)
        permissions: ["read", "write"],
      });
      console.log(`${SAVE_LOG} requestPermissions =>`, requestStatus);
      const granted = requestStatus.publicStorage === "granted" || requestStatus.publicStorage === "limited";
      setHasFsPermission(granted);
      if (!granted) {
        alert("Storage permission was denied. We'll save inside the app sandbox; use Export/Share to move designs out.");
      }
      return granted;
    } catch (permissionError) {
      console.error(`${SAVE_LOG} Filesystem permission request failed:`, permissionError);
      if (options.promptUser) {
        alert("Storage permission check failed. We'll save inside the app sandbox; use Export/Share to move designs out.");
      }
      return false;
    }
  };

  // --- SAVE FUNCTIONS (browser picker with Capacitor native fallback) ---
  const performSave = async (name, fileHandle = null) => {
    const snapshot = getDesignSnapshot();
    const data = JSON.stringify(snapshot, null, 2);
    const blob = new Blob([data], { type: "application/json" });
    // On native (Capacitor WebView), skip File Picker and use Filesystem directly
    const isNativePlatform =
      typeof Capacitor !== "undefined" &&
      typeof Capacitor.isNativePlatform === "function" &&
      Capacitor.isNativePlatform();
    const canUseFilePicker =
      !isNativePlatform &&
      typeof window !== "undefined" &&
      "showSaveFilePicker" in window;
    
    const defaultName = name?.trim() || DEFAULT_DESIGN_NAME;
    let finalDesignName = defaultName;

    // Clean up filename
    const suggestedFileName = `${defaultName.replace(/\s/g, '_')}_design.json`;

    try {
      // 1. MODERN WAY: Use File System Access API (allows user to pick location)
      if (canUseFilePicker) {
        // If we don't have a fileHandle (i.e., it's a "Save As" or first time Save)
        if (!fileHandle) {
          fileHandle = await window.showSaveFilePicker({
            suggestedName: suggestedFileName,
            types: [{
              description: 'Cross-Stitch Design File',
              accept: { 'application/json': ['.json'] },
            }],
          });
          // Store the handle for subsequent quick saves
          setSavedFileHandle(fileHandle); 
        }

        if (fileHandle?.name) {
          finalDesignName = fileHandle.name.replace(/\.json$/i, "");
        }
        
        // Write the blob data to the file handle
        const writable = await fileHandle.createWritable();
        await writable.write(blob);
        await writable.close();
        console.log(`${SAVE_LOG} Saved via File Picker as ${finalDesignName}.json`);
        alert(`Design saved as ${finalDesignName}.json`);

      } else if (isNativePlatform) {
        // 2. CAPACITOR FALLBACK: Persist file through the native filesystem (Android/iOS)
        const permissionGranted = await ensureNativeFsPermission({ promptUser: false });
        const nativeRelativePath = `${NATIVE_SAVE_FOLDER}/${suggestedFileName}`;

        // Try public targets first so the JSON is visible, fallback to app-private
        const targets = permissionGranted
          ? [
              { dir: Directory.Documents, label: "Documents" },
              { dir: Directory.ExternalStorage, label: "ExternalStorage" },
              { dir: Directory.Data, label: "App Data (private)" },
            ]
          : [{ dir: Directory.Data, label: "App Data (private)" }];

        let saved = false;
        let lastError = null;

        for (const target of targets) {
          try {
            await Filesystem.writeFile({
              path: nativeRelativePath,
              data,
              directory: target.dir,
              encoding: Encoding.UTF8,
              recursive: true,
            });
            setSavedFileHandle(null);
            console.log(`${SAVE_LOG} Saved JSON to ${target.label}: ${nativeRelativePath}`);
            alert(
              target.dir === Directory.Data
                ? "Design saved in app-private storage. Use web Save/Load or Export PNG to move it out."
                : `Design saved to ${target.label}/${NATIVE_SAVE_FOLDER}/${suggestedFileName}`
            );
            saved = true;
            break;
          } catch (writeErr) {
            console.warn(`${SAVE_LOG} Save to ${target.label} failed:`, writeErr);
            lastError = writeErr;
          }
        }

        if (!saved) {
          alert(lastError?.message || "Save failed on device storage. Please check storage access and try again.");
          return;
        }

      } else {
        // 3. CLASSIC FALLBACK: Use <a> download attribute (Saves to default folder)
        const url = URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = url;
        a.download = suggestedFileName;
        a.click();
        URL.revokeObjectURL(url);
        // Note: We cannot track fileHandle in this case, so next save will be a new download
        setSavedFileHandle(null);
        console.log(`${SAVE_LOG} Saved via download fallback as ${suggestedFileName}`);
        alert(`Design downloaded as ${suggestedFileName}`);
      }

      // Finalize state update ONLY if save/download was initiated
      currentInitialState.current = snapshot; // Update the last-saved state
      setCurrentDesignName(finalDesignName); // Update the current design name
      registerSavedDesign(finalDesignName);
      setHasUnsavedChanges(false);
      setShowDesignMenu(false); // Close menu after saving
      
    } catch (error) {
      // Catch error if user cancels the dialog (AbortError) or if save fails
      if (error.name !== 'AbortError') {
        console.error(`${SAVE_LOG} Save operation failed:`, error);
        alert(error?.message || "Save failed. Please check storage permissions and try again.");
      }
      return; 
    }
  };
  
  const saveDesign = () => {
      // Use the current savedFileHandle if available, otherwise pass null (which triggers a prompt in performSave)
      performSave(currentDesignName, savedFileHandle); 
  };
  
  // New "Save As" function that asks for a new name and location
  const saveAsDesign = async () => {
    const newName = window.prompt("Enter a design name", currentDesignName) || currentDesignName;
    await performSave(newName, null);
  };
  // --- END SAVE FUNCTIONS ---
  
  const loadDesign = (file) => {
    const reader = new FileReader();
    reader.onload = (event) => {
      try {
        const loadedData = JSON.parse(event.target.result);

        // Load image overlay if exists
        let loadedImage = null;
        if (loadedData.imageOverlay?.url) {
          loadedImage = new Image();
          loadedImage.onload = () => {
            imageOverlayRef.current = loadedImage;
            draw();
          };
          loadedImage.src = loadedData.imageOverlay.url;
        }

        // NEW: Load logo image if exists
        let loadedLogo = null;
        if (loadedData.logoUrl) {
            loadedLogo = new Image();
            loadedLogo.onload = () => {
                setLogoImage(loadedLogo);
            }
            loadedLogo.src = loadedData.logoUrl;
        }

        // Apply grid settings
        const newCols = loadedData.cols || INITIAL_COLS;
        const newRows = loadedData.rows || INITIAL_ROWS;
        const newAida = loadedData.aidaCount || 14;
        const requestedDesignMode = loadedData.designMode || "aida";
        const newDesignMode =
          requestedDesignMode === "pixel" && isPro ? "pixel" : "aida";

        setCols(newCols);
        setRows(newRows);
        setAidaCount(newAida);
        setDesignMode(newDesignMode);
        setCellSize(suggestedCellSize(newAida));
        setGridThin(loadedData.gridThin || 1);
        setGridColor(loadedData.gridColor || "#d0d0d0"); // Set current grid color state
        
        // NEW: Load Fabric Color
        setFabricColor(loadedData.fabricColor || "#ffffff");
        setPendingFabricColor(loadedData.fabricColor || "#ffffff");

        // NEW: Load Symbol Color
        setSymbolColor(loadedData.symbolColor || "auto");
        setPendingSymbolColor(loadedData.symbolColor || "auto");
        setFractionalMode(false);
        const savedFractional = loadedData.fractionalCells;
        const fractionalArray = new Int32Array(
          newCols * newRows * FRACTIONAL_SLOTS
        ).fill(-1);
        if (Array.isArray(savedFractional) || savedFractional?.length) {
          const source = Array.isArray(savedFractional)
            ? savedFractional
            : Array.from(savedFractional);
          const maxCopy = Math.min(source.length, fractionalArray.length);
          for (let i = 0; i < maxCopy; i++) {
            fractionalArray[i] = source[i];
          }
        }
        setFractionalCells(fractionalArray);

        const loadedAllowFractional =
          !!loadedData.allowFractionalStitches && isPro;
        setAllowFractionalStitches(loadedAllowFractional);
        setPendingAllowFractionalStitches(loadedAllowFractional);
        
        setMargin(loadedData.margin || 3);
        setPendingCols(newCols);
        setPendingRows(newRows);
        setPendingAida(newAida);
        setPendingDesignMode(newDesignMode);
        setPendingGridThin(loadedData.gridThin || 1);
        setPendingGridColor(loadedData.gridColor || "#d0d0d0"); // Set pending grid color state (for settings modal)
        setPendingMargin(loadedData.margin || 3);
        
        // Load layers with proper cell data conversion
        let loadedLayers;
        if (loadedData.layers?.length) {
          loadedLayers = loadedData.layers.map(L => {
            const expectedSize = newCols * newRows;
            const cellDataArray = new Int32Array(expectedSize).fill(-1);

            if (Array.isArray(L.cells)) {
              // Array format
              for (let i = 0; i < Math.min(L.cells.length, expectedSize); i++) {
                const value = L.cells[i];
                if (typeof value === 'number' && value >= -1) {
                  cellDataArray[i] = value;
                }
              }
            } else if (L.cells && typeof L.cells === 'object') {
              // Object format (legacy)
              for (let key in L.cells) {
                const index = parseInt(key);
                if (!isNaN(index) && index >= 0 && index < expectedSize) {
                  const value = L.cells[key];
                  if (typeof value === 'number' && value >= -1) {
                    cellDataArray[index] = value;
                  }
                }
              }
            }

            return {
              ...L, 
              cells: cellDataArray,
              visible: L.visible ?? true,
              opacity: L.opacity || 1.0,
              id: L.id || generateId()
            };
          });
        } else {
          loadedLayers = [createEmptyLayer(generateId(), "Stitches", newCols, newRows)];
        }
        
        // Load Overlay Settings
        const initialOverlay = {
            opacity: loadedData.imageOverlay?.opacity ?? 0.5,
            url: loadedData.imageOverlay?.url ?? null,
            x: loadedData.imageOverlay?.x ?? 0,
            y: loadedData.imageOverlay?.y ?? 0,
            w: loadedData.imageOverlay?.w ?? newCols,
            h: loadedData.imageOverlay?.h ?? newRows,
            img: loadedImage,
        };
        
        // Update pending overlay states
        setPendingOverlayX(initialOverlay.x);
        setPendingOverlayY(initialOverlay.y);
        setPendingOverlayW(initialOverlay.w);
        setPendingOverlayH(initialOverlay.h);
        setPendingOverlayOpacity(initialOverlay.opacity);

        // Update all state
        setLayers(loadedLayers);
        setBackstitchLines(loadedData.backstitchLines || []);
        const seededPalette = seedPaletteWithDefaultBlack(loadedData.customPalette || []);
        setCustomPalette(seededPalette);
        setCurrentColorIndex(0);
        
        // Reset file handle on load
        setSavedFileHandle(null);

        setImageOverlay(initialOverlay);
        
        // NEW: Set branding state
        setCompanyName(loadedData.companyName || "");
        setPendingCompanyName(loadedData.companyName || "");
        setLogoUrl(loadedData.logoUrl || null);
        
        // NEW: Load Branding Position/Alignment/Size
        setLogoAlignment(loadedData.logoAlignment || "left");
        setTextAlignment(loadedData.textAlignment || "right");
        setLogoMargin(loadedData.logoMargin ?? 20);
        setTextMargin(loadedData.textMargin ?? 20);
        setLogoScale(loadedData.logoScale ?? DEFAULT_LOGO_SCALE); // NEW
        setTextScale(loadedData.textScale ?? DEFAULT_TEXT_SCALE); // NEW

        setPendingLogoAlignment(loadedData.logoAlignment || "left");
        setPendingTextAlignment(loadedData.textAlignment || "right");
        setPendingLogoMargin(loadedData.logoMargin ?? 20);
        setPendingTextMargin(loadedData.textMargin ?? 20);
        setPendingLogoScale(loadedData.logoScale ?? DEFAULT_LOGO_SCALE); // NEW
        setPendingTextScale(loadedData.textScale ?? DEFAULT_TEXT_SCALE); // NEW

        setZoom(DEFAULT_ZOOM);
        setOffset(getCenteredOffset(DEFAULT_ZOOM, newCols, newRows));
        
        // Clear history and add loaded state as the first entry
        if (history.current && typeof history.current.clear === 'function') {
          history.current.clear();
        } else {
          history.current = new HistoryStack(100);
        }

        // Push the loaded state to history so it becomes the current state
        history.current.push({
          layers: deepCopyLayers(loadedLayers),
          backstitchLines: [...(loadedData.backstitchLines || [])]
        });
          
        const fileName = file.name.replace(/_design\.json$/i, '').replace('.json', '');
        setCurrentDesignName(fileName || "Loaded Design");
        
        // Update initial design state with the loaded data
        currentInitialState.current = {
          layers: loadedLayers.map(L => ({ ...L, cells: Array.from(L.cells) })),
          backstitchLines: loadedData.backstitchLines || [],
          customPalette: seededPalette,
          cols: newCols,
          rows: newRows,
          aidaCount: newAida,
          gridThin: loadedData.gridThin || 1,
          margin: loadedData.margin || 3,
          imageOverlay: initialOverlay,
          // NEW: Include branding state, grid color, and fabric color in initial state
          companyName: loadedData.companyName || "",
          logoUrl: loadedData.logoUrl || null,
          logoAlignment: loadedData.logoAlignment || "left",
          textAlignment: loadedData.textAlignment || "right",
          logoMargin: loadedData.logoMargin ?? 20,
          textMargin: loadedData.textMargin ?? 20,
          logoScale: loadedData.logoScale ?? DEFAULT_LOGO_SCALE, // NEW
          textScale: loadedData.textScale ?? DEFAULT_TEXT_SCALE, // NEW
          gridColor: loadedData.gridColor || "#d0d0d0", // Set initial state's grid color
          fabricColor: loadedData.fabricColor || "#ffffff", // Set initial state's fabric color
          symbolColor: loadedData.symbolColor || "auto", // Set initial state's symbol color
          allowFractionalStitches: loadedAllowFractional,
          fractionalCells: Array.from(fractionalArray),
        };

        setHasUnsavedChanges(false);
        setShowSidebar(false);
        setShowMyPalette(false);
        
        // Force redraw
        setTimeout(draw, 100);
        
      } catch (e) {
        console.error("Failed to load design file:", e);
        alert(`Invalid design file format: ${e.message}`);
      }
    };
    reader.onerror = () => alert("Error reading file");
    reader.readAsText(file);
    setShowDesignMenu(false);
  };
  
  // --- NEW PROJECT LOGIC ---
  const startNewDesign = (name) => {
      // Use the initial state function to get the default grid color
      const initialState = getInitialDesignState(INITIAL_COLS, INITIAL_ROWS);
      const { layers, backstitchLines, customPalette: initialPalette, imageOverlay: initialOverlay, companyName, logoUrl, logoAlignment, textAlignment, logoMargin, textMargin, logoScale, textScale, gridColor: defaultGridColor, fabricColor: defaultFabricColor, symbolColor: defaultSymbolColor, allowFractionalStitches: initialAllowFractional, fractionalCells: initialFractionalCells } = initialState;
      const fractionalSeed = isPro ? initialAllowFractional : false;
      const seededPalette = seedPaletteWithDefaultBlack(initialPalette);

      // Reset design state
      setLayers(layers);
      setBackstitchLines(backstitchLines);
      setCols(initialState.cols);
      setRows(initialState.rows);
      setDesignMode(initialState.designMode || "aida");
      setPendingCols(initialState.cols);
      setPendingRows(initialState.rows);
      setPendingDesignMode(initialState.designMode || "aida");
      setCustomPalette(seededPalette);
      setCurrentColorIndex(0);
      setFractionalCells(initialFractionalCells);
      setFractionalMode(false);
      setAllowFractionalStitches(fractionalSeed);
      setPendingAllowFractionalStitches(fractionalSeed);
      imageOverlayRef.current = null;
      setImageOverlay(initialOverlay);
      setShowSidebar(false);
      setShowMyPalette(false);
      
      // Reset pending overlay states
      setPendingOverlayX(initialOverlay.x);
      setPendingOverlayY(initialOverlay.y);
      setPendingOverlayW(initialOverlay.w);
      setPendingOverlayH(initialOverlay.h);
      setPendingOverlayOpacity(initialOverlay.opacity);


      // NEW: Reset branding and grid color state
      setCompanyName(companyName);
      setPendingCompanyName(companyName);
      setLogoUrl(logoUrl);
      setLogoImage(null);
      
      // NEW: Reset branding position/alignment/size states
      setLogoAlignment(logoAlignment);
      setTextAlignment(textAlignment);
      setLogoMargin(logoMargin);
      setTextMargin(textMargin);
      setLogoScale(logoScale); // NEW
      setLogoScale(logoScale); // NEW
      setLogoScale(logoScale); // NEW
      setTextScale(textScale); // NEW

      setPendingLogoAlignment(logoAlignment);
      setPendingTextAlignment(textAlignment);
      setPendingLogoMargin(logoMargin);
      setPendingTextMargin(textMargin);
      setPendingLogoScale(logoScale); // NEW
      setPendingTextScale(textScale); // NEW
      
      setGridColor(defaultGridColor); // Set current grid color state
      setPendingGridColor(defaultGridColor); // Set pending grid color state
      
      // NEW: Set fabric color state
      setFabricColor(defaultFabricColor);
      setPendingFabricColor(defaultFabricColor);

      // NEW: Set symbol color state
      setSymbolColor(defaultSymbolColor);
      setPendingSymbolColor(defaultSymbolColor);
      
      // Clear saved file handle
      setSavedFileHandle(null);
      
      // Reset view
      setZoom(DEFAULT_ZOOM);
      setOffset(getCenteredOffset(DEFAULT_ZOOM, initialState.cols, initialState.rows));
      
      // Clear selection state
      setSelectionStart(null);
      setSelectionEnd(null);
      setCopiedCells(null);
      setSelectionOffset(null);
      
      // Clear find/replace state
      setShowReplaceModal(false);
      setFindColorIndex(null);


      // Clear history
      if (history.current && typeof history.current.clear === 'function') {
          history.current.clear();
      } else {
          history.current = new HistoryStack(100);
      }
      
      // Update initial state and name
      currentInitialState.current = {
        ...initialState,
        customPalette: seededPalette,
        layers: layers.map((L) => ({ ...L, cells: Array.from(L.cells) })),
        backstitchLines: [...backstitchLines],
        fractionalCells: Array.from(initialFractionalCells),
      }; 
      setCurrentDesignName(name);
      setHasUnsavedChanges(false);
      setShowDesignMenu(false);
      setShowNamePrompt(false);
  };
  
  const promptToSave = (callback) => {
    if (!hasUnsavedChanges) {
      callback();
      return;
    }
    
    if (window.confirm("You have unsaved changes. Do you want to discard them and continue?")) {
      callback();
    }
  };
  
  const handleNewDesignClick = () => {
    promptToSave(() => {
        setPendingDesignName(DEFAULT_DESIGN_NAME);
        setShowDesignMenu(false); // Close the main menu
        setShowNamePrompt(true); // Show the naming prompt
    });
  }
  
  const handleNamePromptSubmit = (e) => {
      e.preventDefault();
      const name = pendingDesignName.trim() || DEFAULT_DESIGN_NAME;
      startNewDesign(name);
  }
  // --- END NEW PROJECT LOGIC ---
  
  // --- IMAGE OVERLAY SETTINGS LOGIC ---
  const handleOpenOverlaySettings = () => {
    // Reset pending states on open
    setPendingOverlayX(imageOverlay.x);
    setPendingOverlayY(imageOverlay.y);
    setPendingOverlayW(imageOverlay.w);
    setPendingOverlayH(imageOverlay.h);
    setPendingOverlayOpacity(imageOverlay.opacity);
    setShowOverlaySettings(true);
  }

  const applyOverlaySettings = () => {
    pushHistory(); // Treat any overlay change as a historical action for undo
    const newX = parseInt(pendingOverlayX) || 0;
    const newY = parseInt(pendingOverlayY) || 0;
    const newW = parseInt(pendingOverlayW) || cols;
    const newH = parseInt(pendingOverlayH) || rows;
    const newO = parseFloat(pendingOverlayOpacity) || 0.5;

    setImageOverlay(prev => ({
        ...prev,
        x: newX,
        y: newY,
        w: Math.max(1, newW),
        h: Math.max(1, newH),
        opacity: clamp(newO, 0.05, 1)
    }));
    setShowOverlaySettings(false);
  }
  // --- END IMAGE OVERLAY SETTINGS LOGIC ---

  const handleFractionalSettingToggle = (value) => {
    if (!isPro) {
      showUpgradePrompt("Fractional stitches");
      return;
    }
    setPendingAllowFractionalStitches(value);
  };

  const handleFractionalIntensityChange = useCallback((value) => {
    const maxIndex = FRACTIONAL_INTENSITY_LEVELS.length - 1;
    const clampedValue = Math.min(
      Math.max(parseInt(value ?? 0, 10) || 0, 0),
      maxIndex
    );
    setConvertFractionalIntensity(clampedValue);
  }, []);

  const openTutorial = () => {
    setShowTutorial(true);
  };

  const closeTutorial = () => {
    setShowTutorial(false);
    if (!hasSeenTutorial) {
      setHasSeenTutorial(true);
      if (typeof window !== "undefined") {
        localStorage.setItem("csd_seen_tutorial", "1");
      }
    }
  };

  const renderFractionalStitches = useCallback(
    (
      ctx,
      cellSizePx,
      originX,
      originY,
      startCol = 0,
      endCol = cols - 1,
      startRow = 0,
      endRow = rows - 1
    ) => {
      if (!allowFractionalStitches || !fractionalCells?.length || cellSizePx <= 0)
        return;

      const drawPolygon = (baseX, baseY, points, color) => {
        ctx.beginPath();
        ctx.moveTo(
          baseX + points[0][0] * cellSizePx,
          baseY + points[0][1] * cellSizePx
        );
        for (let i = 1; i < points.length; i++) {
          ctx.lineTo(
            baseX + points[i][0] * cellSizePx,
            baseY + points[i][1] * cellSizePx
          );
        }
        ctx.closePath();
        ctx.fillStyle = color;
        ctx.fill();
      };

      for (let r = startRow; r <= endRow; r++) {
        if (r < 0 || r >= rows) continue;
        for (let c = startCol; c <= endCol; c++) {
          if (c < 0 || c >= cols) continue;
          const cellIndex = r * cols + c;
          const base = cellIndex * FRACTIONAL_SLOTS;
          const baseX = originX + c * cellSizePx;
          const baseY = originY + r * cellSizePx;

          const colorMasks = new Map();
          for (let q = 0; q < FRACTIONAL_SLOTS; q++) {
            const colorIdx = fractionalCells[base + q];
            if (colorIdx < 0 || !customPalette[colorIdx]) continue;
            const prevMask = colorMasks.get(colorIdx) ?? 0;
            colorMasks.set(colorIdx, prevMask | (1 << q));
          }

          if (!colorMasks.size) continue;

          for (const [colorIdx, maskValue] of colorMasks.entries()) {
            const fillColor = customPalette[colorIdx]?.hex;
            if (!fillColor) continue;
            let mask = maskValue;

            // Draw diagonal half-stitch triangles first so they fill the expected area
            for (const half of FRACTIONAL_HALF_TRIANGLES) {
              if ((mask & half.mask) === half.mask) {
                drawPolygon(baseX, baseY, half.points, fillColor);
                mask &= ~half.mask;
              }
            }

            // Remaining single quadrants become quarter triangles.
            for (let q = 0; q < FRACTIONAL_SLOTS; q++) {
              const bit = 1 << q;
              if ((mask & bit) === 0) continue;
              drawPolygon(baseX, baseY, FRACTIONAL_TRIANGLES[q], fillColor);
              mask &= ~bit;
            }
          }
        }
      }
    },
    [allowFractionalStitches, fractionalCells, customPalette, cols, rows]
  );

  const draw = useCallback(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    
    const ctx = canvas.getContext("2d");
    resizeCanvasToDisplaySize(canvas);

    const cssW = canvas.clientWidth;
    const cssH = canvas.clientHeight;
    
    // Clear background of entire canvas wrapper area
    ctx.fillStyle = "#fafafa"; // Background outside the design area (padding)
    // NEW: Use CSS variable for better theme support (fallback to #fafafa)
    const computedStyle = getComputedStyle(document.documentElement);
    const mainBg = computedStyle.getPropertyValue('--bg').trim() || '#fafafa';
    ctx.fillStyle = mainBg; 
    ctx.fillRect(0, 0, cssW, cssH);

    const cs = cellSize * zoom;
    const ox = offset.x;
    const oy = offset.y;

    // Calculate visible area
    const startCol = Math.floor(Math.max(0, -ox / cs));
    const endCol = Math.min(cols - 1, Math.ceil((cssW - ox) / cs));
    const startRow = Math.floor(Math.max(0, -oy / cs));
    const endRow = Math.min(rows - 1, Math.ceil((cssH - oy) / cs));

    // NEW: Draw fabric background for the design area (the grid itself)
    ctx.globalAlpha = 1.0;
    ctx.fillStyle = fabricColor; // Use the new state for the fabric color
    ctx.fillRect(ox, oy, cols * cs, rows * cs);

    // --- FIX: Draw image overlay AFTER fabric background to ensure visibility ---
    if (imageOverlay.img && imageOverlayRef.current) {
      ctx.globalAlpha = imageOverlay.opacity;
      // Draw using imageOverlay's stored position and dimensions (in cell units)
      ctx.drawImage(
        imageOverlayRef.current,
        ox + imageOverlay.x * cs, 
        oy + imageOverlay.y * cs, 
        imageOverlay.w * cs, 
        imageOverlay.h * cs
      );
    }
    ctx.globalAlpha = 1.0;
    // -------------------------------------------------------------------------


    // Draw cells
    for (const L of layers) {
      if (!L.visible) continue;
      ctx.globalAlpha = L.opacity;
      for (let r = startRow; r <= endRow; r++) {
        for (let c = startCol; c <= endCol; c++) {
          const i = r * cols + c;
          const colorIdx = L.cells[i];
          if (colorIdx >= 0 && customPalette[colorIdx]) {
            ctx.fillStyle = customPalette[colorIdx].hex;
            ctx.fillRect(ox + c * cs, oy + r * cs, cs, cs);
          }
          
          // NEW: Placeholder for Fractional Stitch Rendering (Visual only, no data structure implemented yet)
          // if (L.id === 'fractional') { /* Draw fractional parts */ }
          // if (L.id === 'specialty') { /* Draw beads/knots */ }

        }
      }
    }

    renderFractionalStitches(
      ctx,
      cs,
      ox,
      oy,
      startCol,
      endCol,
      startRow,
      endRow
    );

    // Draw backstitch lines
    ctx.globalAlpha = 1.0;
    ctx.lineWidth = 2;
    ctx.setLineDash([]);

    for (const line of backstitchLines) {
      const { c: startC, r: startR } = line.start;
      const { c: endC, r: endR } = line.end;

      if ((startC >= startCol && startC <= endCol && startR >= startRow && startR <= endRow) ||
          (endC >= startCol && endC <= endCol && endR >= startRow && endR <= endRow)) {
        ctx.strokeStyle = line.color;
        ctx.beginPath();
        ctx.moveTo(ox + startC * cs + cs / 2, oy + startR * cs + cs / 2);
        ctx.lineTo(ox + endC * cs + cs / 2, oy + endR * cs + cs / 2);
        ctx.stroke();
      }
    }

    // Draw temporary line
    if (isDrawingLine && lineStart && currentClientCell) {
      const { c: startC, r: startR } = lineStart;
      const { c: endC, r: endR } = currentClientCell;

      ctx.strokeStyle = lineColor;
      ctx.lineWidth = 2;
      ctx.beginPath();
      ctx.moveTo(ox + startC * cs + cs / 2, oy + startR * cs + cs / 2);
      ctx.lineTo(ox + endC * cs + cs / 2, oy + endR * cs + cs / 2);
      ctx.stroke();
    }

    // Draw symbols
    if (showSymbols) {
      ctx.font = `${Math.floor(cellSize * zoom * 0.6)}px monospace`;
      ctx.textAlign = "center";
      ctx.textBaseline = "middle";
      
      for (const L of layers) {
        if (!L.visible) continue;
        for (let r = startRow; r < endRow + 1; r++) {
          for (let c = startCol; c < endCol + 1; c++) {
            const i = r * cols + c;
            const colorIdx = L.cells[i];
            if (colorIdx >= 0 && customPalette[colorIdx]?.symbol) {
              
              // Determine symbol color: use fixed color if set, otherwise use auto-contrast
              const fillColor = symbolColor !== "auto" 
                ? symbolColor 
                : getSymbolColor(customPalette[colorIdx].hex);

              ctx.fillStyle = fillColor;
              
              ctx.fillText(
                customPalette[colorIdx].symbol,
                ox + c * cs + cs / 2,
                oy + r * cs + cs / 2
              );
            }
          }
        }
      }
    }

    // Draw grid
    ctx.globalAlpha = 1;
    ctx.lineWidth = gridThin;
    ctx.strokeStyle = gridColor; // NEW: Use custom grid color
    ctx.beginPath();
    
    // Vertical lines
    for (let c = startCol; c <= endCol + 1; c++) {
      const x = ox + c * cs + 0.5;
      ctx.moveTo(x, oy + startRow * cs);
      ctx.lineTo(x, oy + (endRow + 1) * cs);
    }
    // Horizontal lines
    for (let r = startRow; r <= endRow + 1; r++) {
      const y = oy + r * cs + 0.5;
      ctx.moveTo(ox + startCol * cs, y);
      ctx.lineTo(ox + (endCol + 1) * cs, y);
    }
    ctx.stroke();

    if (tool === "line" && currentClientCell) {
      const { c, r } = currentClientCell;
      if (c >= 0 && c < cols && r >= 0 && r < rows) {
        ctx.save();
        ctx.globalAlpha = 0.2;
        ctx.fillStyle = lineColor;
        ctx.fillRect(ox + c * cs, oy + r * cs, cs, cs);
        ctx.globalAlpha = 0.9;
        ctx.lineWidth = Math.max(1, gridThin);
        ctx.strokeStyle = lineColor;
        ctx.strokeRect(ox + c * cs, oy + r * cs, cs, cs);
        ctx.restore();
      }
    }

    // Draw center lines
    if (showCenterLines) {
      const centerCol = Math.floor(cols / 2);
      const centerRow = Math.floor(rows / 2);

      const isCenterColVisible = centerCol > startCol && centerCol < endCol + 1;
      const isCenterRowVisible = centerRow > startRow && centerRow < endRow + 1;

      if (isCenterColVisible || isCenterRowVisible) {
        ctx.lineWidth = Math.max(gridThin + 0.5, gridThin * 2);
        ctx.strokeStyle = "#FF0000";
        ctx.setLineDash([5, 5]);

        ctx.beginPath();
        if (isCenterColVisible) {
          const x = ox + centerCol * cs + 0.5;
          ctx.moveTo(x, oy + startRow * cs);
          ctx.lineTo(x, oy + (endRow + 1) * cs);
        }
        if (isCenterRowVisible) {
          const y = oy + centerRow * cs + 0.5;
          ctx.moveTo(ox + startCol * cs, y);
          ctx.lineTo(ox + (endCol + 1) * cs, y);
        }
        ctx.stroke();
        ctx.setLineDash([]);
      }
    }

    // Draw major grid lines
    ctx.lineWidth = Math.max(gridThin + 0.5, gridThin * 1.5);
    ctx.strokeStyle = gridColor; // NEW: Use custom grid color
    ctx.beginPath();
    
    for (let c = Math.ceil(startCol / MAJOR_GRID_EVERY) * MAJOR_GRID_EVERY; c <= endCol; c += MAJOR_GRID_EVERY) {
      const x = ox + c * cs + 0.5;
      ctx.moveTo(x, oy + startRow * cs);
      ctx.lineTo(x, oy + (endRow + 1) * cs);
    }
    for (let r = Math.ceil(startRow / MAJOR_GRID_EVERY) * MAJOR_GRID_EVERY; r <= endRow; r += MAJOR_GRID_EVERY) {
      const y = oy + r * cs + 0.5;
      ctx.moveTo(ox + startCol * cs, y);
      ctx.lineTo(ox + (endCol + 1) * cs, y);
    }
    ctx.stroke();

    // --- NEW: Draw selection box and moving preview ---
    const normalizedSelection = getNormalizedSelection();

    // 1. Draw Selection Outline
    if (tool === 'select' && normalizedSelection && !copiedCells) {
        ctx.globalAlpha = 1.0;
        ctx.lineWidth = 3;
        ctx.strokeStyle = "rgb(66, 153, 225)"; // Distinct blue
        ctx.setLineDash([5, 5]);

        const { minC, maxC, minR, maxR } = normalizedSelection;
        const selectX = ox + minC * cs;
        const selectY = oy + minR * cs;
        const selectW = (maxC - minC + 1) * cs;
        const selectH = (maxR - minR + 1) * cs;

        ctx.strokeRect(selectX, selectY, selectW, selectH);
        ctx.setLineDash([]);
    }

    // 2. Draw Moving Preview (if a selection is active and being moved)
    if (selectionOffset && copiedCells) {
        const { c: dx, r: dy } = selectionOffset;
        const { width, height } = copiedCells;
        
        // Draw cells from copiedCells at the new offset
        for (let r = 0; r < height; r++) {
            for (let c = 0; c < width; c++) {
                const cellIndex = r * width + c;
                const colorIdx = copiedCells.cells[cellIndex];
                
                if (colorIdx >= 0 && customPalette[colorIdx]) {
                    const drawC = c + dx;
                    const drawR = r + dy;
                    
                    // Only draw if within the main grid boundaries
                    if (drawC >= 0 && drawC < cols && drawR >= 0 && drawR < rows) {
                        ctx.globalAlpha = 0.7; // Draw with slight transparency for preview
                        ctx.fillStyle = customPalette[colorIdx].hex;
                        ctx.fillRect(ox + drawC * cs, oy + drawR * cs, cs, cs);
                    }
                }
            }
        }
        ctx.globalAlpha = 1.0;
    }
    // ----------------------------------------------------

  }, [
    layers,
    cols,
    rows,
    zoom,
    offset,
    gridThin,
    customPalette,
    cellSize,
    showSymbols,
    isDrawingLine,
    lineStart,
    currentClientCell,
    lineColor,
    showCenterLines,
    backstitchLines,
    imageOverlay,
    gridColor,
    fabricColor,
    symbolColor,
    selectionOffset,
    copiedCells,
    getNormalizedSelection,
    tool,
    renderFractionalStitches,
  ]); // Added new selection dependencies

  useEffect(() => {
    draw();
    const handleResize = () => draw();
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, [draw]);

  const paintAtClient = (x, y, colorIdx) => {
    const info = getCellInfoFromClient(x, y);
    if (!info) return;
    const { cellIndex, localX, localY } = info;

    if (allowFractionalStitches && fractionalMode) {
      const quadrant = quadrantFromLocalOffsets(localX, localY);
      if (colorIdx >= 0) {
        setLayers((prev) => {
          const currentValue = prev[activeLayer].cells[cellIndex];
          if (currentValue === -1) return prev;
          const next = deepCopyLayers(prev);
          next[activeLayer].cells[cellIndex] = -1;
          return next;
        });
      }
      setFractionalQuadrantValue(cellIndex, quadrant, colorIdx);
      return;
    }

    setLayers((prev) => {
      const next = deepCopyLayers(prev);
      next[activeLayer].cells[cellIndex] = colorIdx;
      return next;
    });

    if (allowFractionalStitches) {
      clearFractionalValuesForIndices([cellIndex]);
    }
  };

  // --- NEW: Cut/Copy/Paste/Move/Flip Functions ---
  
  const extractCells = (minC, maxC, minR, maxR, width, height) => {
      const layerCells = layers[activeLayer].cells;
      const newCopiedCells = new Int32Array(width * height);
      
      for (let r = minR; r <= maxR; r++) {
          for (let c = minC; c <= maxC; c++) {
              const oldIndex = r * cols + c;
              const newIndex = (r - minR) * width + (c - minC);
              newCopiedCells[newIndex] = layerCells[oldIndex];
          }
      }
      return { cells: newCopiedCells, width, height };
  };

  // Helper to copy the cells (original remains)
  const copySelectedCells = () => {
      const sel = getNormalizedSelection();
      if (!sel || !layers[activeLayer]) {
          alert("Please select an area on the canvas first.");
          return;
      }
      
      const { minC, minR, width, height } = sel;
      const copied = extractCells(minC, sel.maxC, minR, sel.maxR, width, height);

      setCopiedCells(copied);
      // Set initial offset to be right next to the original selection
      setSelectionOffset({ c: minC + width, r: minR }); 
      
      // Clear the selection box visual and switch to "move" mode
      setSelectionStart(null);
      setSelectionEnd(null);
      setLastPan(null);
      draw();
  };

  // Helper to cut out the cells (original is erased)
  const cutSelectedCells = () => {
      const sel = getNormalizedSelection();
      if (!sel || !layers[activeLayer]) {
          alert("Please select an area on the canvas first.");
          return;
      }

      pushHistory();
      const { minC, maxC, minR, maxR, width, height } = sel;
      const clearedIndices = [];
      
      // 1. Copy (Cut) the data
      const copied = extractCells(minC, maxC, minR, maxR, width, height);
      setCopiedCells(copied);
      // The initial offset is where the selection currently is (Top-Left corner)
      setSelectionOffset({ c: minC, r: minR }); 

      // 2. Erase the original area (by setting to -1, which is background/empty)
      setLayers((prev) => {
          const next = deepCopyLayers(prev);
          const nextCells = next[activeLayer].cells;
          for (let r = minR; r <= maxR; r++) {
              for (let c = minC; c <= maxC; c++) {
                  nextCells[r * cols + c] = -1;
                  clearedIndices.push(r * cols + c);
              }
          }
          return next;
      });

      // 3. Clear the selection box visual and switch to "move" mode
      setSelectionStart(null);
      setSelectionEnd(null);
      setLastPan(null); // Clear last pan so next move is relative
      draw();
      clearFractionalValuesForIndices(clearedIndices);
  };

  // NEW: Flip functions operate directly on copiedCells
  const flipSelectedCellsHorizontal = () => {
      if (!copiedCells) return;

      const { cells: oldCells, width, height } = copiedCells;
      const newCells = new Int32Array(width * height);

      for (let r = 0; r < height; r++) {
          for (let c = 0; c < width; c++) {
              const oldIndex = r * width + c;
              const newIndex = r * width + (width - 1 - c);
              newCells[newIndex] = oldCells[oldIndex];
          }
      }
      setCopiedCells({ cells: newCells, width, height });
      draw(); // Force redraw the preview
  };

  const flipSelectedCellsVertical = () => {
      if (!copiedCells) return;

      const { cells: oldCells, width, height } = copiedCells;
      const newCells = new Int32Array(width * height);

      for (let r = 0; r < height; r++) {
          for (let c = 0; c < width; c++) {
              const oldIndex = r * width + c;
              const newIndex = (height - 1 - r) * width + c;
              newCells[newIndex] = oldCells[oldIndex];
          }
      }
      setCopiedCells({ cells: newCells, width, height });
      draw(); // Force redraw the preview
  };
  
  // Helper to paste/finalize the move
  const pasteSelectedCells = () => {
      if (!copiedCells || !selectionOffset || !layers[activeLayer]) return;
      
      pushHistory();
      const { cells: pasteCells, width, height } = copiedCells;
      const { c: startC, r: startR } = selectionOffset;
      const overwrittenIndices = [];

      setLayers((prev) => {
          const next = deepCopyLayers(prev);
          const nextCells = next[activeLayer].cells;

          for (let r = 0; r < height; r++) {
              for (let c = 0; c < width; c++) {
                  const drawC = c + startC;
                  const drawR = r + startR;
                  
                  // Only paste if within the main grid boundaries
                  if (drawC >= 0 && drawC < cols && drawR >= 0 && drawR < rows) {
                      const pasteIndex = r * width + c;
                      const finalIndex = drawR * cols + drawC;
                      
                      nextCells[finalIndex] = pasteCells[pasteIndex];
                      overwrittenIndices.push(finalIndex);
                  }
              }
          }
          return next;
      });

      // Clear the copied data/move state
      setCopiedCells(null);
      setSelectionOffset(null);
      setSelectionStart(null);
      setSelectionEnd(null);
      setTool('draw'); // Switch back to draw mode after placing
      clearFractionalValuesForIndices(overwrittenIndices);
  };

  const cancelSelection = () => {
    // We only need to clear the selection state
    setSelectionStart(null);
    setSelectionEnd(null);
    setCopiedCells(null);
    setSelectionOffset(null);
    setLastPan(null);
    setIsSelecting(false);
    // Switch to draw mode unless we're already drawing a line or using find/replace
    if (tool !== 'line' && tool !== 'replace' && tool !== 'fill') setTool('draw');
  };
  // -------------------------------------
  
// --- NEW FUNCTION: Core Find and Replace Logic (using new requirePro) ---
const findAndReplace = requirePro("Find and Replace Tool", (newColorIndex) => {
    
    if (findColorIndex === null || newColorIndex === null || findColorIndex === newColorIndex) {
        setShowReplaceModal(false);
        setFindColorIndex(null);
        return;
    }

    pushHistory();

    const changedIndices = [];
    setLayers((prev) => {
        const next = deepCopyLayers(prev);
        const findValue = findColorIndex;
        const replaceValue = newColorIndex;

        // Iterate through all layers and cells
        for (const layer of next) {
            const nextCells = layer.cells;
            for (let i = 0; i < layer.cells.length; i++) {
                if (nextCells[i] === findValue) {
                    nextCells[i] = replaceValue;
                    changedIndices.push(i);
                }
            }
        }
        
        // This return statement must be here, inside the setLayers callback:
        return next;
    });

    // Clean up state
    setShowReplaceModal(false);
    setFindColorIndex(null);
    setTool('draw'); // Switch back to draw mode
    draw();
    clearFractionalValuesForIndices(changedIndices);
});
  // --------------------------------------------------

  // --- NEW FUNCTION: Core Palette Optimization Logic (using new requirePro) ---
  const analyzeAndOptimizePalette = requirePro("Palette Optimization", (threshold = 50) => {
      pushHistory(); // Start history for this major change

      const usageCounts = new Map();
      const allCells = layers.flatMap(l => Array.from(l.cells));

      // 1. Calculate usage counts
      for (const index of allCells) {
          if (index >= 0) {
              usageCounts.set(index, (usageCounts.get(index) || 0) + 1);
          }
      }

      const colorsToReplace = [];
      const colorsToKeep = [];

      // 2. Identify colors to replace and keep
      for (let i = 0; i < customPalette.length; i++) {
          const count = usageCounts.get(i) || 0;
          if (count <= threshold) {
              colorsToReplace.push({ index: i, count, ...customPalette[i] });
          } else {
              colorsToKeep.push({ index: i, count, ...customPalette[i] });
          }
      }
      
      if (colorsToReplace.length === 0) {
          alert("No colors found with usage count below the threshold. Optimization canceled.");
          history.current.undo();
          return;
      }
      
      const replacementMap = new Map(); // Maps old index to new index

      // 3. Find the best replacement for each low-usage color
      colorsToReplace.forEach(lowColor => {
          const targetR = parseInt(lowColor.hex.slice(1, 3), 16);
          const targetG = parseInt(lowColor.hex.slice(3, 5), 16);
          const targetB = parseInt(lowColor.hex.slice(5, 7), 16);

          let bestMatchIndex = -1;
          let minDistanceSq = Infinity;

          colorsToKeep.forEach(highColor => {
              const hex = highColor.hex;
              const sourceR = parseInt(hex.slice(1, 3), 16);
              const sourceG = parseInt(hex.slice(3, 5), 16);
              const sourceB = parseInt(hex.slice(5, 7), 16);

              const distanceSq = (targetR - sourceR)**2 + (targetG - sourceG)**2 + (targetB - sourceB)**2;

              if (distanceSq < minDistanceSq) {
                  minDistanceSq = distanceSq;
                  bestMatchIndex = highColor.index;
              }
          });
          
          if (bestMatchIndex !== -1) {
              replacementMap.set(lowColor.index, bestMatchIndex);
          }
      });
      
      // --- REVISED SIMPLIFIED INDEX REMAPPING ---
      const indicesToRemove = new Set(colorsToReplace.map(c => c.index));
      let adjustmentMap = new Map();
      let shift = 0;
      
      const newCustomPalette = customPalette.filter((c, i) => {
          if (indicesToRemove.has(i)) {
              shift++;
              return false;
          } else {
              adjustmentMap.set(i, i - shift);
              return true;
          }
      });
      
      setLayers(prev => {
          const nextLayers = deepCopyLayers(prev);
          nextLayers.forEach(layer => {
              for (let i = 0; i < layer.cells.length; i++) {
                  const oldIndex = layer.cells[i];
                  if (oldIndex >= 0) {
                      // Apply replacement first (if the color was a removed color, map it to a kept color)
                      const effectiveOldIndex = replacementMap.has(oldIndex) ? replacementMap.get(oldIndex) : oldIndex;
                      
                      // Then apply the positional adjustment (kept color's old index -> new index in finalPalette)
                      if (adjustmentMap.has(effectiveOldIndex)) {
                          layer.cells[i] = adjustmentMap.get(effectiveOldIndex);
                      } else {
                          // This case should ideally not be hit with the current logic flow
                      }
                  }
              }
          });
          return nextLayers;
      });
      // ------------------------------------------

      setCustomPalette(newCustomPalette);
      setCurrentColorIndex(c => adjustmentMap.has(c) ? adjustmentMap.get(c) : 0); // Reset or remap current color
      
      alert(`Optimization complete! Removed ${colorsToReplace.length} low-usage colors (Count <= ${threshold}) and remapped them to the closest remaining colors.`);
      setShowOptimizeModal(false);
      draw();
  });
  // ------------------------------------------------------------------------------------
  
  // --- NEW FUNCTION: Core Palette Conversion Logic (e.g., DMC to Anchor) (using new requirePro) ---
  const convertPaletteBrand = requirePro("Palette Conversion", (targetBrand) => {
      const paletteToSearch = targetBrand === "DMC" ? dmcPalette : anchorPalette;
      
      if (paletteToSearch.length === 0) {
          alert(`The ${targetBrand} palette data is missing. Conversion failed.`);
          return;
      }
      
      pushHistory();
      
      setCustomPalette(prevPalette => {
          return prevPalette.map(oldColor => {
              const targetR = parseInt(oldColor.hex.slice(1, 3), 16);
              const targetG = parseInt(oldColor.hex.slice(3, 5), 16);
              const targetB = parseInt(oldColor.hex.slice(5, 7), 16);

              let closestThreadColor = null;
              let minDistanceSq = Infinity;

              paletteToSearch.forEach(paletteColor => {
                  const hex = paletteColor.hex;
                  const sourceR = parseInt(hex.slice(1, 3), 16);
                  const sourceG = parseInt(hex.slice(3, 5), 16);
                  const sourceB = parseInt(hex.slice(5, 7), 16);

                  const distanceSq = (targetR - sourceR)**2 + (targetG - sourceG)**2 + (targetB - sourceB)**2;

                  if (distanceSq < minDistanceSq) {
                      minDistanceSq = distanceSq;
                      closestThreadColor = paletteColor;
                  }
              });
              
              if (closestThreadColor && closestThreadColor.code !== oldColor.code) {
                  return {
                      ...closestThreadColor,
                      symbol: oldColor.symbol // Keep the same symbol
                  };
              } else {
                  return oldColor; // If no match found, or match is the same color (e.g. converting DMC to DMC), keep original.
              }
          });
      });
      
      alert(`Successfully converted My Palette colors to the closest match in the ${targetBrand} brand!`);
      setShowOptimizeModal(false);
      setPaletteBrand(targetBrand); // Update the search palette as well
      draw();
  });
  // ------------------------------------------------------------------------------------


  // Event handlers
  const onPointerDown = (e) => {
    // Determine the tool being used and the pointer type
    if (e.pointerType === "touch" || e.button !== 0) return;
    if (tool === "pan") return;
    
    const cell = clientToCell(e.clientX, e.clientY);
    if (cell == null) return;
    const c = cell % cols;
    const r = Math.floor(cell / cols);

    // --- NEW: FIND/REPLACE LOGIC (With Pro Check) ---
    if (tool === "replace") {
        if (!isPro) {
            showUpgradePrompt("Find and Replace Tool");
            setTool('draw');
            return;
        }
        const colorIdx = layers[activeLayer].cells[getIndex(c, r)];
        
        if (colorIdx >= 0) {
            // Found a color, set state and open modal
            setFindColorIndex(colorIdx);
            setShowReplaceModal(true);
        } else {
            alert("No color found in this cell on the active layer to replace.");
            setTool('draw'); // Fallback to draw if clicked on empty cell
        }
        return;
    }
    // -----------------------------
    
    // --- NEW: FILL TOOL LOGIC ---
    if (tool === "fill") {
        const targetColorIdx = layers[activeLayer].cells[getIndex(c, r)];
        
        if (targetColorIdx === currentColorIndex) {
            alert("This cell already has the selected color. Click on a different colored or empty area to fill it.");
            return;
        }
        
        // Target index for empty cells is -1
        floodFill(c, r, targetColorIdx, currentColorIndex);
        return;
    }
    // ----------------------------

    if (tool === "select") {
        // --- SELECTION LOGIC (for mouse/pen) ---
        
        // 1. Check for 'Grab and Move' mode
        if (copiedCells) {
             const { c: offsetC, r: offsetR } = selectionOffset;
             const { width, height } = copiedCells;
            
             // Check if click is inside the moving block
             if (c >= offsetC && c < offsetC + width && r >= offsetR && r < offsetR + height) {
                setLastPan({ x: e.clientX, y: e.clientY }); // Start dragging
                setIsSelecting(false); // Make sure selection isn't active
                return;
             }
        }
        
        // 2. Start a new marquee selection
        setSelectionStart({ c, r });
        setSelectionEnd({ c, r });
        setIsSelecting(true);
        // Clear previous move/cut/copy state
        setCopiedCells(null); 
        setSelectionOffset(null); 
        return;
        // ---------------------------------------
    }

    if (tool === "line") {
        // Line tool: Set to drag mode on down, or start line in tap mode
        if (isDrawingLine) {
            // End the line if we're already drawing in tap mode (double click/tap)
            finalizeLine(c, r);
            lineDrawMode.current = 'tap'; // Reset mode for clarity
        } else {
            // Start a new line in 'drag' mode (mouse/pen click and hold)
            lineDrawMode.current = 'drag';
            setIsDrawingLine(true);
            setLineStart({ c, r });
            setCurrentClientCell({ c, r });
            draw(); // FIXED: Force redraw on line start in drag mode for immediate preview
        }
        return;
    }

    pushHistory();
    paintAtClient(e.clientX, e.clientY, tool === "draw" ? currentColorIndex : -1);
  };

  const onPointerUp = (e) => {
    if (e.pointerType === "touch" || e.button !== 0) return;
    
    // --- SELECTION/MOVE LOGIC (Finalize move/selection) ---
    if (tool === "select") {
        if (isSelecting) {
            // End marquee select: The selection is now defined. Do NOT clear it.
            setIsSelecting(false);
            // If the selection is just one cell (a click), clear the selection
            const sel = getNormalizedSelection();
            if (sel && sel.width === 1 && sel.height === 1) {
                setSelectionStart(null);
                setSelectionEnd(null);
            }
        } else if (selectionOffset && lastPan) {
            // End Move/Drag: The block is placed where it ended. Clear lastPan only.
            setLastPan(null); 
            draw();
        }
        return;
    }
    // ----------------------------------------------------

    if (tool !== "line" || !isDrawingLine || lineDrawMode.current !== 'drag') return;
    
    const cell = clientToCell(e.clientX, e.clientY);
    if (cell != null) {
      const c = cell % cols;
      const r = Math.floor(cell / cols);
      finalizeLine(c, r);
    } else {
      // If pointer is released outside, cancel the drag line
      setIsDrawingLine(false);
      setLineStart(null);
      setCurrentClientCell(null);
    }
    lineDrawMode.current = 'tap'; // Reset for next interaction
  };

  const onPointerMove = (e) => {
    if (e.pointerType === "touch") return;
    
    const cell = clientToCell(e.clientX, e.clientY);
    const c = cell != null ? cell % cols : null;
    const r = cell != null ? Math.floor(cell / cols) : null;
    
    // --- SELECTION/MOVE LOGIC ---
    if (tool === "select" && e.buttons === 1) {
        if (isSelecting && cell != null) {
            // 1. Drawing the selection box
            setSelectionEnd({ c, r });
            draw();
        } else if (selectionOffset && lastPan && cell != null) {
            // 2. Moving the selected cells
            
            // Calculate movement in terms of grid cells since last event
            const dx = Math.round((e.clientX - lastPan.x) / (cellSize * zoom));
            const dy = Math.round((e.clientY - lastPan.y) / (cellSize * zoom));
            
            if (dx !== 0 || dy !== 0) {
                // Update offset if movement exceeds one cell
                setSelectionOffset(prev => ({ c: prev.c + dx, r: prev.r + dy }));
                // Update lastPan for continuous tracking
                setLastPan({ x: e.clientX, y: e.clientY });
                draw();
            }
        }
        return;
    }
    // ----------------------------

    if (tool === "line") {
        if (cell != null) {
            setCurrentClientCell({ c, r });
            draw();
        } else if (currentClientCell) {
            setCurrentClientCell(null);
            draw();
        }
        if (!isDrawingLine || lineDrawMode.current !== "drag") return;
    }

    if (e.buttons !== 1 || tool === "pan" || tool === "line" || tool === "select" || tool === "replace" || tool === "fill") return; // Added 'replace' and 'fill'
    paintAtClient(e.clientX, e.clientY, tool === "draw" ? currentColorIndex : -1);
  };

  const onPointerDownCapture = (e) => {
    if (e.pointerType !== "pen" || tool === "line" || tool === "select" || tool === "replace" || tool === "fill") return; // Added 'replace' and 'fill'
    pushHistory();
    paintAtClient(e.clientX, e.clientY, currentColorIndex);
  };

  const onPointerMoveCapture = (e) => {
    if (e.pointerType !== "pen" || e.buttons !== 1 || tool === "line" || tool === "select" || tool === "replace" || tool === "fill") return; // Added 'replace' and 'fill'
    paintAtClient(e.clientX, e.clientY, currentColorIndex);
  };

  const handleTouchStart = (e) => {
    if (e.touches.length === 1) {
      const t = e.touches[0];
      const cell = clientToCell(t.clientX, t.clientY);
      if (cell == null) return;
      const c = cell % cols;
      const r = Math.floor(cell / cols);

      if (tool === "draw" || tool === "erase") {
        pushHistory();
        paintAtClient(t.clientX, t.clientY, tool === "draw" ? currentColorIndex : -1);
      } else if (tool === "line") {
        // Touch is always 'tap' mode for the line tool for simplicity
        lineDrawMode.current = 'tap';
        if (!isDrawingLine) {
            // First tap: Start the line
            setIsDrawingLine(true);
            setLineStart({ c, r });
            setCurrentClientCell({ c, r });
            draw(); // FIXED: Force redraw on line start in tap mode for immediate preview
        } else {
            // Second tap: Finalize the line
            finalizeLine(c, r);
        }
      } else if (tool === "replace") {
        if (!isPro) {
            showUpgradePrompt("Find and Replace Tool");
            setTool('draw');
            setLastPan({ x: t.clientX, y: t.clientY });
            return;
        }
        const colorIdx = layers[activeLayer].cells[getIndex(c, r)];
        if (colorIdx >= 0) {
            // Found a color, set state and open modal
            setFindColorIndex(colorIdx);
            setShowReplaceModal(true);
        } else {
            // If click on empty cell, just fall through to pan logic
            setLastPan({ x: t.clientX, y: t.clientY });
        }
      } else if (tool === "fill") {
        const targetColorIdx = layers[activeLayer].cells[getIndex(c, r)];
        
        if (targetColorIdx !== currentColorIndex) {
            floodFill(c, r, targetColorIdx, currentColorIndex);
        } else {
             // If click on same color, fall through to pan logic
             setLastPan({ x: t.clientX, y: t.clientY });
        }
      } else if (tool === "select") {
        // Touch selection starts a move action if copiedCells exist, otherwise it is ignored 
        // (Marquee select by touch is complex, we rely on the buttons for actions)
        if (copiedCells) {
             const { c: offsetC, r: offsetR } = selectionOffset;
             const { width, height } = copiedCells;
            
             // Check if click is inside the moving block
             if (c >= offsetC && c < offsetC + width && r >= offsetR && r < offsetR + height) {
                setLastPan({ x: t.clientX, y: t.clientY }); // Start dragging
                return;
             }
        }
        setLastPan({ x: t.clientX, y: t.clientY }); // Pan mode (single touch tap/drag)
      } else { // Pan mode
        setLastPan({ x: t.clientX, y: t.clientY });
      }
    } else if (e.touches.length === 2) {
      // Cancel drawing/line/move on pinch/zoom
      if (isDrawingLine) {
        setIsDrawingLine(false);
        setLineStart(null);
        setCurrentClientCell(null);
      }
      if (tool === 'select' && copiedCells) {
          // Do nothing, but prevent move. We only allow placing via button.
          setLastPan(null);
      }
      const [t1, t2] = e.touches;
      setLastTouchDistance(Math.hypot(t2.clientX - t1.clientX, t2.clientY - t1.clientY));
    }
  };

  const handleTouchMove = (e) => {
    if (e.touches.length === 1) {
      const t = e.touches[0];
      if (tool === "draw" || tool === "erase") {
        paintAtClient(t.clientX, t.clientY, tool === "draw" ? currentColorIndex : -1);
      } else if (tool === "line" && isDrawingLine && lineDrawMode.current === 'drag') { 
        // Only track cell for line if in drag mode (which touch doesn't usually use, but good to have)
        const cell = clientToCell(t.clientX, t.clientY);
        if (cell != null) {
          const c = cell % cols;
          const r = Math.floor(cell / cols);
          setCurrentClientCell({ c, r });
          draw();
        }
      } else if (lastPan && (tool === "pan" || tool === "replace" || tool === "fill" || (tool === "select" && copiedCells))) { // Added 'replace' and 'fill'
        // Pan or Move Selection
        const dx = t.clientX - lastPan.x;
        const dy = t.clientY - lastPan.y;
        
        if (tool === "select" && copiedCells) {
             const cellDx = Math.round(dx / (cellSize * zoom));
             const cellDy = Math.round(dy / (cellSize * zoom));
             
             if (cellDx !== 0 || cellDy !== 0) {
                 setSelectionOffset(o => ({ c: o.c + cellDx, r: o.r + cellDy }));
                 // Compensate for the discrete cell snapping in the lastPan tracking
                 setLastPan(p => ({ 
                     x: p.x + cellDx * cellSize * zoom, 
                     y: p.y + cellDy * cellSize * zoom 
                 }));
             }
             
        } else {
             setOffset(o => ({ x: o.x + dx, y: o.y + dy }));
             setLastPan({ x: t.clientX, y: t.clientY });
        }
      }
    } else if (e.touches.length === 2 && lastTouchDistance) {
      const [t1, t2] = e.touches;
      const newDist = Math.hypot(t2.clientX - t1.clientX, t2.clientY - t1.clientY);
      const scale = newDist / lastTouchDistance;
      applyZoomCentered(zoom * scale);
      setLastTouchDistance(newDist);
    }
  };

  const handleTouchEnd = () => {
    setLastPan(null);
    setLastTouchDistance(null);
    // Note: Line tool finalization is handled in touchStart for 'tap' mode, or pointerUp for 'drag' mode
  };

  // Palette management
  const addToPalette = (color) => {
    if (!customPalette.find((c) => c.code === color.code)) {
      const symbol = SYMBOL_LIST[customPalette.length % SYMBOL_LIST.length];
      setCustomPalette((p) => [...p, { ...color, symbol }]);
      setCurrentColorIndex(customPalette.length);
    }
  };
  
  // NEW: Move Palette Color Function
  const movePaletteColor = (i, dir) => {
    pushHistory();
    setCustomPalette((prev) => {
      const next = [...prev];
      const to = clamp(i + dir, 0, prev.length - 1);
      if (to === i) return prev;
      
      const [it] = next.splice(i, 1);
      next.splice(to, 0, it);
      
      // Also update the active color index if the moved color was selected
      if (i === currentColorIndex) {
        setCurrentColorIndex(to);
      } else if (i < currentColorIndex && to >= currentColorIndex) {
        setCurrentColorIndex(c => c - 1);
      } else if (i > currentColorIndex && to <= currentColorIndex) {
        setCurrentColorIndex(c => c + 1);
      }
      
      return next;
    });
  };
  
  // NEW: Sort Palette by Code
  const sortPaletteByCode = () => {
      pushHistory();
      
      // 1. Create a copy and sort it by code
      const sortedPalette = [...customPalette].sort((a, b) => {
          const codeA = a.code || "";
          const codeB = b.code || "";
          if (codeA < codeB) return -1;
          if (codeA > codeB) return 1;
          return 0;
      });
      
      // 2. Map old index to new index (Needed to update layer cell data)
      const oldIndexToNewIndexMap = new Map();
      customPalette.forEach((oldColor, oldIndex) => {
          const newIndex = sortedPalette.findIndex(newColor => newColor.code === oldColor.code);
          oldIndexToNewIndexMap.set(oldIndex, newIndex);
      });
      
      // 3. Update all layers' cells
      setLayers(prev => {
          const nextLayers = deepCopyLayers(prev);
          nextLayers.forEach(layer => {
              for (let i = 0; i < layer.cells.length; i++) {
                  const oldIndex = layer.cells[i];
                  if (oldIndex >= 0 && oldIndexToNewIndexMap.has(oldIndex)) {
                      layer.cells[i] = oldIndexToNewIndexMap.get(oldIndex);
                  }
              }
          });
          return nextLayers;
      });
      
      // 4. Update custom palette state and active color
      setCustomPalette(sortedPalette);
      setCurrentColorIndex(oldIndexToNewIndexMap.get(currentColorIndex) || 0); // Remap active color index
      alert("Palette sorted by code! All layers updated to reflect new color indices.");
  };

  // Layer management
  const addLayer = () => {
    pushHistory();
    setLayers((prev) => [...prev, createEmptyLayer(generateId(), `Layer ${prev.length + 1}`, cols, rows)]);
    setActiveLayer((i) => i + 1);
  };

  const deleteLayer = (i) => {
    if (layers.length === 1) return;
    pushHistory();
    setLayers((prev) => prev.filter((_, idx) => idx !== i));
    setActiveLayer((v) => Math.max(0, Math.min(v, layers.length - 2)));
  };

  const moveLayer = (i, dir) => {
    pushHistory();
    setLayers((prev) => {
      const next = [...prev];
      const to = clamp(i + dir, 0, prev.length - 1);
      if (to === i) return prev;
      const [it] = next.splice(i, 1);
      next.splice(to, 0, it);
      return next;
    });
    setActiveLayer((v) => clamp(v + dir, 0, layers.length - 1));
  };

  const renameLayer = (i, name) =>
    setLayers((prev) => {
      const n = [...prev];
      n[i] = { ...n[i], name };
      return n;
    });

  const toggleLayerVisible = (i) =>
    setLayers((prev) => {
      const n = [...prev];
      n[i] = { ...n[i], visible: !n[i].visible };
      return n;
    });

  const setLayerOpacity = (i, val) =>
    setLayers((prev) => {
      const n = [...prev];
      n[i] = { ...n[i], opacity: val };
      return n;
    });

  // Settings
  const applySettings = () => {
    pushHistory();
    const newCols = Math.max(1, parseInt(pendingCols) || 1);
    const newRows = Math.max(1, parseInt(pendingRows) || 1);
    const newAidaCount = Math.max(1, parseInt(pendingAida, 10) || 14);
    const newMargin = Math.max(0, parseFloat(pendingMargin) || 0);
    const newGridThinValue = Math.max(
      0.5,
      Math.min(3, parseFloat(pendingGridThin) || 1)
    );
    const requestedDesignMode = pendingDesignMode === "pixel" ? "pixel" : "aida";
    const newDesignMode = isPro ? requestedDesignMode : designMode;
    const requestedAllowFractional = !!pendingAllowFractionalStitches;
    const newAllowFractional = isPro ? requestedAllowFractional : false;

    const nextCell = suggestedCellSize(newAidaCount);

    const copyCols = Math.min(cols, newCols);
    const copyRows = Math.min(rows, newRows);

    setLayers((prev) =>
      prev.map((L) => {
        const next = new Int32Array(newCols * newRows).fill(-1);
        for (let r = 0; r < copyRows; r++) {
          const oldOff = r * cols;
          const newOff = r * newCols;
          for (let c = 0; c < copyCols; c++) {
            next[newOff + c] = L.cells[oldOff + c];
          }
        }
        return { ...L, cells: next };
      })
    );

    const resizedFractional = new Int32Array(
      newCols * newRows * FRACTIONAL_SLOTS
    ).fill(-1);
    if (allowFractionalStitches && newAllowFractional) {
      for (let r = 0; r < copyRows; r++) {
        for (let c = 0; c < copyCols; c++) {
          const oldIndex = r * cols + c;
          const newIndex = r * newCols + c;
          const oldBase = oldIndex * FRACTIONAL_SLOTS;
          const newBase = newIndex * FRACTIONAL_SLOTS;
          for (let q = 0; q < FRACTIONAL_SLOTS; q++) {
            resizedFractional[newBase + q] = fractionalCells[oldBase + q];
          }
        }
      }
    }
    setFractionalCells(resizedFractional);

    setCols(newCols);
    setRows(newRows);
    setDesignMode(newDesignMode);
    setPendingDesignMode(newDesignMode);
    setCellSize(nextCell);
    setAidaCount(newAidaCount);
    setGridThin(newGridThinValue);
    setPendingGridThin(newGridThinValue);
    setGridColor(pendingGridColor); 
    setMargin(newMargin);
    setPendingMargin(newMargin);
    setAllowFractionalStitches(newAllowFractional);
    setPendingAllowFractionalStitches(newAllowFractional);
    if (!newAllowFractional) {
      setFractionalMode(false);
    }
    
    // NEW: Apply fabric color
    setFabricColor(pendingFabricColor); 
    
    // Adjust overlay dimensions if the grid shrinks
    setImageOverlay(prev => ({
        ...prev,
        w: Math.min(prev.w, newCols),
        h: Math.min(prev.h, newRows),
    }));
    
    setOffset({ x: 20, y: 20 });
    setShowSettings(false);
  };

  // NEW: Branding Settings (using new requirePro)
  const applyBrandingSettings = requirePro("Branding", () => {
      pushHistory(); // Treat branding changes as historical
      setCompanyName(pendingCompanyName.trim());
      
      // NEW: Apply Position/Alignment/Size
      setLogoAlignment(pendingLogoAlignment);
      setTextAlignment(pendingTextAlignment);
      
      const newLogoMargin = Math.max(0, parseInt(pendingLogoMargin) || 0);
      setLogoMargin(newLogoMargin);
      setPendingLogoMargin(newLogoMargin); 

      const newTextMargin = Math.max(0, parseInt(pendingTextMargin) || 0);
      setTextMargin(newTextMargin);
      setPendingTextMargin(newTextMargin); 
      
      const newLogoScale = clamp(parseInt(pendingLogoScale) || DEFAULT_LOGO_SCALE, 10, 200);
      setLogoScale(newLogoScale);
      setPendingLogoScale(newLogoScale);

      const newTextScale = clamp(parseInt(pendingTextScale) || DEFAULT_TEXT_SCALE, 50, 200);
      setTextScale(newTextScale);
      setPendingTextScale(newTextScale);


      // NEW: Apply Symbol Color
      const finalSymbolColor = pendingSymbolColor === "auto" || pendingSymbolColor.match(/^#[0-9a-f]{6}$/i) 
          ? pendingSymbolColor 
          : "auto";
          
      setSymbolColor(finalSymbolColor);
      setPendingSymbolColor(finalSymbolColor); // Ensure pending reflects final value
      
      // Explicitly call draw() to update the canvas (only symbols are affected here)
      draw(); 
      
      setShowBrandingSettings(false);
      setShowBrandingPreview(false);
  });
  
  // Toolbar actions
  const applyZoomCentered = useCallback(
    (nextZoom) => {
      const clamped = clamp(nextZoom, MIN_ZOOM, MAX_ZOOM);
      const currentZoom = zoom;
      const centerX = offset.x + (cols * cellSize * currentZoom) / 2;
      const centerY = offset.y + (rows * cellSize * currentZoom) / 2;
      setOffset({
        x: centerX - (cols * cellSize * clamped) / 2,
        y: centerY - (rows * cellSize * clamped) / 2,
      });
      setZoom(clamped);
    },
    [cellSize, cols, offset.x, offset.y, rows, zoom]
  );
  const zoomIn = () => applyZoomCentered(zoom * ZOOM_FACTOR);
  const zoomOut = () => applyZoomCentered(zoom / ZOOM_FACTOR);
  const resetView = () => {
    setZoom(DEFAULT_ZOOM);
    setOffset(getCenteredOffset(DEFAULT_ZOOM, cols, rows));
  };
  
  const clearAll = () => {
    if (!window.confirm("Clear pattern (all layers AND backstitch lines)?")) return;
    pushHistory();
    setLayers((prev) => prev.map((L) => ({ ...L, cells: new Int32Array(cols * rows).fill(-1) })));
    setBackstitchLines([]);
    setFractionalCells(new Int32Array(cols * rows * FRACTIONAL_SLOTS).fill(-1));
    setFractionalMode(false);
    imageOverlayRef.current = null;
    setImageOverlay(prev => ({ ...prev, img: null, url: null, opacity: 0.5, x: 0, y: 0, w: cols, h: rows })); // Reset overlay on clear
  };

  // --- EXPORT FUNCTION (Multi-format) ---
  const buildExportCanvases = () => {
    // 1. Define the multiplier for high resolution (e.g., 4x the current size)
    const HD_MULTIPLIER = 4;
    const finalCellSize = cellSize * HD_MULTIPLIER;
    
    // 2. Define Key Dimensions and Header Size
    const KEY_HEIGHT_PER_ROW = 160;
    const KEY_INNER_PADDING = 60; // Padding between design grid and color key title
    const SWATCH_SIZE = 80;
    const COL_SPACING = 500;
    
    // NEW: Calculate padding in HD pixels
    const HD_H_PADDING = EXPORT_HORIZONTAL_PADDING * HD_MULTIPLIER;
    const HD_T_PADDING = EXPORT_TOP_PADDING * HD_MULTIPLIER;
    
    // Calculated Grid Dimensions
    const designWidthPx = cols * finalCellSize;
    const designHeightPx = rows * finalCellSize;
    
    // Final Canvas Dimensions
    const finalWidth = designWidthPx + 2 * HD_H_PADDING;
    const keyMaxW = finalWidth - 2 * HD_H_PADDING; // Key maximum width is now based on final canvas width
    
    const colorsPerRow = Math.floor(keyMaxW / COL_SPACING);
    const numRows = colorsPerRow > 0 ? Math.ceil(customPalette.length / colorsPerRow) : customPalette.length;
    
    // Calculate required vertical space for the key block
    const requiredKeyHeight = KEY_INNER_PADDING + (numRows * KEY_HEIGHT_PER_ROW) + 50; 
    
    // Branding Header Size (HEADER_HEIGHT is the height of the white space for branding)
    const HEADER_HEIGHT = (companyName.trim() || logoImage) ? BASE_HEADER_HEIGHT * HD_MULTIPLIER : 0; // 150*4px height when active
    
    // Base font size (50px) scaled by HD_MULTIPLIER and then scaled by textScale
    const BASE_FONT_SIZE_PX = 50; 
    const HEADER_TITLE_SIZE = (BASE_FONT_SIZE_PX * HD_MULTIPLIER / 4) * (textScale / 100);
    
    // 3. Create the main canvas for the design (This is now just a render target for the grid, no padding here)
    const designCanvas = document.createElement('canvas');
    const designCtx = designCanvas.getContext('2d');
    
    designCanvas.width = designWidthPx;
    designCanvas.height = designHeightPx;
    
    // --- DRAW DESIGN GRID (to temporary canvas) ---
    
    // Draw background (of the grid area)
    designCtx.fillStyle = fabricColor; // Use the new state for the background color
    designCtx.fillRect(0, 0, designWidthPx, designHeightPx);
    
    // Draw cells (Origin is 0,0 of this temporary canvas)
    for (const L of layers) {
      if (!L.visible) continue;
      designCtx.globalAlpha = L.opacity;
      for (let r = 0; r < rows; r++) {
        for (let c = 0; c < cols; c++) {
          const i = r * cols + c;
          const colorIdx = L.cells[i];
          if (colorIdx >= 0 && customPalette[colorIdx]) {
            // NEW: DMC Floss Simulation Placeholder
            // if (paletteBrand === 'DMC') { /* Draw subtle texture/gradient for sheen */ }
            designCtx.fillStyle = customPalette[colorIdx].hex;
            designCtx.fillRect(c * finalCellSize, r * finalCellSize, finalCellSize, finalCellSize);
          }
        }
      }
    }
    designCtx.globalAlpha = 1.0;

    renderFractionalStitches(
      designCtx,
      finalCellSize,
      0,
      0,
      0,
      cols - 1,
      0,
      rows - 1
    );

    // Draw backstitch lines
    designCtx.lineWidth = 2 * HD_MULTIPLIER; // Thicker lines for HD
    designCtx.setLineDash([]);

    for (const line of backstitchLines) {
        const { c: startC, r: startR } = line.start;
        const { c: endC, r: endR } = line.end;

        designCtx.strokeStyle = line.color;
        designCtx.beginPath();
        const x1 = startC * finalCellSize + finalCellSize / 2;
        const y1 = startR * finalCellSize + finalCellSize / 2;
        const x2 = endC * finalCellSize + finalCellSize / 2;
        const y2 = endR * finalCellSize + finalCellSize / 2;
        
        designCtx.moveTo(x1, y1);
        designCtx.lineTo(x2, y2);
        designCtx.stroke();
    }
    
    // Draw symbols
    if (showSymbols) {
      designCtx.font = `${Math.floor(finalCellSize * 0.6)}px monospace`;
      designCtx.textAlign = "center";
      designCtx.textBaseline = "middle";
      
      for (const L of layers) {
        if (!L.visible) continue;
        for (let r = 0; r < rows; r++) {
          for (let c = 0; c < cols; c++) {
            const i = r * cols + c;
            const colorIdx = L.cells[i];
            if (colorIdx >= 0 && customPalette[colorIdx]?.symbol) {
              
              // Determine symbol color: use fixed color if set, otherwise use auto-contrast
              const fillColor = symbolColor !== "auto" 
                ? symbolColor 
                : getSymbolColor(customPalette[colorIdx].hex);

              designCtx.fillStyle = fillColor;

              designCtx.fillText(
                customPalette[colorIdx].symbol,
                c * finalCellSize + finalCellSize / 2,
                r * finalCellSize + finalCellSize / 2
              );
            }
          }
        }
      }
    }
    
    // Draw Grid
    designCtx.lineWidth = gridThin * HD_MULTIPLIER * 0.5; // Scale grid thickness
    designCtx.strokeStyle = gridColor; // NEW: Use custom grid color
    designCtx.beginPath();
    
    // Vertical lines
    for (let c = 0; c <= cols; c++) {
      const x = c * finalCellSize + 0.5;
      designCtx.moveTo(x, 0);
      designCtx.lineTo(x, designHeightPx);
    }
    // Horizontal lines
    for (let r = 0; r <= rows; r++) {
      const y = r * finalCellSize + 0.5;
      designCtx.moveTo(0, y);
      designCtx.lineTo(designWidthPx, y);
    }
    designCtx.stroke();
    
    // Draw major grid lines
    designCtx.lineWidth = Math.max(gridThin * HD_MULTIPLIER, 1.5);
    designCtx.strokeStyle = gridColor; // NEW: Use custom grid color
    designCtx.beginPath();
    
    for (let c = 0; c <= cols; c += MAJOR_GRID_EVERY) {
      const x = c * finalCellSize + 0.5;
      designCtx.moveTo(x, 0);
      designCtx.lineTo(x, designHeightPx);
    }
    for (let r = 0; r <= rows; r += MAJOR_GRID_EVERY) {
      const y = r * finalCellSize + 0.5;
      designCtx.moveTo(0, y);
      designCtx.lineTo(designWidthPx, y);
    }
    designCtx.stroke();
    
    // --- END DRAW DESIGN GRID ---


    // 4. Create the final canvas including the enlarged key and header
    // Total Height = Header + Top Padding + Grid Height + Key Height + Bottom Padding (HD_H_PADDING)
    const finalHeight = HEADER_HEIGHT + HD_T_PADDING + designHeightPx + requiredKeyHeight + HD_H_PADDING;
    
    const finalCanvas = document.createElement('canvas');
    finalCanvas.width = finalWidth;
    finalCanvas.height = finalHeight;
    const finalCtx = finalCanvas.getContext('2d');
    
    // Fill background
    finalCtx.fillStyle = "white";
    finalCtx.fillRect(0, 0, finalCanvas.width, finalCanvas.height);
    
    // Draw the Design Grid onto the final canvas (Offset by padding)
    const designDrawY = HEADER_HEIGHT + HD_T_PADDING;
    finalCtx.drawImage(designCanvas, HD_H_PADDING, designDrawY);

    if (exportBorderEnabled) {
      const borderWidthPx = Math.max(1, exportBorderThickness) * HD_MULTIPLIER;
      finalCtx.strokeStyle = exportBorderColor;
      finalCtx.lineWidth = borderWidthPx;
      finalCtx.strokeRect(
        HD_H_PADDING - borderWidthPx / 2,
        designDrawY - borderWidthPx / 2,
        designWidthPx + borderWidthPx,
        designHeightPx + borderWidthPx
      );
    }
    
    
    // 5. Draw the Branding Header
    if (HEADER_HEIGHT > 0) {
        const headerTop = 0;
        const headerBottom = HEADER_HEIGHT;
        const headerCenterY = headerTop + (headerBottom - headerTop) / 2;
        
        // --- Logo Positioning ---
        if (logoImage) {
            // Logo scale is relative to the header height (BASE_HEADER_HEIGHT)
            const scaledLogoMaxHeight = BASE_HEADER_HEIGHT * HD_MULTIPLIER * (logoScale / 100);
            const logoMaxWidth = finalWidth - 2 * HD_H_PADDING - (companyName.trim() ? 100 * HD_MULTIPLIER : 0);
            
            const scale = Math.min(1, scaledLogoMaxHeight / logoImage.height, logoMaxWidth / logoImage.width);
            const drawH = logoImage.height * scale;
            const drawW = logoImage.width * scale;
            
            let logoX = 0;
            // X position (Based on Alignment and Margin)
            if (logoAlignment === 'left') {
                logoX = HD_H_PADDING + (logoMargin * HD_MULTIPLIER);
            } else if (logoAlignment === 'center') {
                logoX = finalWidth / 2 - drawW / 2;
            } else { // right
                logoX = finalWidth - HD_H_PADDING - (logoMargin * HD_MULTIPLIER) - drawW;
            }

            // Center logo vertically
            const logoY = headerCenterY - drawH / 2;
            
            finalCtx.drawImage(logoImage, logoX, logoY, drawW, drawH);
        }

        // --- Text Positioning ---
        const text = companyName.trim() || currentDesignName;
        let textX = 0;
        let textAnchor = 'left';
        
        finalCtx.fillStyle = '#0f172a';
        finalCtx.font = `bold ${HEADER_TITLE_SIZE}px system-ui`;
        finalCtx.textBaseline = 'middle';
        
        // X position (Based on Alignment and Margin)
        if (textAlignment === 'left') {
            textX = HD_H_PADDING + (textMargin * HD_MULTIPLIER);
            textAnchor = 'left';
        } else if (textAlignment === 'center') {
            textX = finalWidth / 2;
            textAnchor = 'center';
        } else { // right
            textX = finalWidth - HD_H_PADDING - (textMargin * HD_MULTIPLIER);
            textAnchor = 'right';
        }
        
        finalCtx.textAlign = textAnchor;
        // Place text at the center Y position
        finalCtx.fillText(text, textX, headerCenterY);
    }
    
    
    // 6. Draw the Enlarged Key (Palette)
    const keyStartX = HD_H_PADDING;
    let currentY = designDrawY + designHeightPx + KEY_INNER_PADDING;
    let currentX = keyStartX;
    
    const keyTitleSize = 40 * HD_MULTIPLIER / 4;
    const keyCodeSize = 32 * HD_MULTIPLIER / 4;
    const keySymbolSize = 40 * HD_MULTIPLIER / 4;
    const scaledSwatchSize = SWATCH_SIZE * HD_MULTIPLIER / 4;
    const scaledColSpacing = COL_SPACING * HD_MULTIPLIER / 4;
    const scaledKeyHeightPerRow = KEY_HEIGHT_PER_ROW * HD_MULTIPLIER / 4;
    const scaledMarginOffset = 35 * HD_MULTIPLIER / 4;
    
    // Draw Title
    finalCtx.fillStyle = '#0f172a';
    finalCtx.font = `bold ${keyTitleSize}px system-ui`;
    finalCtx.textAlign = 'left';
    finalCtx.fillText("Color Key (My Palette)", keyStartX, currentY - (10 * HD_MULTIPLIER / 4));
    currentY += (10 * HD_MULTIPLIER / 4); 

    // Draw Separator line
    finalCtx.strokeStyle = '#a7a7a7';
    finalCtx.lineWidth = 4;
    finalCtx.beginPath();
    finalCtx.moveTo(keyStartX, currentY);
    finalCtx.lineTo(finalWidth - keyStartX, currentY);
    finalCtx.stroke();
    currentY += (20 * HD_MULTIPLIER / 4); // Move down after separator

    if (colorsPerRow > 0) {
      customPalette.forEach((c, i) => {
        // Wrap to next row
        if (i > 0 && i % colorsPerRow === 0) {
          currentY += scaledKeyHeightPerRow;
          currentX = keyStartX;
        }

        // Draw Swatch
        finalCtx.fillStyle = c.hex;
        finalCtx.fillRect(currentX, currentY, scaledSwatchSize, scaledSwatchSize);
        finalCtx.strokeStyle = '#0f172a';
        finalCtx.lineWidth = 5;
        finalCtx.strokeRect(currentX, currentY, scaledSwatchSize, scaledSwatchSize);

        // Draw Symbol
        finalCtx.font = `bold ${keySymbolSize}px monospace`;
        finalCtx.textAlign = 'center';
        
        // Determine symbol color for the key: use fixed color if set, otherwise auto-contrast
        const fillColor = symbolColor !== "auto" 
            ? symbolColor 
            : getSymbolColor(c.hex);
            
        finalCtx.fillStyle = fillColor;
        finalCtx.textBaseline = 'middle';
        finalCtx.fillText(c.symbol || '?', currentX + scaledSwatchSize / 2, currentY + scaledSwatchSize / 2);
        
        // Draw Label (Code Only, Larger/Bolder)
        finalCtx.font = `bold ${keyCodeSize}px system-ui`; // Increase font size and bold it
        finalCtx.textAlign = 'left';
        finalCtx.fillStyle = '#0f172a';
        finalCtx.textBaseline = 'top';
        // Only draw the code, with increased offset
        finalCtx.fillText(`Code: ${c.code}`, currentX + scaledSwatchSize + scaledMarginOffset, currentY + (25 * HD_MULTIPLIER / 4)); 

        currentX += scaledColSpacing;
      });
    } else {
        finalCtx.font = '28px system-ui';
        finalCtx.fillStyle = '#0f172a';
        finalCtx.fillText("Design too narrow to display key effectively.", keyStartX, currentY + 30);
    }


    const sizeLabel = isPixelMode
      ? `${cols}x${rows}px`
      : `${cols}x${rows}_${aidaCount}ct`;
    const chartBaseName = `${currentDesignName.replace(/\s/g, '_')}_HD_Key_${sizeLabel}`;
    const artBaseName = `${currentDesignName.replace(/\s/g, '_')}_${sizeLabel}`;
    return { designCanvas, finalCanvas, chartBaseName, artBaseName };
  };

  const buildGalleryPayload = async () => {
    const { designCanvas } = buildExportCanvases();
    if (!designCanvas) return null;
    const maxSize = 320;
    const maxDimension = Math.max(designCanvas.width, designCanvas.height);
    const scale = maxDimension > 0 ? Math.min(1, maxSize / maxDimension) : 1;
    const previewCanvas = document.createElement("canvas");
    previewCanvas.width = Math.max(1, Math.round(designCanvas.width * scale));
    previewCanvas.height = Math.max(1, Math.round(designCanvas.height * scale));
    const previewCtx = previewCanvas.getContext("2d");
    if (!previewCtx) return null;
    previewCtx.imageSmoothingEnabled = false;
    previewCtx.drawImage(
      designCanvas,
      0,
      0,
      previewCanvas.width,
      previewCanvas.height
    );
    const previewUrl = previewCanvas.toDataURL("image/png");
    return {
      previewUrl,
      cols,
      rows,
      paletteCount: customPalette.length,
      stitchCount: totalStitches,
      isPixelMode,
      aidaCount,
    };
  };

  const downloadBlob = (blob, fileName) => {
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = fileName;
    a.click();
    URL.revokeObjectURL(url);
  };

  const downloadText = (text, fileName, mimeType = "text/plain") => {
    downloadBlob(new Blob([text], { type: mimeType }), fileName);
  };

  const downloadDataUrl = (dataUrl, fileName) => {
    const a = document.createElement("a");
    a.download = fileName;
    a.href = dataUrl;
    a.click();
  };

  const blobToBase64 = (blob) =>
    new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onloadend = () => {
        const result = reader.result;
        if (typeof result === "string") {
          resolve(result.split(",")[1] || "");
        } else {
          resolve("");
        }
      };
      reader.onerror = () => reject(reader.error || new Error("File read failed"));
      reader.readAsDataURL(blob);
    });

  const saveNativeFile = async ({ fileName, data, encoding, showAlert = true }) => {
    await ensureNativeFsPermission();

    const nativeRelativePath = `${NATIVE_SAVE_FOLDER}/${fileName}`;
    const targets = [
      { dir: Directory.Documents, label: "Documents" },
      { dir: Directory.ExternalStorage, label: "ExternalStorage" },
      { dir: Directory.Data, label: "App Data (private)" },
    ];

    let lastError = null;

    for (const target of targets) {
      try {
        await Filesystem.writeFile({
          path: nativeRelativePath,
          data,
          directory: target.dir,
          recursive: true,
          encoding,
        });
        if (showAlert) {
          alert(
            target.dir === Directory.Data
              ? "Export saved in app-private storage. Use Share/Copy from the app or Export on web to move it out."
              : `Export saved to ${target.label}/${NATIVE_SAVE_FOLDER}/${fileName}`
          );
        }
        return target;
      } catch (writeErr) {
        console.warn(`${SAVE_LOG} Export to ${target.label} failed:`, writeErr);
        lastError = writeErr;
      }
    }

    if (showAlert) {
      alert(lastError?.message || "Export failed on device storage. Please check storage access and try again.");
    }
    return null;
  };

  const createPdfFromJpeg = (jpegDataUrl, width, height) => {
    const base64 = jpegDataUrl.split(",")[1] || "";
    const binary = atob(base64);
    const imgBytes = new Uint8Array(binary.length);
    for (let i = 0; i < binary.length; i++) {
      imgBytes[i] = binary.charCodeAt(i);
    }

    const encoder = new TextEncoder();
    const parts = [];
    const offsets = [];
    let length = 0;

    const addBytes = (bytes) => {
      parts.push(bytes);
      length += bytes.length;
    };
    const addText = (text) => addBytes(encoder.encode(text));

    addText("%PDF-1.4\n");
    offsets[1] = length;
    addText("1 0 obj << /Type /Catalog /Pages 2 0 R >>\nendobj\n");
    offsets[2] = length;
    addText("2 0 obj << /Type /Pages /Kids [3 0 R] /Count 1 >>\nendobj\n");
    offsets[3] = length;
    addText(
      `3 0 obj << /Type /Page /Parent 2 0 R /Resources << /XObject <</Im0 4 0 R>> /ProcSet [/PDF /ImageC] >> /MediaBox [0 0 ${Math.round(
        width
      )} ${Math.round(height)}] /Contents 5 0 R >>\nendobj\n`
    );
    offsets[4] = length;
    addText(
      `4 0 obj << /Type /XObject /Subtype /Image /Width ${Math.round(
        width
      )} /Height ${Math.round(
        height
      )} /ColorSpace /DeviceRGB /BitsPerComponent 8 /Filter /DCTDecode /Length ${
        imgBytes.length
      } >>\nstream\n`
    );
    addBytes(imgBytes);
    addText("\nendstream\nendobj\n");
    const content = `q ${Math.round(width)} 0 0 ${Math.round(
      height
    )} 0 0 cm /Im0 Do Q`;
    offsets[5] = length;
    addText(
      `5 0 obj << /Length ${content.length} >>\nstream\n${content}\nendstream\nendobj\n`
    );

    const xrefOffset = length;
    addText("xref\n0 6\n0000000000 65535 f \n");
    for (let i = 1; i <= 5; i++) {
      addText(`${String(offsets[i]).padStart(10, "0")} 00000 n \n`);
    }
    addText(`trailer << /Size 6 /Root 1 0 R >>\nstartxref\n${xrefOffset}\n%%EOF`);

    return new Blob(parts, { type: "application/pdf" });
  };

  const createSvgFromPng = (canvas) => {
    const pngDataUrl = canvas.toDataURL("image/png");
    const width = canvas.width;
    const height = canvas.height;
    return `<?xml version="1.0" encoding="UTF-8"?>\n<svg xmlns="http://www.w3.org/2000/svg" width="${width}" height="${height}" viewBox="0 0 ${width} ${height}">\n  <image href="${pngDataUrl}" width="${width}" height="${height}" />\n</svg>`;
  };

  const exportCanvasImage = async ({ canvas, fileName, mimeType, quality }) => {
    const dataUrl = canvas.toDataURL(mimeType, quality);
    if (isNativeRuntime()) {
      const base64Data = dataUrl.split(",")[1] || "";
      await saveNativeFile({ fileName, data: base64Data });
      return;
    }
    downloadDataUrl(dataUrl, fileName);
  };

  const exportObjBundle = async ({ designCanvas, baseName }) => {
    const safeBase = baseName || "design";
    const textureFile = `${safeBase}.png`;
    const objFile = `${safeBase}.obj`;
    const mtlFile = `${safeBase}.mtl`;
    const materialName = "material0";
    const objWidth = cols;
    const objHeight = rows;

    const objText = [
      `mtllib ${mtlFile}`,
      `o ${safeBase}`,
      `v 0 0 0`,
      `v ${objWidth} 0 0`,
      `v ${objWidth} ${objHeight} 0`,
      `v 0 ${objHeight} 0`,
      "vt 0 1",
      "vt 1 1",
      "vt 1 0",
      "vt 0 0",
      `usemtl ${materialName}`,
      "f 1/1 2/2 3/3 4/4",
      "",
    ].join("\n");

    const mtlText = [
      `newmtl ${materialName}`,
      "Ka 1 1 1",
      "Kd 1 1 1",
      "Ks 0 0 0",
      `map_Kd ${textureFile}`,
      "",
    ].join("\n");

    const textureDataUrl = designCanvas.toDataURL("image/png");

    if (isNativeRuntime()) {
      const textureBase64 = textureDataUrl.split(",")[1] || "";
      const textureSaved = await saveNativeFile({
        fileName: textureFile,
        data: textureBase64,
        showAlert: false,
      });
      await saveNativeFile({
        fileName: objFile,
        data: objText,
        encoding: Encoding.UTF8,
        showAlert: false,
      });
      await saveNativeFile({
        fileName: mtlFile,
        data: mtlText,
        encoding: Encoding.UTF8,
        showAlert: false,
      });
      if (textureSaved) {
        alert(
          `OBJ bundle saved to ${textureSaved.label}/${NATIVE_SAVE_FOLDER}/${safeBase}.*`
        );
      }
      return;
    }

    downloadDataUrl(textureDataUrl, textureFile);
    downloadText(objText, objFile);
    downloadText(mtlText, mtlFile);
  };

  const exportDesign = async (format = exportFormat) => {
    if (isExporting) return;
    setIsExporting(true);
    try {
      const { designCanvas, finalCanvas, chartBaseName, artBaseName } =
        buildExportCanvases();
      const normalized = (format || "png").toLowerCase();

      if (normalized === "png") {
        await exportCanvasImage({
          canvas: finalCanvas,
          fileName: `${chartBaseName}.png`,
          mimeType: "image/png",
        });
      } else if (normalized === "jpg" || normalized === "jpeg") {
        await exportCanvasImage({
          canvas: finalCanvas,
          fileName: `${chartBaseName}.jpg`,
          mimeType: "image/jpeg",
          quality: 0.92,
        });
      } else if (normalized === "webp") {
        await exportCanvasImage({
          canvas: finalCanvas,
          fileName: `${chartBaseName}.webp`,
          mimeType: "image/webp",
          quality: 0.92,
        });
      } else if (normalized === "pdf") {
        const jpegDataUrl = finalCanvas.toDataURL("image/jpeg", 0.92);
        const pdfBlob = createPdfFromJpeg(
          jpegDataUrl,
          finalCanvas.width,
          finalCanvas.height
        );
        if (isNativeRuntime()) {
          const base64 = await blobToBase64(pdfBlob);
          await saveNativeFile({
            fileName: `${chartBaseName}.pdf`,
            data: base64,
          });
        } else {
          downloadBlob(pdfBlob, `${chartBaseName}.pdf`);
        }
      } else if (normalized === "svg") {
        const svgText = createSvgFromPng(finalCanvas);
        if (isNativeRuntime()) {
          await saveNativeFile({
            fileName: `${chartBaseName}.svg`,
            data: svgText,
            encoding: Encoding.UTF8,
          });
        } else {
          downloadText(svgText, `${chartBaseName}.svg`, "image/svg+xml");
        }
      } else if (normalized === "obj") {
        await exportObjBundle({
          designCanvas,
          baseName: artBaseName,
        });
      } else {
        alert("Unsupported export format.");
      }
    } catch (error) {
      console.error("Export failed:", error);
      alert(error?.message || "Export failed. Please try again.");
    } finally {
      setIsExporting(false);
      setShowExportModal(false);
    }
  };
// --- END EXPORT FUNCTION ---

  // Symbol editor
  const openSymbolEditorFor = (index) => {
    setSymbolEditIndex(index);
    setShowSymbolEditor(true);
  };

  const assignSymbolToIndex = (symbol) => {
    if (symbolEditIndex == null) return;
    setCustomPalette((prev) => {
      const next = [...prev];
      // Ensure the chosen symbol isn't already used
      const isSymbolUsed = next.some((c, i) => i !== symbolEditIndex && c.symbol === symbol);
      if (isSymbolUsed) {
          alert(`The symbol '${symbol}' is already in use by another color. Please choose a different symbol.`);
          return prev;
      }
      next[symbolEditIndex] = { ...next[symbolEditIndex], symbol };
      return next;
    });
    setShowSymbolEditor(false);
    setSymbolEditIndex(null);
  };

  // Fixed selectTool function
  const selectTool = (newTool) => {
    // If clicking the same tool that's already active
    if (tool === newTool) {
      // If it's the line tool and we're currently drawing a line, cancel it
      if (newTool === "line" && isDrawingLine) {
        setIsDrawingLine(false);
        setLineStart(null);
        setCurrentClientCell(null);
      }
      // If the tool is line, and we are not drawing, we stay in line tool but do nothing.
      if (newTool === "line") return;
      
      // If tool is select, clear selection unless we're in the middle of a move
      if (newTool === "select" && !copiedCells) {
          setSelectionStart(null);
          setSelectionEnd(null);
          setTool("draw"); // Double-click select switches back to draw
          return; 
      }
      // If tool is replace, switch back to draw
      if (newTool === "replace") {
          setFindColorIndex(null);
          setTool("draw");
          return;
      }
      // If tool is fill, switch back to draw
      if (newTool === "fill") {
          setTool("draw");
          return;
      }
      
      // For all other tools, clicking the active tool does nothing.
      return; 
    }
    
    // If switching to a different tool, cancel any active tool mode
    if (isDrawingLine) {
      setIsDrawingLine(false);
      setLineStart(null);
      setCurrentClientCell(null);
    }
    if (tool === 'select') {
        // Clear selection state if switching away from select tool
        setSelectionStart(null);
        setSelectionEnd(null);
        // Do NOT clear copiedCells/offset here, allow placing after switching tool
    }
    
    setTool(newTool);
  };
  
  // -----------------------------------------------------------

  const renderPrimaryActions = () => (
    <>
      <button className="btn" onClick={() => setShowDesignMenu((v) => !v)}>
        <EmojiIcon symbol={EMOJI.MENU} label="Menu" />
        Menu
        {hasUnsavedChanges && (
          <EmojiIcon
            symbol={EMOJI.SPARKLE}
            label="Unsaved changes"
            className="emoji-trailing"
          />
        )}
      </button>
      <button
        className="btn"
        onClick={saveDesign}
        disabled={!hasUnsavedChanges}
      >
        <EmojiIcon symbol={EMOJI.SAVE} label="Save" />
        Save
        {hasUnsavedChanges && (
          <EmojiIcon
            symbol={EMOJI.PENDING}
            label="Pending changes"
            className="emoji-trailing"
          />
        )}
      </button>
      <button className="btn icon-only" onClick={undo} aria-label="Undo" title="Undo">
        <EmojiIcon symbol={EMOJI.UNDO} label="Undo" />
      </button>
      <button className="btn icon-only" onClick={redo} aria-label="Redo" title="Redo">
        <EmojiIcon symbol={EMOJI.REDO} label="Redo" />
      </button>
      <button
        className={`btn icon-only ${tool === "draw" ? "btn-primary" : ""}`}
        onClick={() => selectTool("draw")}
        aria-label="Draw"
        title="Draw"
      >
        <EmojiIcon symbol={EMOJI.DRAW} label="Draw tool" />
      </button>
      <button
        className={`btn icon-only ${tool === "erase" ? "btn-primary" : ""}`}
        onClick={() => selectTool("erase")}
        aria-label="Erase"
        title="Erase"
      >
        <EmojiIcon symbol={EMOJI.ERASE} label="Erase tool" />
      </button>
      <button
        className={`btn icon-only ${tool === "fill" ? "btn-primary" : ""}`}
        onClick={() => selectTool("fill")}
        aria-label="Fill"
        title="Fill"
      >
        <EmojiIcon symbol={EMOJI.FILL} label="Fill tool" />
      </button>
      <button
        className={`btn icon-only ${tool === "select" ? "btn-primary" : ""}`}
        onClick={() => selectTool("select")}
        aria-label="Select"
        title="Select"
      >
        <EmojiIcon symbol={EMOJI.SELECT} label="Selection tool" />
      </button>
      <button
        className={`btn icon-only ${tool === "pan" ? "btn-primary" : ""}`}
        onClick={() => selectTool("pan")}
        aria-label="Pan tool"
        title="Pan tool"
      >
        <EmojiIcon symbol={EMOJI.PAN} label="Pan tool" />
      </button>
      <button className="btn icon-only" onClick={zoomIn} aria-label="Zoom In" title="Zoom In">
        <EmojiIcon symbol={EMOJI.ZOOM} label="Zoom in" />
      </button>
      <button className="btn icon-only" onClick={zoomOut} aria-label="Zoom Out" title="Zoom Out">
        <EmojiIcon symbol={EMOJI.ZOOM} label="Zoom out" />
      </button>
      <button
        className="btn icon-only"
        onClick={resetView}
        aria-label="Reset view"
        title="Reset view"
      >
        <EmojiIcon symbol="↺" label="Reset view" />
      </button>
      <button
        className="btn icon-only"
        onClick={toggleTheme}
        title={`Switch to ${theme === "light" ? "Dark" : "Light"} Mode`}
        aria-label={`Switch to ${theme === "light" ? "Dark" : "Light"} Mode`}
      >
        <EmojiIcon symbol={theme === "light" ? EMOJI.MOON : EMOJI.SUN} label="Theme toggle" />
      </button>
    </>
  );

  const renderSecondaryActions = () => (
    <>
      <button className="btn" onClick={saveAsDesign}>
        <EmojiIcon symbol={EMOJI.SAVE} label="Save As" />
        Save As
      </button>
      <button className="btn" onClick={clearAll}>
        Clear
      </button>
      <button className="btn" onClick={() => setShowSettings(true)}>
        <EmojiIcon symbol={EMOJI.SETTINGS} label="Settings" />
        Settings
      </button>
      <button
        className="btn"
        onClick={() => setShowBrandingSettings(true)}
        disabled={!isPro}
        title={!isPro ? "Pro feature: Sign up to enable branding." : "Customize Export Branding"}
      >
        <EmojiIcon symbol={EMOJI.BRANDING} label="Branding" />
        Branding
      </button>
      <button
        className="btn"
        onClick={() => setShowExportModal(true)}
        title="Export your chart in multiple formats"
        disabled={isExporting}
      >
        <EmojiIcon symbol={EMOJI.EXPORT} label="Export" />
        Export
      </button>
      <div
        className="export-border-controls"
        style={{
          display: "flex",
          alignItems: "center",
          gap: "6px",
          flexWrap: "wrap",
        }}
      >
        <label
          className="checkbox"
          style={{ display: "flex", alignItems: "center", gap: "4px" }}
        >
          <input
            type="checkbox"
            checked={exportBorderEnabled}
            onChange={(event) => setExportBorderEnabled(event.target.checked)}
          />
          Border
        </label>
        {exportBorderEnabled && (
          <>
            <label style={{ fontSize: "12px", color: "var(--ink-muted)" }}>
              Width
              <input
                type="number"
                min="1"
                max="40"
                value={exportBorderThickness}
                onChange={(event) =>
                  setExportBorderThickness(
                    Math.max(1, parseInt(event.target.value, 10) || 1)
                  )
                }
                className="input"
                style={{ width: "70px", marginLeft: "4px" }}
              />
            </label>
            <label style={{ fontSize: "12px", color: "var(--ink-muted)" }}>
              Color
              <input
                type="color"
                value={exportBorderColor}
                onChange={(event) => setExportBorderColor(event.target.value)}
                className="color-input-swatch"
                style={{ marginLeft: "4px" }}
              />
            </label>
          </>
        )}
      </div>
      <button
        className={`btn icon-only ${showEstimates ? "btn-primary" : ""}`}
        onClick={() => setShowEstimates((v) => !v)}
        title="Toggle resource estimate panel"
        aria-label="Toggle estimates panel"
      >
        <EmojiIcon symbol="📊" label="Estimate panel" />
      </button>
      <button
        className={`btn icon-only ${tool === "line" ? "btn-primary" : ""}`}
        onClick={() => selectTool("line")}
        aria-label="Line tool"
        title={isDrawingLine ? "Line tool (active)" : "Line tool"}
      >
        <EmojiIcon symbol={EMOJI.LINE} label="Line tool" />
      </button>
      {tool === "line" && (
        <div className="line-color-picker">
          <input
            type="color"
            value={lineColor}
            onChange={(e) => setLineColor(e.target.value)}
            title="Line Color"
            style={{
              height: "30px",
              width: "30px",
              margin: "0 5px",
              padding: 0,
              border: "none",
              cursor: "pointer",
            }}
          />
        </div>
      )}
      <button
        className={`btn icon-only ${fractionalMode ? "btn-primary" : ""}`}
        onClick={() => setFractionalMode((v) => !v)}
        disabled={!allowFractionalStitches}
        title={
          allowFractionalStitches
            ? "Toggle fractional stitch placement mode"
            : "Enable fractional stitches in Settings to use this mode."
        }
        aria-label="Fractional stitch mode"
      >
        ¼
      </button>
      <button
        className={`btn icon-only ${tool === "replace" ? "btn-primary" : ""}`}
        onClick={() => selectTool("replace")}
        disabled={!isPro}
        title={!isPro ? "Pro feature: Sign up to enable Find/Replace." : "Find and Replace a color on the grid"}
        aria-label="Find/Replace"
      >
        <EmojiIcon symbol={EMOJI.FIND} label="Find Replace" />
      </button>
      {tool === "select" && (
        <div className="select-actions" style={{ display: "flex", gap: "6px" }}>
          {selectionStart && !copiedCells && (
            <>
              <button
                className="btn"
                onClick={cutSelectedCells}
                title="Cut the selected area and enter Move mode."
              >
                <EmojiIcon symbol={EMOJI.SELECT} label="Cut selection" />
                Cut & Move
              </button>
              <button
                className="btn"
                onClick={copySelectedCells}
                title="Copy the selected area and enter Move mode (duplicate)."
              >
                <EmojiIcon symbol={EMOJI.DUPLICATE} label="Duplicate selection" />
                Duplicate
              </button>
            </>
          )}
          {copiedCells && (
            <>
              <button
                className="btn"
                onClick={flipSelectedCellsHorizontal}
                title="Flip the copied/moving block horizontally"
              >
                <EmojiIcon symbol={EMOJI.FLIP_H} label="Flip horizontal" />
                Flip H
              </button>
              <button
                className="btn"
                onClick={flipSelectedCellsVertical}
                title="Flip the copied/moving block vertically"
              >
                <EmojiIcon symbol={EMOJI.FLIP_V} label="Flip vertical" />
                Flip V
              </button>
              <button
                className="btn btn-good"
                onClick={pasteSelectedCells}
                title="Place the moved block onto the grid."
              >
                <EmojiIcon symbol={EMOJI.PLACE} label="Place selection" />
                Place
              </button>
            </>
          )}
          {(selectionStart || copiedCells) && (
            <button
              className="btn danger"
              onClick={cancelSelection}
              title="Clear selection or cancel move operation."
            >
              <EmojiIcon symbol={EMOJI.CLEAR} label="Clear selection" />
              Clear Select
            </button>
          )}
        </div>
      )}
      <button
        className={`btn icon-only ${showSymbols ? "btn-good" : ""}`}
        onClick={() => setShowSymbols((v) => !v)}
        aria-label={showSymbols ? "Hide symbols" : "Show symbols"}
        title={showSymbols ? "Hide symbols" : "Show symbols"}
      >
        <EmojiIcon symbol={showSymbols ? EMOJI.SYMBOLS_ON : EMOJI.SYMBOLS_OFF} label="Symbol toggle" />
      </button>
      <button
        className={`btn icon-only ${showCenterLines ? "btn-good" : ""}`}
        onClick={() => setShowCenterLines((v) => !v)}
        aria-label={showCenterLines ? "Hide center guides" : "Show center guides"}
        title={showCenterLines ? "Hide center guides" : "Show center guides"}
      >
        <EmojiIcon symbol={EMOJI.CENTER} label="Center guide" />
      </button>
      <button
        className={`btn icon-only ${showSidebar ? "btn-good" : ""}`}
        onClick={() => setShowSidebar((v) => !v)}
        title="Toggle Color Palette"
        aria-label={showSidebar ? "Hide color search" : "Show color search"}
      >
        <EmojiIcon symbol={EMOJI.PALETTE} label="Palette toggle" />
      </button>
      <button
        className={`btn icon-only ${showLayers ? "btn-good" : ""}`}
        onClick={() => setShowLayers((v) => !v)}
        title="Toggle Layers Panel"
        aria-label={showLayers ? "Hide layers" : "Show layers"}
      >
        <EmojiIcon symbol={EMOJI.LAYERS} label="Layers toggle" />
      </button>
      <button
        className={`btn icon-only ${showMyPalette ? "btn-good" : ""}`}
        onClick={() => setShowMyPalette((v) => !v)}
        title="Toggle My Palette"
        aria-label={showMyPalette ? "Hide my palette" : "Show my palette"}
      >
        <EmojiIcon symbol={EMOJI.TOOLBOX} label="My palette toggle" />
      </button>
      {!isPro && !hideUpgradeUi && (
        <button
          className="btn btn-good"
          onClick={() => setShowProModal(true)}
          style={{ background: 'linear-gradient(135deg, var(--good) 0%, #27ae60 100%)', color: 'white', fontWeight: 'bold' }}
        >
          <EmojiIcon symbol={EMOJI.ROCKET} label="Upgrade" />
          Upgrade to Pro
        </button>
      )}
      <button
        className={`btn ${showCommunityForum ? "btn-primary" : ""}`}
        onClick={() =>
          showCommunityForum ? closeCommunityForum() : openCommunityForum()
        }
        title="Toggle the community forum"
      >
        Community Forum
      </button>
      <button
        className={`btn ${showCommunityCenter ? "btn-primary" : ""}`}
        onClick={() => setShowCommunityCenter((v) => !v)}
        title="Toggle the community control center"
      >
        <EmojiIcon symbol={EMOJI.TOOLBOX} label="Control center" />
        {showCommunityCenter ? "Hide Control Center" : "Control Center"}
      </button>
      {user ? (
        <>
          <span style={{ fontSize: '12px', color: 'var(--ink-muted)' }}>
            Welcome, <strong>{user.displayName}</strong>
            {isPro ? (
              <span className="pro-badge" style={{ color: 'var(--good)', fontWeight: 'bold', marginLeft: '5px', marginRight: '5px' }}>PRO</span>
            ) : (
              !hideUpgradeUi && (
                <button
                  onClick={() => setShowProModal(true)}
                  style={{
                    background: 'none',
                    border: '1px solid var(--accent)',
                    color: 'var(--accent)',
                    borderRadius: '10px',
                    padding: '2px 8px',
                    marginLeft: '5px',
                    fontSize: '10px',
                    cursor: 'pointer',
                  }}
                >
                  UPGRADE
                </button>
              )
            )}
          </span>
          <button className="btn" onClick={handleLogout}>
            Sign Out
          </button>
        </>
      ) : (
        <div style={{ display: "flex", gap: "8px", flexWrap: "wrap" }}>
          <button className="btn btn-primary" onClick={handleGoogleRegister}>
            Register
          </button>
          <button className="btn" onClick={handleGoogleLogin}>
            Sign In
          </button>
        </div>
      )}
    </>
  );




  if (showCommunityForum) {
    return (
      <div className="app">
        <div className="main">
          <header className="header community-header">
            <div className="title">
              <span className="app-name">🧵 Cross-Stitch Designer</span>
              <span className="spec">Community Forum</span>
            </div>
            <div className="toolbar toolbar-inline">
              <button className="btn" onClick={() => closeCommunityForum()}>
                Back to Designer
              </button>
              {user ? (
                <button className="btn" onClick={handleLogout}>
                  Sign Out
                </button>
              ) : (
                <div style={{ display: "flex", gap: "8px", flexWrap: "wrap" }}>
                  <button className="btn btn-primary" onClick={handleGoogleRegister}>
                    Register
                  </button>
                  <button className="btn" onClick={handleGoogleLogin}>
                    Sign In
                  </button>
                </div>
              )}
            </div>
          </header>

          {requiresForumAuth && (
            <div className="auth-gate">
              <div className="auth-gate__panel">
                <h2>Register to continue</h2>
                <p>
                  Create a free account to unlock the community forum. New
                  registrations include a 1-day Pro trial.
                </p>
                <div className="auth-gate__actions">
                  <button className="btn btn-primary" onClick={handleGoogleRegister}>
                    Create account
                  </button>
                  <button className="btn" onClick={handleGoogleLogin}>
                    Sign in
                  </button>
                </div>
              </div>
            </div>
          )}

          {showThemeOnboarding && (
            <div
              style={{
                margin: "12px 0",
                padding: "14px",
                borderRadius: "10px",
                border: "1px solid var(--border, #ccc)",
                background: "var(--card, #f9fafb)",
                color: "var(--ink, #111)",
                display: "flex",
                flexWrap: "wrap",
                alignItems: "center",
                gap: "10px",
              }}
            >
              <div style={{ flex: "1 1 260px" }}>
                <div style={{ fontWeight: 700, marginBottom: "4px" }}>
                  Pick your look: Light or Dark
                </div>
                <div style={{ fontSize: "13px", color: "var(--ink-muted, #555)" }}>
                  You can change this anytime later in Settings.
                </div>
              </div>
              <div style={{ display: "flex", gap: "8px", flexWrap: "wrap" }}>
                <button className="btn" onClick={() => handleThemeChoice("light")}>
                  Use Light
                </button>
                <button className="btn" onClick={() => handleThemeChoice("dark")}>
                  Use Dark
                </button>
                <button className="btn" onClick={() => setShowThemeOnboarding(false)}>
                  Maybe later
                </button>
              </div>
            </div>
          )}

          <div className="community-forum-shell">
            <div className="community-forum-toolbar">
              <button
                className="btn"
                type="button"
                onClick={() => closeCommunityForum()}
              >
                Minimize Community Forum
              </button>
            </div>
            <div className="community-forum-panel">
              <CommunityForum
                user={user}
                communitySettings={communitySettings}
                currentDesignName={currentDesignName}
                buildGalleryPayload={buildGalleryPayload}
              />
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="app">
      <div className="main">
        <header className="header">
          <div className="title">
            <span className="app-name">🧵 Cross-Stitch Designer</span>
            <span className="spec">
              {currentDesignName} • {cols}×{rows}{" "}
              {isPixelMode ? "px grid" : `stitches on ${aidaCount}ct`}
              {!isPixelMode && (
                <>
                  &nbsp;• Design: {designWidthInches.toFixed(2)}″ ×{" "}
                  {designHeightInches.toFixed(2)}″ ({designWidthCM.toFixed(1)}×
                  {designHeightCM.toFixed(1)}cm)
                  &nbsp;• Fabric (margin {margin}" each side):{" "}
                  {totalFabricWidth.toFixed(2)}″ ×{" "}
                  {totalFabricHeight.toFixed(2)}″ (
                  {totalFabricWidthCM.toFixed(1)}×
                  {totalFabricHeightCM.toFixed(1)}cm)
                </>
              )}
            </span>
          </div>
          {!isCompactToolbar ? (
            <div className="toolbar toolbar-inline">
              {renderPrimaryActions()}
              {renderSecondaryActions()}
            </div>
          ) : (
            <div className="toolbar toolbar-compact">
              <div className="toolbar-primary">
                {renderPrimaryActions()}
                <button
                  className="btn more-tools-btn"
                  onClick={() => setShowToolsMenu((v) => !v)}
                  aria-expanded={showToolsMenu}
                  aria-label="Toggle more tools"
                >
                  <EmojiIcon symbol={EMOJI.MENU} label="More tools" />
                  More tools
                </button>
              </div>
              {showToolsMenu && (
                <div className="toolbar-drawer">
                  {renderSecondaryActions()}
                </div>
              )}
            </div>
          )}

        </header>

        {showThemeOnboarding && (
          <div
            style={{
              margin: "12px 0",
              padding: "14px",
              borderRadius: "10px",
              border: "1px solid var(--border, #ccc)",
              background: "var(--card, #f9fafb)",
              color: "var(--ink, #111)",
              display: "flex",
              flexWrap: "wrap",
              alignItems: "center",
              gap: "10px",
            }}
          >
            <div style={{ flex: "1 1 260px" }}>
              <div style={{ fontWeight: 700, marginBottom: "4px" }}>
                Pick your look: Light or Dark
              </div>
              <div style={{ fontSize: "13px", color: "var(--ink-muted, #555)" }}>
                You can change this anytime later in Settings.
              </div>
            </div>
            <div style={{ display: "flex", gap: "8px", flexWrap: "wrap" }}>
              <button className="btn" onClick={() => handleThemeChoice("light")}>
                Use Light
              </button>
              <button className="btn" onClick={() => handleThemeChoice("dark")}>
                Use Dark
              </button>
              <button className="btn" onClick={() => setShowThemeOnboarding(false)}>
                Maybe later
              </button>
            </div>
          </div>
        )}

        {showCommunityCenter && (
          <div className="community-center-shell">
            <div className="community-center-toolbar">
              <button
                className="btn"
                type="button"
                onClick={() => setShowCommunityCenter(false)}
              >
                Minimize Control Center
              </button>
            </div>
            <div className="community-center-panel">
              <CommunityControlCenter
                user={user}
                isPro={isPro}
                editionName={editionName}
                badges={flavorBadges}
                tagline={flavorTagline}
                description={flavorDescription}
                settings={communitySettings}
                onSettingChange={updateCommunitySetting}
                onSignIn={handleGoogleLogin}
                onRegister={handleGoogleRegister}
                onLogout={handleLogout}
                onRequestUpgrade={() => setShowProModal(true)}
                stats={communityStats}
                config={flavorUserConfig}
                hideUpgrade={hideUpgradeUi}
              />
            </div>
          </div>
        )}

        {showEstimates && (
          <section
            className="estimates-panel"
            style={{
              margin: "10px 0 20px",
              padding: "16px",
              border: "1px solid var(--border, #ccc)",
              borderRadius: "12px",
              background: "var(--card)",
              color: "var(--ink)",
            }}
          >
            <div
              className="estimates-settings"
              style={{ display: "flex", flexWrap: "wrap", gap: "12px" }}
            >
              <div style={{ minWidth: "140px" }}>
                <label style={{ fontSize: "12px", color: "var(--ink-muted)" }}>
                  Strands per stitch
                  <input
                    type="number"
                    min="1"
                    step="1"
                    value={estimateStrands}
                    onChange={(event) =>
                      setEstimateStrands(
                        Math.max(1, parseFloat(event.target.value) || 1)
                      )
                    }
                    className="input"
                    style={{ marginTop: "4px" }}
                  />
                </label>
                <div style={{ display: "flex", gap: "6px", marginTop: "4px" }}>
                  {[1, 2, 3].map((preset) => (
                    <button
                      key={preset}
                      type="button"
                      className={`mini-btn ${
                        estimateStrands === preset ? "btn-primary" : ""
                      }`}
                      onClick={() => setEstimateStrands(preset)}
                      style={{ minWidth: "40px" }}
                    >
                      {preset}
                    </button>
                  ))}
                </div>
              </div>
              <div style={{ minWidth: "160px" }}>
                <label style={{ fontSize: "12px", color: "var(--ink-muted)" }}>
                  Waste factor (tails/travel)
                  <input
                    type="number"
                    min="1"
                    step="0.1"
                    value={estimateWasteFactor}
                    onChange={(event) =>
                      setEstimateWasteFactor(
                        Math.max(1, parseFloat(event.target.value) || 1)
                      )
                    }
                    className="input"
                    style={{ marginTop: "4px" }}
                  />
                </label>
              </div>
              <div style={{ minWidth: "170px" }}>
                <label style={{ fontSize: "12px", color: "var(--ink-muted)" }}>
                  Skein length (inches)
                  <input
                    type="number"
                    min="60"
                    step="5"
                    value={estimateSkeinLength}
                    onChange={(event) =>
                      setEstimateSkeinLength(
                        Math.max(1, parseFloat(event.target.value) || 1)
                      )
                    }
                    className="input"
                    style={{ marginTop: "4px" }}
                  />
                </label>
                <small style={{ fontSize: "11px", color: "var(--ink-muted)" }}>
                  315″ ≈ standard 8m skein
                </small>
              </div>
              <div style={{ minWidth: "170px" }}>
                <label style={{ fontSize: "12px", color: "var(--ink-muted)" }}>
                  Stitches per hour
                  <input
                    type="number"
                    min="10"
                    step="10"
                    value={estimateStitchesPerHour}
                    onChange={(event) =>
                      setEstimateStitchesPerHour(
                        Math.max(1, parseFloat(event.target.value) || 1)
                      )
                    }
                    className="input"
                    style={{ marginTop: "4px" }}
                  />
                </label>
              </div>
              <div style={{ minWidth: "180px", fontSize: "12px" }}>
                Per-stitch usage:{" "}
                <strong>
                  {perStitchInches.toFixed(3)}″ ({perStitchCm.toFixed(2)}cm)
                </strong>
              </div>
            </div>

            <div style={{ marginTop: "10px", fontSize: "13px" }}>
              <strong>{formatStitchCount(totalStitches)}</strong> stitches •{" "}
              {totalInches.toFixed(1)}″ ({(totalInches / 36).toFixed(2)} yd |
              {totalMeters.toFixed(2)} m) of floss • approx{" "}
              <strong>
                {totalSkeinsRounded} skein{totalSkeinsRounded === 1 ? "" : "s"}
              </strong>{" "}
              total (exact {totalSkeinsExact.toFixed(2)}) • work time ≈{" "}
              <strong>{formatDuration(totalTimeHours)}</strong> at{" "}
              {safeStitchesPerHour} stitches/hr
            </div>

            <div
              className="table-wrapper"
              style={{
                marginTop: "12px",
                maxHeight: "320px",
                overflow: "auto",
                border: "1px solid var(--border, #ddd)",
                borderRadius: "8px",
              }}
            >
              <table
                className="data-table"
                style={{ width: "100%", borderCollapse: "collapse" }}
              >
                <thead style={{ background: "var(--card)" }}>
                  <tr>
                    <th style={{ textAlign: "left", padding: "6px" }}>
                      Color
                    </th>
                    <th style={{ textAlign: "right", padding: "6px" }}>
                      Stitches
                    </th>
                    <th style={{ textAlign: "right", padding: "6px" }}>
                      Length
                    </th>
                    <th style={{ textAlign: "right", padding: "6px" }}>
                      Skeins
                    </th>
                    <th style={{ textAlign: "right", padding: "6px" }}>
                      Time
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {sortedColorEstimates.length === 0 ? (
                    <tr>
                      <td
                        colSpan={5}
                        style={{ padding: "10px", textAlign: "center" }}
                      >
                        Add stitches to the grid to see per-color estimates.
                      </td>
                    </tr>
                  ) : (
                    sortedColorEstimates.map(
                      ({ color, index, stitches, inches, cm, skeinsRounded, skeinsExact, hours }) => (
                        <tr
                          key={`${color.code || color.name || index}-${index}`}
                          style={{
                            borderTop: "1px solid var(--border, #eee)",
                          }}
                        >
                          <td style={{ padding: "6px" }}>
                            <div
                              style={{
                                display: "flex",
                                alignItems: "center",
                                gap: "8px",
                              }}
                            >
                              <span
                                style={{
                                  width: "18px",
                                  height: "18px",
                                  borderRadius: "4px",
                                  border: "1px solid rgba(0,0,0,0.1)",
                                  background: color.hex,
                                  display: "inline-block",
                                }}
                              />
                              <div>
                                <div style={{ fontWeight: 600 }}>
                                  {color.code || `Color ${index + 1}`}
                                </div>
                                <div
                                  style={{
                                    fontSize: "11px",
                                    color: "var(--ink-muted)",
                                  }}
                                >
                                  {color.name || ""}
                                </div>
                              </div>
                            </div>
                          </td>
                          <td style={{ textAlign: "right", padding: "6px" }}>
                            {formatStitchCount(stitches)}
                          </td>
                          <td style={{ textAlign: "right", padding: "6px" }}>
                            {inches.toFixed(1)}″ / {cm.toFixed(1)}cm
                          </td>
                          <td style={{ textAlign: "right", padding: "6px" }}>
                            {skeinsRounded} ({skeinsExact.toFixed(2)})
                          </td>
                          <td style={{ textAlign: "right", padding: "6px" }}>
                            {formatDuration(hours)}
                          </td>
                        </tr>
                      )
                    )
                  )}
                </tbody>
              </table>
            </div>
            <p className="hint" style={{ marginTop: "8px" }}>
              Estimates include waste/tails factor and assume 2× diagonal
              stitches per cross. Adjust the inputs above to match your stitching
              habits or fabric setup.
            </p>
          </section>
        )}

        {/* LAYERS AREA: Conditionally rendered based on showLayers state */}
        {showLayers && (
          <div className="layers">
            <div className="layers-head">
              <div className="layers-title">🗂 Layers (Active: {layers[activeLayer]?.name || 'N/A'})</div>
              <button className="btn" onClick={addLayer}>＋ Add</button>
            </div>
            <div className="layer-list">
              {layers.map((L, i) => (
                <div key={L.id} className={`layer-item ${i === activeLayer ? "layer-active" : ""}`}>
                  <button className="icon-btn" onClick={() => toggleLayerVisible(i)} title={L.visible ? "👁" : "🚫"}>
                    {L.visible ? "👁" : "🚫"}
                  </button>
                  <input className="layer-name" value={L.name} onChange={(e) => renameLayer(i, e.target.value)} />
                  <input className="layer-opacity" type="range" min={0.2} max={1} step={0.05} value={L.opacity} 
                    onChange={(e) => setLayerOpacity(i, parseFloat(e.target.value))} title="Opacity" />
                  <button className="icon-btn" onClick={() => moveLayer(i, -1)}>▲</button>
                  <button className="icon-btn" onClick={() => moveLayer(i, 1)}>▼</button>
                  <button className="icon-btn" onClick={() => setActiveLayer(i)}>Set</button>
                  <button className="icon-btn danger" onClick={() => deleteLayer(i)}>🗑</button>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* MY PALETTE AREA: Conditionally rendered and horizontally scrollable */}
        {showMyPalette && (
          <div className="mypalette">
            <div className="mypalette-head">
              <div className="mypalette-title">
                🌈 My Palette ({customPalette.length} Colors)
              </div>
              <div>
                <button className="btn" onClick={sortPaletteByCode} title="Sort palette by color code (updates layer indices)">
                  🔠 Sort
                </button>
                {/* Optimize Button with Pro Check */}
                <button 
                    className="btn" 
                    onClick={() => setShowOptimizeModal(true)} 
                    title="Run optimization (conversion, reduction, etc.)"
                >
                  📈 Optimize
                </button>
                <button className="btn" onClick={() => setShowSymbolEditor(true)}>
                  🧩 Edit Symbols
                </button>
              </div>
            </div>
            
            {/* NEW: Scrollable container */}
            <div className="mypalette-row-scroll-container">
                <div className="mypalette-row">
                    {customPalette.map((c, i) => (
                      <div key={c.code || i} className="mypalette-item"> {/* Use code or index as key */}
                        <button className={`swatch ${currentColorIndex === i ? "swatch-active" : ""}`} style={{ background: c.hex }} 
                          onClick={() => setCurrentColorIndex(i)} title={`${c.code} ${c.name}`}>
                          <span 
                            className="swatch-symbol" 
                            style={{ 
                                fontSize: '25px', 
                                lineHeight: "1", 
                                transform: "translateY(-1px)", 
                                // NEW FIX: Apply symbolColor override here too
                                color: symbolColor !== "auto" ? symbolColor : getSymbolColor(c.hex) 
                            }}
                          >
                            {c.symbol || ""}
                          </span>
                        </button>
                        <div className="mypalette-actions">
                          {/* NEW: Move Buttons */}
                          <button className="mini-btn" onClick={() => movePaletteColor(i, -1)} disabled={i === 0} title="Move color left">←</button>
                          <button className="mini-btn" onClick={() => movePaletteColor(i, 1)} disabled={i === customPalette.length - 1} title="Move color right">→</button>
                          <button className="mini-btn" title="Change symbol" onClick={() => openSymbolEditorFor(i)}>✎</button>
                          <button className="mini-btn danger" onClick={() => {
                              if(window.confirm(`Are you sure you want to remove color ${c.code}? All stitches using this color will become empty on all layers.`)){
                                  // This is a disruptive action, so we use pushHistory *before* changing state
                                  pushHistory();
                                  setLayers(prev => {
                                      const nextLayers = deepCopyLayers(prev);
                                      nextLayers.forEach(layer => {
                                          for(let j = 0; j < layer.cells.length; j++){
                                              if(layer.cells[j] === i) layer.cells[j] = -1; // Remove all stitches of this color
                                              if(layer.cells[j] > i) layer.cells[j] -= 1; // Shift indices
                                          }
                                      });
                                      return nextLayers;
                                  });
                                  setCustomPalette((p) => p.filter((_, idx) => idx !== i));
                                  setCurrentColorIndex(c => (c === i ? 0 : (c > i ? c - 1 : c)));
                                  draw();
                              }
                          }}>✕</button>
                        </div>
                        <div className="mypalette-code" title={c.name}>{c.code}</div>
                      </div>
                    ))}
                </div>
            </div>
          </div>
        )}
        {/* End My Palette Area */}
        
        <div className="image-overlay-controls">
            <div className="layers-head">
                <div className="layers-title">🖼 Image Import / Overlay</div>
                <input type="file" accept="image/*" onChange={handleImageImport} style={{ display: 'none' }} id="image-import-file" />
                <button
                  className="btn"
                  onClick={openImageImportDialog}
                  disabled={!isPro}
                  title={!isPro ? "Pro feature: Sign up to import reference images." : "Import an image to convert or use as an overlay."}
                >
                    + Import Image
                </button>
                {!isPro && (
                  <p className="hint danger" style={{ marginTop: '5px' }}>
                    Pro Feature: Upgrade to import custom images for overlays or conversion.
                  </p>
                )}
                {imageOverlay.img && (
                     <button className="btn" onClick={handleOpenOverlaySettings}>
                        ⚙️ Adjust Overlay
                    </button>
                )}
                {imageOverlay.img && (
                     <button className="btn danger" onClick={() => {
                        imageOverlayRef.current = null;
                        setImageOverlay(prev => ({ ...prev, img: null, url: null }));
                        draw();
                    }}>
                        ✕ Remove
                    </button>
                )}
            </div>
            {imageOverlay.img && (
                <div style={{ display: 'flex', alignItems: 'center', gap: '10px', padding: '10px' }}>
                    <label>Transparency:</label>
                    <input type="range" min={0.05} max={1} step={0.05} value={imageOverlay.opacity} 
                      onChange={(e) => setImageOverlay(prev => ({ ...prev, opacity: parseFloat(e.target.value) }))} style={{ flexGrow: 1 }} />
                    <span>{Math.round(imageOverlay.opacity * 100)}%</span>
                </div>
            )}
        </div>

        <div className="canvas-wrap">
          <canvas ref={canvasRef} className="canvas" 
            onPointerDown={onPointerDown} 
            onPointerUp={onPointerUp} 
            onPointerMove={onPointerMove} 
            onPointerDownCapture={onPointerDownCapture} 
            onPointerMoveCapture={onPointerMoveCapture}
            onTouchStart={handleTouchStart} 
            onTouchMove={handleTouchMove} 
            onTouchEnd={handleTouchEnd} 
          />
        </div>
      </div>

      {showSidebar && (
        <aside className="sidebar">
           <div className="sidebar-head">
            <select className="select" value={paletteBrand} onChange={(e) => setPaletteBrand(e.target.value)}>
              <option value="DMC">DMC Threads</option>
              <option value="Anchor">Anchor Threads</option>
            </select>
            <input className="input" placeholder="Search color name or code..." value={search} onChange={(e) => setSearch(e.target.value)} />
          </div>
          <div className="sidebar-body">
            <div className="palette-grid">
              {filteredPalette.map((c, i) => (
                <div key={i} className="palette-item">
                  <button className="palette-tile" style={{ background: c.hex }} title={`${c.code} ${c.name}`} onClick={() => addToPalette(c)} />
                  <div className="palette-code">{c.code}</div>
                </div>
              ))}
            </div>
          </div>
        </aside>
      )}
      
      {/* Modals */}
      {showConvertOptions && (
        <ConvertOptionsModal
          paletteBrand={paletteBrand}
          customPalette={customPalette}
          pendingDominantColorCount={pendingDominantColorCount}
          onPendingDominantColorCountChange={setPendingDominantColorCount}
          onAnalyzeSelectedColors={handleAnalyzeSelectedColors}
          onConvertToStitches={applyImageAsStitches}
          onUseOverlay={applyImageAsOverlay}
          cartoonifyEnabled={cartoonifyConversion}
          onCartoonifyChange={setCartoonifyConversion}
          fractionalDetailEnabled={convertUseFractionalDetail}
          onFractionalDetailChange={setConvertUseFractionalDetail}
          fractionalIntensityValue={convertFractionalIntensity}
          fractionalIntensityOptions={FRACTIONAL_INTENSITY_LEVELS}
          onFractionalIntensityChange={handleFractionalIntensityChange}
          cols={cols}
          rows={rows}
          isPro={isPro}
          onClose={handleCloseConvertOptions}
        />
      )}
      {showOptimizeModal && (
        <PaletteOptimizationModal
          customPalette={customPalette}
          optimizationThreshold={optimizationThreshold}
          onOptimizationThresholdChange={setOptimizationThreshold}
          onOptimize={analyzeAndOptimizePalette}
          pendingConversionBrand={pendingConversionBrand}
          onPendingConversionBrandChange={setPendingConversionBrand}
          onConvertPaletteBrand={convertPaletteBrand}
          isPro={isPro}
          onClose={handleOptimizationClose}
        />
      )}

      {/* NEW: Find and Replace Modal */}
      {showReplaceModal && (
        <FindReplaceModal
          findColorIndex={findColorIndex}
          customPalette={customPalette}
          isPro={isPro}
          onClose={handleReplaceModalClose}
          onReplace={findAndReplace}
          getSymbolColor={getSymbolColor}
        />
      )}
      
      {/* NEW: Pro Upgrade Modal */}
      <ProUpgradeModal
        isOpen={showProModal}
        onClose={closeProModal}
        onUpgrade={handleUpgradeClick}
        featureName={pendingProFeature}
        isProcessing={isProcessingCheckout}
        ctaLabel={getUpgradeCtaLabel()}
      />

      {showExportModal && (
        <div className="modal-backdrop">
          <div className="modal">
            <div className="modal-header">
              <h2>Export Design</h2>
              <button className="icon-btn" onClick={() => setShowExportModal(false)}>
                ✕
              </button>
            </div>
            <div className="modal-body">
              <label>
                <span className="user-hub__label">Format</span>
                <select
                  className="select"
                  value={exportFormat}
                  onChange={(event) => setExportFormat(event.target.value)}
                >
                  <option value="png">PNG (.png)</option>
                  <option value="jpg">JPEG (.jpg)</option>
                  <option value="webp">WEBP (.webp)</option>
                  <option value="pdf">PDF (.pdf)</option>
                  <option value="svg">SVG (.svg)</option>
                  <option value="obj">OBJ + PNG (.obj/.mtl/.png)</option>
                </select>
              </label>
              <p className="hint">
                OBJ exports the design grid with a PNG texture for use in 3D
                tools. Other formats include the full chart with the color key.
              </p>
            </div>
            <div className="modal-actions">
              <button className="btn" onClick={() => setShowExportModal(false)}>
                Cancel
              </button>
              <button
                className="btn btn-primary"
                onClick={() => exportDesign()}
                disabled={isExporting}
              >
                {isExporting ? "Exporting..." : "Export"}
              </button>
            </div>
          </div>
        </div>
      )}

      {showDesignMenu && (
        <div className="modal-backdrop">
            <div className="modal">
                <div className="modal-header">
                    <h2>📂 Design Menu</h2>
                    <button className="icon-btn" onClick={() => setShowDesignMenu(false)}>✕</button>
                </div>
                <div className="modal-body">
                    <p>Current Design: <strong>{currentDesignName}</strong> {hasUnsavedChanges && "(Unsaved Changes!)"}</p>
                    <hr />
                    
                    <h3>Actions</h3>
                    <div className="design-menu-actions">
                        <button className="btn btn-primary" onClick={saveDesign} disabled={!hasUnsavedChanges} style={{ width: '100%', marginBottom: '10px' }}>
                            💾 Save Current Design
                        </button>
                        <button className="btn" onClick={saveAsDesign} style={{ width: '100%', marginBottom: '10px' }}>
                            💾 Save Design As...
                        </button>
                        <input type="file" accept=".json" onChange={(e) => {
                            const file = e.target.files[0];
                            if (!file) return;
                            promptToSave(() => loadDesign(file));
                            e.target.value = null;
                        }} style={{ display: 'none' }} id="load-design-file" />
                        <button className="btn" onClick={() => document.getElementById('load-design-file').click()} style={{ width: '100%', marginBottom: '10px' }}>
                            📂 Load Design File
                        </button>
                        <button className="btn" onClick={handleNewDesignClick} style={{ width: '100%' }}>
                            ➕ Start New Design
                        </button>
                        <button className="btn" onClick={() => { setShowDesignMenu(false); openTutorial(); }} style={{ width: '100%', marginTop: '10px' }}>
                            📘 Show Quick Tour
                        </button>
                    </div>
                    <hr />
                    <div className="setting-group">
                        <button
                          className="btn"
                          onClick={() => {
                            setShowDesignMenu(false);
                            exportDesign("pdf");
                          }}
                          style={{ width: '100%' }}
                        >
                            📄 Export PDF Chart
                        </button>
                    </div>
                </div>
            </div>
        </div>
      )}
      
      {/* New Project Name Prompt Modal */}
      {showNamePrompt && (
          <div className="modal-backdrop">
              <div className="modal">
                  <div className="modal-header">
                      <h2>📝 New Design Name</h2>
                      <button className="icon-btn" onClick={() => setShowNamePrompt(false)}>✕</button>
                  </div>
                  <form onSubmit={handleNamePromptSubmit} className="modal-body">
                      <label htmlFor="design-name-input">Enter a name for your new design:</label>
                      <input 
                          id="design-name-input"
                          type="text" 
                          value={pendingDesignName} 
                          onChange={(e) => setPendingDesignName(e.target.value)}
                          placeholder={DEFAULT_DESIGN_NAME}
                          autoFocus
                          className="input"
                      />
                      <div className="modal-actions">
                          <button type="button" className="btn" onClick={() => setShowNamePrompt(false)}>Cancel</button>
                          <button type="submit" className="btn btn-primary">Create Design</button>
                      </div>
                  </form>
              </div>
          </div>
      )}
      
      {showOverlaySettings && (
        <OverlaySettingsModal
          cols={cols}
          rows={rows}
          pendingOverlayX={pendingOverlayX}
          pendingOverlayY={pendingOverlayY}
          pendingOverlayW={pendingOverlayW}
          pendingOverlayH={pendingOverlayH}
          pendingOverlayOpacity={pendingOverlayOpacity}
          setPendingOverlayX={setPendingOverlayX}
          setPendingOverlayY={setPendingOverlayY}
          setPendingOverlayW={setPendingOverlayW}
          setPendingOverlayH={setPendingOverlayH}
          setPendingOverlayOpacity={setPendingOverlayOpacity}
          onApply={applyOverlaySettings}
          onCancel={handleOverlayCancel}
        />
      )}

      {showTutorial && <TutorialModal onClose={closeTutorial} />}


      {showBrandingSettings && (
        <BrandingSettingsModal
          pendingCompanyName={pendingCompanyName}
          setPendingCompanyName={setPendingCompanyName}
          logoUrl={logoUrl}
          onLogoImport={handleLogoImport}
          onRemoveLogo={() => {
            setLogoUrl(null);
            setLogoImage(null);
          }}
          pendingLogoAlignment={pendingLogoAlignment}
          setPendingLogoAlignment={setPendingLogoAlignment}
          pendingLogoMargin={pendingLogoMargin}
          setPendingLogoMargin={setPendingLogoMargin}
          pendingLogoScale={pendingLogoScale}
          setPendingLogoScale={setPendingLogoScale}
          pendingTextAlignment={pendingTextAlignment}
          setPendingTextAlignment={setPendingTextAlignment}
          pendingTextMargin={pendingTextMargin}
          setPendingTextMargin={setPendingTextMargin}
          pendingTextScale={pendingTextScale}
          setPendingTextScale={setPendingTextScale}
          pendingSymbolColor={pendingSymbolColor}
          setPendingSymbolColor={setPendingSymbolColor}
          showBrandingPreview={showBrandingPreview}
          onToggleBrandingPreview={() => setShowBrandingPreview((prev) => !prev)}
          logoImage={logoImage}
          currentDesignName={currentDesignName}
          onApply={applyBrandingSettings}
          onCancel={handleBrandingCancel}
          maxLogoWidth={MAX_LOGO_SIZE_W}
          maxLogoHeight={MAX_LOGO_SIZE_H}
          horizontalPadding={EXPORT_HORIZONTAL_PADDING}
          headerHeight={BASE_HEADER_HEIGHT}
        />
      )}


      {showSettings && (
        <CanvasSettingsModal
          pendingCols={pendingCols}
          pendingRows={pendingRows}
          pendingDesignMode={pendingDesignMode}
          pendingAida={pendingAida}
          pendingMargin={pendingMargin}
          pendingGridThin={pendingGridThin}
          pendingGridColor={pendingGridColor}
          pendingFabricColor={pendingFabricColor}
          pendingAllowFractionalStitches={pendingAllowFractionalStitches}
          fractionalDisabled={!isPro}
          setPendingCols={setPendingCols}
          setPendingRows={setPendingRows}
          setPendingDesignMode={setPendingDesignMode}
          setPendingAida={setPendingAida}
          setPendingMargin={setPendingMargin}
          setPendingGridThin={setPendingGridThin}
          setPendingGridColor={setPendingGridColor}
          setPendingFabricColor={setPendingFabricColor}
          onToggleFractionalStitches={handleFractionalSettingToggle}
          onApply={applySettings}
          onCancel={handleSettingsCancel}
          designModeDisabled={!isPro}
        />
      )}


      {showSymbolEditor && (
        <div className="modal-backdrop">
          <div className="modal symbol-modal">
            <div className="modal-header">
              <h2>🧩 Assign Symbols</h2>
              <button className="icon-btn" onClick={() => setShowSymbolEditor(false)}>✕</button>
            </div>
            <div className="modal-body">
              {symbolEditIndex !== null ? (
                <>
                  <p>Select a new symbol for color <strong>{customPalette[symbolEditIndex]?.code}</strong>:</p>
                  <div className="symbol-grid">
                    {SYMBOL_LIST.map((s) => (
                      <button key={s} className="symbol-btn" onClick={() => assignSymbolToIndex(s)}>{s}</button>
                    ))}
                  </div>
                </>
              ) : (
                <p>Click the ✎ icon next to a color swatch in 'My Palette' to edit its symbol.</p>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}



